/**
 * PIQ Command Center™ - Brain-Powered Intelligence Hub
 * =====================================================
 * 
 * CONSOLIDATED VIEW (2026-01-06)
 * ==============================
 * The Command Center now sources EXCLUSIVELY from the Brain pipeline:
 * - Brain Findings: /api/brain/findings/list (week-scoped, deduplicated)
 * - Brain Cases: /api/brain/cases (prioritized, with best plays)
 * - Brain Patterns: 13 statistical detectors (no LLM required)
 * 
 * This replaces the legacy multi-source architecture that caused:
 * - Duplicate recommendations from overlapping engines
 * - Stale insights accumulating across weeks
 * - Confusion from multiple tabs
 * 
 * Single Source of Truth: ONE Brain run → ONE set of findings → ONE view.
 * 
 * This is NOT a dashboard. This is where AI SHOWS ITS VALUE.
 * 
 * Structure:
 * Tab 1: Executive Brief
 *   - AI Executive Summary - What you need to know right now
 *   - Priority Actions - Do these TODAY (with $$ impact)
 *   - Early Warnings - Risks the AI detected before they hit
 *   - Pattern Discoveries - Insights humans would miss
 *   - Performance Snapshot - Quick numbers (secondary)
 * 
 * Tab 2: AI Insights
 *   - All AI insights with acknowledgment functionality
 *   - Priority and category filtering
 *   - Cross-retailer view
 */

import React, { useState, useEffect, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import GlobalHeader from '../components/GlobalHeader';
import { useTenant } from '../contexts/TenantContext';
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext';
import { Link, useLocation } from 'wouter';
import {
  Zap, TrendingUp, TrendingDown, AlertTriangle, Target,
  Clock, ChevronRight, Brain, Sparkles, BarChart3, Lightbulb,
  CheckCircle, Loader2, DollarSign, Rocket, ArrowRight,
  AlertCircle, Shield, Eye, Activity, Flame, ThumbsUp,
  Package, ShoppingCart, Store, Filter, RefreshCw, Check,
  ChevronDown, ChevronUp, ExternalLink, Info, Database, Calendar, Star,
  Pause, Play, Users, X, MoreVertical, User,
  Maximize2, Minimize2, Save, ListTodo, FileDown, Bell, Archive, Mail, MailOpen,
  Gauge, Signal, Timer, Settings, BookOpen
} from 'lucide-react';
import { EvidencePanel } from '../components/evidence/EvidencePanel';
import RunWorkflowModal from '../components/workflows/RunWorkflowModal';
import { buildOperatorWorkflowView, OperatorWorkflowView } from '../components/workflows/operatorView';
import { BrainRunDrawer } from '../components/brain/BrainRunDrawer';
// PIQ 8.0/8.2/8.5: Operator Console Components
import { 
  OperatorStatusBar as OperatorStatusBarComponent,
  OperatorConsole,
  NarrativeSpine,
  CoverageAuditPanel,
  DebugDataDump,
  buildOperatorView,
  extractAllChannelMetrics,
  type BrainContractSubset,
  type ExecBriefData,
} from '../components/commandCenter';

// ============================================================================
// TYPES - From our AI Engines
// ============================================================================

interface AIEvidence {
  dataSource: string;
  fiscalWeek?: string;
  queryType: string;
  verificationQuery: string;
  supportingMetrics: {
    label: string;
    value: string | number;
    trend?: 'up' | 'down' | 'neutral';
    trendPct?: number;
  }[];
  historicalContext?: string;
  confidenceScore: number;
  methodology: string;
}

interface DailyAction {
  id: string;
  contentHash: string;
  priority: number;
  cadence: 'daily' | 'weekly';
  action: string;
  rationale: string;
  expectedImpact: string;
  impactDollars: number;
  effort: 'low' | 'medium' | 'high';
  category: string;
  deadline?: string;
  retailerId?: string;
  retailerName?: string;
  aiEvidence?: AIEvidence;
  isAcknowledged?: boolean;
}

interface EarlyWarning {
  id?: string;
  contentHash: string;
  riskType: string;
  cadence: 'daily' | 'weekly';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  estimatedImpactDollars: number;
  confidence: number;
  recommendedAction: string;
  affectedSkus?: string[];
  retailerName?: string;
  aiEvidence?: AIEvidence;
  isAcknowledged?: boolean;
}

// Note: AIEvidence interface defined above (lines 39-53)

interface Insight {
  id: string;
  contentHash?: string;  // For acknowledgment tracking
  insightType: string;
  cadence: 'daily' | 'weekly';
  title: string;
  description: string;
  impactEstimate: number;
  noveltyScore: number;
  confidence: number;
  category: string;
  recommendation?: string;
  retailerName?: string;
  aiEvidence?: AIEvidence;
  isAcknowledged?: boolean;
}

// ============================================================================
// UNIFIED ACTION TYPE (Phase 1 - Command Center 2.0)
// ============================================================================

/**
 * CommandCenterAction - Unified action model
 * Normalizes Actions, Warnings, and Insights into one type
 */
// Phase 2 - Action status type
type ActionStatus = 'new' | 'acknowledged' | 'in_progress' | 'snoozed' | 'resolved' | 'rejected';

// Phase 2 - Valid assignee roles
const ASSIGNEE_ROLES = ['Founder', 'Retail', 'Marketing', 'Ops'] as const;

// GA-R3A: Seasonal expectation context
interface ExpectationContext {
  status: 'within_expected' | 'above_expected' | 'below_expected';
  expectedRange: [number, number];
  deviationPct: number;
  consensus: boolean;
  consensusType?: 'seasonal' | 'macro' | null;
  explanation?: string;
}

// GA-R3B: Forecast expectation context
interface ForecastExpectationContext {
  forecastValue: number;
  errorPct: number;
  errorBand: [number, number];  // [median, p75]
  status: 'within_expected_error_band' | 'outside_error_band' | 'no_baseline' | 'no_forecast';
  explanation?: string;
}

// Context event data from Brain findings
interface ContextEventMetadata {
  id: string;
  name: string;
  type: string;
  impact: 'up' | 'down' | 'mixed' | null;
  impactRange: [number, number] | null;
  confidence: number;
  daysActive?: number;
}

interface CommandCenterAction {
  id: string;
  contentHash: string;
  companyId: string;
  retailerId?: string | null;
  retailerName?: string;
  
  // Classification
  category: string;
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  timeHorizon: 'today' | 'this_week' | 'this_month';
  
  // Display
  title: string;
  description: string;
  recommendation?: string;
  
  // Impact
  impactDollars?: number | null;
  impactDataStatus: 'known' | 'unavailable' | 'estimated';
  confidence?: number | null;
  priorityScore: number;
  
  // Source
  sourceType: 'action' | 'warning' | 'insight';
  sourceAgentId?: string | null;
  aiEvidence?: AIEvidence;
  
  // Context Intelligence
  contextEvents?: ContextEventMetadata[];
  contextExplanation?: string;
  beliefException?: boolean;
  
  // Lifecycle
  status: ActionStatus;
  createdAt: string;
  updatedAt?: string;
  
  // Phase 2 - Workflow State
  assignedTo?: string | null;
  snoozeUntil?: string | null;
  notes?: string | null;
  stateId?: string | null;
  
  // Filtering
  tags?: string[];
  effort?: 'low' | 'medium' | 'high';
  cadence?: 'daily' | 'weekly';
  
  // UI state
  isAcknowledged?: boolean;
  
  // GA-R3A: Seasonal expectation context
  expectation?: ExpectationContext;
  // GA-R3B: Forecast expectation context
  forecastExpectation?: ForecastExpectationContext;
  suppressCase?: boolean;
  // GA-R4A: Business state context
  businessState?: {
    primaryState: string;
    stateLabel: string;
    urgency: 'low' | 'medium' | 'high';
    implication?: string;
  };
  
  // GA-R4B: State-aware action status
  stateStatus?: 'state_preferred' | 'state_neutral' | 'state_suppressed';
  stateRationale?: string;
  isActionable?: boolean;
}

interface AgentIntelligence {
  // NEW: Unified actions array (Phase 1 - Command Center 2.0)
  // Sorted by priority score, highest first
  unifiedActions?: CommandCenterAction[];
  
  // GA-R4A: Business State (MECE primary state per BrainRun)
  businessState?: {
    primaryState: string;
    stateLabel: string;
    confidence: number;
    supportingSignals: string[];
    explanation: string;
    urgency: 'low' | 'medium' | 'high';
    implications: string[];
  };

  // V3-10A: Dominant Belief summary
  beliefSummary?: {
    primary: string | null;
    confidence: number | null;
    contributors: any[];
    exceptions: any[];
    createdAt?: string | null;
  };
  
  // @deprecated - Use unifiedActions. Kept for backward compatibility.
  // From Daily Actions Engine
  actions: {
  dailyActions: DailyAction[];
  weeklyActions: DailyAction[];
  totalPotentialImpact: number;
    quickWinCount: number;
  } | null;
  
  // @deprecated - Use unifiedActions. Kept for backward compatibility.
  // From Early Warning Engine
  warnings: {
    dailyWarnings: EarlyWarning[];
    weeklyWarnings: EarlyWarning[];
    totalImpactDollars: number;
    criticalCount: number;
    highCount: number;
  } | null;
  
  // @deprecated - Use unifiedActions. Kept for backward compatibility.
  // From Insight Discovery Engine
  insights: {
    dailyInsights: Insight[];
    weeklyInsights: Insight[];
    topInsights: Insight[];
    highNoveltyCount: number;
    highImpactCount: number;
  } | null;
  
  // Summary
  executiveSummary?: string;
  generatedAt: string;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Compact format for badges/summaries: $15.5K, $1.2M
const formatCurrencyCompact = (value: number | string | null | undefined): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return '$0';
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${Math.round(num).toLocaleString()}`;
};

// Full format with commas, no decimals: $15,519
const formatCurrency = (value: number | string | null | undefined): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return '$0';
  return `$${Math.round(num).toLocaleString()}`;
};

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical': return 'bg-red-100 text-red-800 border-red-200';
    case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'medium': return 'bg-amber-100 text-amber-800 border-amber-200';
    default: return 'bg-blue-100 text-blue-800 border-blue-200';
  }
};

const getEffortBadge = (effort: string) => {
  switch (effort) {
    case 'low': return { text: 'Quick Win', color: 'bg-emerald-100 text-emerald-700' };
    case 'medium': return { text: 'Medium Effort', color: 'bg-amber-100 text-amber-700' };
    case 'high': return { text: 'Strategic', color: 'bg-purple-100 text-purple-700' };
    default: return { text: effort, color: 'bg-gray-100 text-gray-700' };
  }
};

/**
 * GA-R4B: Get badge for state-aware action status
 */
const getStateStatusBadge = (stateStatus: string | undefined) => {
  switch (stateStatus) {
    case 'state_preferred': 
      return { 
        text: '✓ Recommended Now', 
        color: 'bg-emerald-100 text-emerald-800 border-emerald-300',
        tooltip: 'This action aligns with the current business phase',
      };
    case 'state_suppressed': 
      return { 
        text: '⏸ Not Recommended', 
        color: 'bg-gray-200 text-gray-500 border-gray-300 opacity-70',
        tooltip: 'This action is suppressed in the current business phase',
      };
    default: 
      return null; // Don't show badge for neutral
  }
};

const getNoveltyLabel = (score: number) => {
  if (score >= 0.8) return { text: 'Highly Unusual', icon: Flame, color: 'text-red-500' };
  if (score >= 0.6) return { text: 'Notable', icon: Eye, color: 'text-orange-500' };
  if (score >= 0.4) return { text: 'Worth Noting', icon: Lightbulb, color: 'text-amber-500' };
  return { text: 'Expected', icon: CheckCircle, color: 'text-gray-400' };
};

// ============================================================================
// UNIFIED ACTIONS PARTITIONING (Phase 1 - Command Center 2.0)
// ============================================================================

/**
 * Partition unified actions into the three Command Center lanes:
 * 1. Today's Plan - High priority actions that need attention now
 * 2. Early Warnings - Risk-related items requiring monitoring
 * 3. Pattern Discoveries - Insights and opportunities
 * 4. Snoozed Items - Items deferred for later (Phase 2)
 */
function partitionUnifiedActions(
  unifiedActions: CommandCenterAction[],
  acknowledgedHashes: Set<string>,
  showAcknowledged: boolean
): {
  todaysPlan: CommandCenterAction[];
  earlyWarnings: CommandCenterAction[];
  patternDiscoveries: CommandCenterAction[];
  snoozedItems: CommandCenterAction[];
} {
  // Mark acknowledged status
  const actionsWithAck = unifiedActions.map(a => ({
    ...a,
    isAcknowledged: acknowledgedHashes.has(a.contentHash)
  }));

  // V3-10A: De-dupe by contentHash (keep first occurrence)
  const deduped = Array.from(
    actionsWithAck.reduce((map, action) => {
      if (!map.has(action.contentHash)) {
        map.set(action.contentHash, action);
      }
      return map;
    }, new Map<string, CommandCenterAction>())
  ).map(([_, action]) => action);

  // V3-10A: Suppress findings that support primary belief unless they are exceptions
  const beliefFiltered = deduped.filter(a => !a.suppressCase || a.beliefException);
  
  // Phase 2: Separate snoozed items first
  const { active, snoozed } = partitionActions(beliefFiltered);
  
  // Filter by acknowledged status if needed (only for active items)
  const filtered = showAcknowledged 
    ? active 
    : active.filter(a => !a.isAcknowledged);
  
  // Partition into 3 lanes based on sourceType and severity
  // NOTE: No limits - show ALL findings (can add pagination later)
  const todaysPlan = filtered.filter(a => 
    a.sourceType === 'action' &&
    (a.severity === 'critical' || a.severity === 'high' || a.priorityScore >= 50)
  );
  
  const earlyWarnings = filtered.filter(a =>
    a.sourceType === 'warning'
  );
  
  const patternDiscoveries = filtered.filter(a =>
    a.sourceType === 'insight'
  );
  
  // Phase 2: Snoozed items for collapsed section
  const snoozedItems = snoozed;
  
  return { todaysPlan, earlyWarnings, patternDiscoveries, snoozedItems };
}

/**
 * Convert a CommandCenterAction to DailyAction format for backward compatibility
 * with existing rendering components
 */
function unifiedToLegacyAction(action: CommandCenterAction): DailyAction {
  return {
    id: action.id,
    contentHash: action.contentHash,
    priority: action.priorityScore,
    cadence: action.cadence || 'daily',
    action: action.title,
    rationale: action.description,
    expectedImpact: action.recommendation || '',
    impactDollars: action.impactDollars || 0,
    effort: action.effort || 'medium',
    category: action.category,
    retailerId: action.retailerId || undefined,
    retailerName: action.retailerName,
    aiEvidence: action.aiEvidence,
    isAcknowledged: action.isAcknowledged
  };
}

/**
 * Convert a CommandCenterAction to EarlyWarning format for backward compatibility
 */
function unifiedToLegacyWarning(action: CommandCenterAction): EarlyWarning {
  return {
    id: action.id,
    contentHash: action.contentHash,
    riskType: action.type,
    cadence: action.cadence || 'daily',
    severity: action.severity,
    title: action.title,
    description: action.description,
    estimatedImpactDollars: action.impactDollars || 0,
    confidence: action.confidence || 0,
    recommendedAction: action.recommendation || '',
    retailerName: action.retailerName,
    aiEvidence: action.aiEvidence,
    isAcknowledged: action.isAcknowledged
  };
}

/**
 * Convert a CommandCenterAction to Insight format for backward compatibility
 */
function unifiedToLegacyInsight(action: CommandCenterAction): Insight {
  // Map severity to novelty score
  const noveltyMap: Record<string, number> = {
    critical: 0.9,
    high: 0.7,
    medium: 0.5,
    low: 0.3
  };
  
  return {
    id: action.id,
    contentHash: action.contentHash,
    insightType: action.type,
    cadence: action.cadence || 'daily',
    title: action.title,
    description: action.description,
    impactEstimate: action.impactDollars || 0,
    noveltyScore: noveltyMap[action.severity] || 0.5,
    confidence: action.confidence || 0,
    category: action.category,
    recommendation: action.recommendation,
    retailerName: action.retailerName,
    aiEvidence: action.aiEvidence,
    isAcknowledged: action.isAcknowledged
  };
}

// ============================================================================
// PHASE 2: ACTION WORKFLOW API FUNCTIONS
// ============================================================================

/**
 * Update action status via API
 * Phase 4B: Optionally pass action for outcome tracking when transitioning to in_progress
 */
async function updateActionStatus(
  actionId: string, 
  newStatus: ActionStatus, 
  action?: CommandCenterAction
): Promise<boolean> {
  try {
    const response = await fetch('/api/command-center/actions/updateStatus', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        actionId, 
        newStatus,
        // Pass action for outcome tracking when transitioning to in_progress
        action: newStatus === 'in_progress' ? action : undefined
      })
    });
    return response.ok;
  } catch (error) {
    console.error('[updateActionStatus] Error:', error);
    return false;
  }
}

/**
 * Assign action to user/role via API
 */
async function assignAction(actionId: string, assignedTo: string | null): Promise<boolean> {
  try {
    const response = await fetch('/api/command-center/actions/assign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actionId, assignedTo })
    });
    return response.ok;
  } catch (error) {
    console.error('[assignAction] Error:', error);
    return false;
  }
}

/**
 * Snooze action until date via API
 */
async function snoozeAction(actionId: string, snoozeUntil: string | null): Promise<boolean> {
  try {
    const response = await fetch('/api/command-center/actions/snooze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actionId, snoozeUntil })
    });
    return response.ok;
  } catch (error) {
    console.error('[snoozeAction] Error:', error);
    return false;
  }
}

// ============================================================================
// PHASE 2: ACTION WORKFLOW UI COMPONENTS
// ============================================================================

/**
 * Status badge with color coding
 */
function StatusBadge({ status }: { status: ActionStatus }) {
  const statusStyles: Record<ActionStatus, string> = {
    new: 'bg-blue-100 text-blue-700',
    acknowledged: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    snoozed: 'bg-purple-100 text-purple-700',
    resolved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  };
  
  const statusLabels: Record<ActionStatus, string> = {
    new: 'New',
    acknowledged: 'Acknowledged',
    in_progress: 'In Progress',
    snoozed: 'Snoozed',
    resolved: 'Resolved',
    rejected: 'Rejected'
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.new}`}>
      {statusLabels[status] || status}
    </span>
  );
}

/**
 * Phase 3: Confidence badge with color coding
 */
function ConfidenceBadge({ confidence }: { confidence?: number | null }) {
  if (confidence == null) return null;
  
  const label = confidence >= 0.8 ? 'High' : confidence >= 0.5 ? 'Medium' : 'Low';
  const color = confidence >= 0.8 
    ? 'bg-green-100 text-green-700' 
    : confidence >= 0.5 
      ? 'bg-yellow-100 text-yellow-700' 
      : 'bg-red-100 text-red-700';
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`} title={`${Math.round(confidence * 100)}% confidence`}>
      {label} Conf.
    </span>
  );
}

/**
 * V3-3B: Proven Play badge - shows when action pattern has proven track record
 */
function ProvenPlayBadge({ isProvenPlay }: { isProvenPlay?: boolean }) {
  if (!isProvenPlay) return null;
  
  return (
    <span 
      className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 flex items-center gap-1"
      title="This action pattern has a proven track record of improving outcomes"
    >
      ⭐ Proven Play
    </span>
  );
}

/**
 * V3-3B: Outcome Status badge - shows evaluation result for actions
 */
type OutcomeStatus = 'improved' | 'neutral' | 'worse' | 'pending';

function OutcomeStatusBadge({ outcome }: { outcome?: OutcomeStatus }) {
  if (!outcome || outcome === 'pending') return null;
  
  const config: Record<OutcomeStatus, { label: string; color: string; icon: string }> = {
    improved: { label: 'Improved', color: 'bg-green-100 text-green-700', icon: '📈' },
    neutral: { label: 'Neutral', color: 'bg-gray-100 text-gray-600', icon: '➡️' },
    worse: { label: 'Declined', color: 'bg-red-100 text-red-700', icon: '📉' },
    pending: { label: 'Pending', color: 'bg-blue-100 text-blue-600', icon: '⏳' },
  };
  
  const c = config[outcome];
  
  return (
    <span 
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.color} flex items-center gap-1`}
      title={`Outcome: ${c.label}`}
    >
      {c.icon} {c.label}
    </span>
  );
}

/**
 * Phase 3: AI inference type badge
 */
type AIInferenceType = 'ml_model' | 'llm_sql' | 'rule_based' | 'hybrid';

function InferenceTypeBadge({ type }: { type?: AIInferenceType }) {
  if (!type) return null;
  
  const labels: Record<AIInferenceType, string> = {
    ml_model: 'ML Model',
    llm_sql: 'LLM + SQL',
    rule_based: 'Rule-Based',
    hybrid: 'Hybrid'
  };
  
  const colors: Record<AIInferenceType, string> = {
    ml_model: 'bg-violet-100 text-violet-700',
    llm_sql: 'bg-blue-100 text-blue-700',
    rule_based: 'bg-gray-100 text-gray-700',
    hybrid: 'bg-indigo-100 text-indigo-700'
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[type] || colors.rule_based}`}>
      {labels[type] || type}
    </span>
  );
}

/**
 * Phase 3: Explain action via API
 */
async function explainAction(action: CommandCenterAction): Promise<{
  success: boolean;
  explanation?: {
    summary: string;
    priorityReason: string;
    priorityScore: number;
    metrics: { name: string; label: string; value: string | number; unit?: string; trend?: string }[];
    dataSources: { name: string; description?: string; timeWindow?: string }[];
    inferenceType: AIInferenceType;
    inferenceTypeLabel: string;
    confidenceLabel: string;
    confidenceScore: number | null;
    methodology: string | null;
    caveats: string[];
  };
}> {
  try {
    const response = await fetch('/api/command-center/actions/explain', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actionId: action.contentHash, action })
    });
    if (!response.ok) return { success: false };
    return response.json();
  } catch (error) {
    console.error('[explainAction] Error:', error);
    return { success: false };
  }
}

/**
 * Phase 3: Action Explain Drawer Component
 */
// Phase 6A: Outcome status type
interface OutcomeStatusResult {
  hasTracking: boolean;
  hasBaseline?: boolean;
  hasOutcome?: boolean;
  direction?: 'improved' | 'worsened' | 'no_change' | null;
  metricName?: string;
  baselineValue?: number;
  outcomeValue?: number | null;
  improvementPct?: number | null;
  daysRemaining?: number | null;
  statusLabel?: string;
}

// Phase 6A: Fetch outcome status for campaign actions
async function fetchOutcomeStatus(actionId: string): Promise<OutcomeStatusResult | null> {
  try {
    const res = await fetch(`/api/command-center/outcomes/status?actionId=${encodeURIComponent(actionId)}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.success ? data : null;
  } catch (error) {
    console.error('[fetchOutcomeStatus] Error:', error);
    return null;
  }
}

function ActionExplainDrawer({ 
  action, 
  isOpen, 
  onClose 
}: { 
  action: CommandCenterAction | null; 
  isOpen: boolean; 
  onClose: () => void;
}) {
  const [explanation, setExplanation] = useState<{
    summary: string;
    priorityReason: string;
    priorityScore: number;
    metrics: { name: string; label: string; value: string | number; unit?: string; trend?: string }[];
    dataSources: { name: string; description?: string; timeWindow?: string }[];
    inferenceType: AIInferenceType;
    inferenceTypeLabel: string;
    confidenceLabel: string;
    confidenceScore: number | null;
    methodology: string | null;
    caveats: string[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [outcomeStatus, setOutcomeStatus] = useState<OutcomeStatusResult | null>(null);
  const [googleAdvisory, setGoogleAdvisory] = useState<{
    hasAdvisory: boolean;
    alignment: string | null;
    alignmentReason: string | null;
    uiText: string | null;
    conflictDetails: { piqSays: string; googleSays: string } | null;
    recs: Array<{
      recType: string;
      recTypeLabel: string;
      summary: string;
      impactPct: number | null;
    }>;
  } | null>(null);

  useEffect(() => {
    if (isOpen && action) {
      setIsLoading(true);
      setOutcomeStatus(null);
      setGoogleAdvisory(null);
      
      // Fetch explanation
      explainAction(action).then(result => {
        if (result.success && result.explanation) {
          setExplanation(result.explanation);
        }
        setIsLoading(false);
      });
      
      // For campaign or google_ads actions, also fetch outcome status
      if (action.category === 'campaign' || action.category === 'google_ads') {
        fetchOutcomeStatus(action.contentHash).then(setOutcomeStatus);
        
        // GA-R1: Also fetch Google Advisory data
        fetch(`/api/dtc/google-ads/advisory?actionId=${encodeURIComponent(action.contentHash)}`)
          .then(res => res.json())
          .then(data => {
            if (data.ok) {
              setGoogleAdvisory(data);
            }
          })
          .catch(err => console.error('[GoogleAdvisory] Error fetching:', err));
      }
    } else {
      setExplanation(null);
      setOutcomeStatus(null);
      setGoogleAdvisory(null);
    }
  }, [isOpen, action?.contentHash]);

  if (!isOpen || !action) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-50" 
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Why This Action?</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
            </div>
          ) : explanation ? (
            <>
              {/* Action Title */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{action.title}</h3>
                <p className="text-sm text-gray-600">{explanation.summary}</p>
              </div>

              {/* AI Type & Confidence */}
              <div className="flex items-center gap-2 flex-wrap">
                <InferenceTypeBadge type={explanation.inferenceType} />
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  explanation.confidenceLabel === 'High' ? 'bg-green-100 text-green-700' :
                  explanation.confidenceLabel === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                  explanation.confidenceLabel === 'Low' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {explanation.confidenceLabel} Confidence
                  {explanation.confidenceScore != null && ` (${Math.round(explanation.confidenceScore * 100)}%)`}
                </span>
              </div>

              {/* Priority Breakdown */}
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg p-4 border border-violet-200">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-violet-600" />
                  <span className="text-sm font-semibold text-violet-800">Priority Score: {explanation.priorityScore}</span>
                </div>
                <p className="text-sm text-gray-700 font-mono">{explanation.priorityReason}</p>
              </div>

              {/* Key Metrics */}
              {explanation.metrics.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Metrics</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {explanation.metrics.map((metric, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-500">{metric.label}</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {metric.value}{metric.unit}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Data Sources */}
              {explanation.dataSources.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Data Sources</h4>
                  <ul className="space-y-2">
                    {explanation.dataSources.map((ds, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Database className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-gray-900">{ds.name}</span>
                          {ds.timeWindow && (
                            <span className="text-gray-500 ml-2">({ds.timeWindow})</span>
                          )}
                          {ds.description && (
                            <p className="text-gray-500 text-xs">{ds.description}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Methodology */}
              {explanation.methodology && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Methodology</h4>
                  <p className="text-sm text-gray-600">{explanation.methodology}</p>
                </div>
              )}

              {/* GA-R3A: Expectation Context */}
              {action.expectation && (
                <div className={`rounded-lg p-3 ${
                  action.expectation.status === 'within_expected' 
                    ? 'bg-sky-50 border border-sky-200' 
                    : action.expectation.status === 'above_expected'
                    ? 'bg-emerald-50 border border-emerald-200'
                    : 'bg-amber-50 border border-amber-200'
                }`}>
                  <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
                    action.expectation.status === 'within_expected' 
                      ? 'text-sky-800' 
                      : action.expectation.status === 'above_expected'
                      ? 'text-emerald-800'
                      : 'text-amber-800'
                  }`}>
                    <Calendar className="w-4 h-4" />
                    Expectation Context
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p className={
                      action.expectation.status === 'within_expected' 
                        ? 'text-sky-700' 
                        : action.expectation.status === 'above_expected'
                        ? 'text-emerald-700'
                        : 'text-amber-700'
                    }>
                      {action.expectation.explanation || (
                        action.expectation.status === 'within_expected' 
                          ? 'This change is within expected seasonal range.'
                          : action.expectation.status === 'above_expected'
                          ? 'This is above expected seasonal range — a positive deviation.'
                          : 'This is below expected seasonal range — requires attention.'
                      )}
                    </p>
                    <div className="flex items-center gap-4 text-xs">
                      <span>
                        Expected: ${action.expectation.expectedRange[0].toLocaleString()} - ${action.expectation.expectedRange[1].toLocaleString()}
                      </span>
                      <span>
                        Deviation: {action.expectation.deviationPct > 0 ? '+' : ''}{action.expectation.deviationPct.toFixed(1)}%
                      </span>
                      {action.expectation.consensus && (
                        <span className="px-2 py-0.5 bg-white rounded text-sky-600 font-medium">
                          {action.expectation.consensusType === 'macro' ? '🌐 Macro Consensus' : '📅 Seasonal Consensus'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* GA-R4A: Business State Context */}
              {action.businessState && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Business State Context
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-700">Primary State:</span>
                      <span className="px-2 py-0.5 bg-white border border-slate-200 rounded text-slate-800 text-xs font-medium">
                        {action.businessState.stateLabel}
                      </span>
                    </div>
                    <p className="text-slate-600">
                      <span className="font-medium">Implication:</span> {action.businessState.implication || 
                        (action.businessState.urgency === 'low' 
                          ? 'This action is informational, not urgent.' 
                          : action.businessState.urgency === 'high'
                          ? 'This action requires immediate attention.'
                          : 'This action has moderate priority.')
                      }
                    </p>
                  </div>
                </div>
              )}
              
              {/* GA-R4B: State-Aware Action Impact */}
              {action.stateStatus && action.stateStatus !== 'state_neutral' && (
                <div className={`rounded-lg p-3 ${
                  action.stateStatus === 'state_preferred' 
                    ? 'bg-emerald-50 border border-emerald-200' 
                    : 'bg-gray-100 border border-gray-300'
                }`}>
                  <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
                    action.stateStatus === 'state_preferred' ? 'text-emerald-800' : 'text-gray-700'
                  }`}>
                    {action.stateStatus === 'state_preferred' ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Business State Impact on Recommendation
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4" />
                        Action Not Recommended in Current State
                      </>
                    )}
                  </h4>
                  <p className={`text-sm ${
                    action.stateStatus === 'state_preferred' ? 'text-emerald-700' : 'text-gray-600'
                  }`}>
                    {action.stateRationale || (
                      action.stateStatus === 'state_preferred'
                        ? 'This action aligns well with the current business phase. Proceed with confidence.'
                        : 'This action is typically effective during different business phases. Consider waiting for conditions to change.'
                    )}
                  </p>
                  {action.stateStatus === 'state_suppressed' && action.isActionable === false && (
                    <div className="mt-2 text-xs text-gray-500 italic">
                      Note: This action is shown for context but is not recommended for execution in the current state.
                    </div>
                  )}
                </div>
              )}

              {/* Caveats */}
              {explanation.caveats.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-amber-800 mb-1">Caveats</h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    {explanation.caveats.map((caveat, idx) => (
                      <li key={idx}>• {caveat}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Phase 6A: Outcome Tracking Status - Real Data (including Google Ads) */}
              {(action.category === 'campaign' || action.category === 'google_ads') && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Outcome Tracking
                  </h4>
                  
                  {outcomeStatus?.hasTracking ? (
                    <div className="space-y-2">
                      {/* Outcome evaluated */}
                      {outcomeStatus.hasOutcome && outcomeStatus.direction ? (
                        <div className={`flex items-center gap-2 ${
                          outcomeStatus.direction === 'improved' ? 'text-green-600' :
                          outcomeStatus.direction === 'worsened' ? 'text-red-600' :
                          'text-gray-600'
                        }`}>
                          {outcomeStatus.direction === 'improved' ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : outcomeStatus.direction === 'worsened' ? (
                            <TrendingDown className="w-4 h-4" />
                          ) : (
                            <Activity className="w-4 h-4" />
                          )}
                          <span className="font-medium">
                            {outcomeStatus.direction === 'improved' ? 'Improved' :
                             outcomeStatus.direction === 'worsened' ? 'Declined' : 'No Material Change'}
                            {outcomeStatus.improvementPct != null && (
                              <span className="ml-1">
                                ({outcomeStatus.improvementPct > 0 ? '+' : ''}{outcomeStatus.improvementPct.toFixed(1)}%)
                              </span>
                            )}
                          </span>
                        </div>
                      ) : outcomeStatus.hasBaseline ? (
                        /* Baseline recorded, waiting for evaluation */
                        <div className="flex items-center gap-2 text-blue-600">
                          <Clock className="w-4 h-4" />
                          <span>
                            Evaluating {outcomeStatus.metricName?.toUpperCase() || 'metric'}
                            {outcomeStatus.daysRemaining != null && outcomeStatus.daysRemaining > 0 && (
                              <span className="ml-1">({outcomeStatus.daysRemaining} days remaining)</span>
                            )}
                          </span>
                        </div>
                      ) : null}
                      
                      {/* Baseline/Outcome values */}
                      {outcomeStatus.baselineValue != null && (
                        <div className="text-xs text-gray-500 space-y-1">
                          <div className="flex justify-between">
                            <span>Baseline ({outcomeStatus.metricName?.toUpperCase()}):</span>
                            <span className="font-mono">{outcomeStatus.baselineValue.toFixed(2)}</span>
                          </div>
                          {outcomeStatus.outcomeValue != null && (
                            <div className="flex justify-between">
                              <span>Outcome:</span>
                              <span className="font-mono">{outcomeStatus.outcomeValue.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : action.status === 'in_progress' || action.status === 'resolved' ? (
                    /* Status changed but no baseline recorded yet */
                    <div className="text-sm text-gray-600">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        Baseline recorded for ROAS/CPA tracking
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Outcomes will be evaluated after 14 days to measure improvement.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Change status to "In Progress" to start tracking this recommendation's impact.
                    </p>
                  )}
                </div>
              )}

              {/* GA-R1: Google Advisory Section */}
              {googleAdvisory?.hasAdvisory && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Google Ads Advisory
                    {/* Alignment badge */}
                    {googleAdvisory.alignment && (
                      <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-medium ${
                        googleAdvisory.alignment === 'align' ? 'bg-green-100 text-green-700' :
                        googleAdvisory.alignment === 'conflict' ? 'bg-red-100 text-red-700' :
                        googleAdvisory.alignment === 'complementary' ? 'bg-purple-100 text-purple-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {googleAdvisory.alignment === 'align' ? '✓ Aligned' :
                         googleAdvisory.alignment === 'conflict' ? '⚠ Conflict' :
                         googleAdvisory.alignment === 'complementary' ? '+ Complementary' :
                         'Unknown'}
                      </span>
                    )}
                  </h4>
                  
                  {/* Conflict Details */}
                  {googleAdvisory.alignment === 'conflict' && googleAdvisory.conflictDetails && (
                    <div className="bg-red-50 border border-red-200 rounded p-2 mb-2 text-xs">
                      <div className="flex items-start gap-2 mb-1">
                        <span className="font-semibold text-red-800">PIQ says:</span>
                        <span className="text-red-700">{googleAdvisory.conflictDetails.piqSays}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-red-800">Google says:</span>
                        <span className="text-red-700">{googleAdvisory.conflictDetails.googleSays}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* UI Text */}
                  {googleAdvisory.uiText && (
                    <p className="text-sm text-blue-700 mb-2">{googleAdvisory.uiText}</p>
                  )}
                  
                  {/* Recommendations list */}
                  {googleAdvisory.recs.length > 0 && (
                    <div className="space-y-2">
                      {googleAdvisory.recs.slice(0, 3).map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs bg-white/60 rounded p-2">
                          <span className="px-1.5 py-0.5 bg-blue-200 text-blue-800 rounded font-medium">
                            {rec.recTypeLabel}
                          </span>
                          <span className="text-gray-700 flex-1">{rec.summary}</span>
                          {rec.impactPct != null && (
                            <span className="text-green-600 font-medium whitespace-nowrap">
                              +{rec.impactPct}%
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>Unable to load explanation</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Assignee pill display
 */
function AssigneePill({ assignedTo }: { assignedTo: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
      <User className="w-3 h-3" />
      {assignedTo}
    </span>
  );
}

/**
 * Map action category to recommended workflow
 */
function getWorkflowForCategory(category: string): { id: string; label: string } | null {
  switch (category) {
    case 'campaign':
      return { id: 'marketing_mix_strategy', label: 'Campaign Deep Dive' };
    case 'inventory':
      return { id: 'supply_ops_flow', label: 'Inventory Review' };
    case 'retail':
      return { id: 'weekly_strategy_briefing', label: 'Retail Review' };
    case 'Growth Opportunity':
    case 'Ad Optimization':
      return { id: 'marketing_mix_strategy', label: 'Marketing Analysis' };
    default:
      return null;
  }
}

/**
 * Action workflow controls dropdown
 */
function ActionWorkflowControls({
  action,
  onStatusChange,
  onAssign,
  onSnooze
}: {
  action: CommandCenterAction;
  onStatusChange: (actionId: string, status: ActionStatus) => Promise<void>;
  onAssign: (actionId: string, assignee: string | null) => Promise<void>;
  onSnooze: (actionId: string, until: string | null) => Promise<void>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRunningWorkflow, setIsRunningWorkflow] = useState(false);
  const [workflowResult, setWorkflowResult] = useState<WorkflowRunResult | null>(null);
  const [showWorkflowDrawer, setShowWorkflowDrawer] = useState(false);
  
  // Get recommended workflow for this action's category
  const recommendedWorkflow = getWorkflowForCategory(action.category);
  
  const handleRunWorkflow = async (workflowId: string) => {
    setIsRunningWorkflow(true);
    try {
      const result = await runWorkflow(workflowId, { actionId: action.contentHash });
      if (result) {
        setWorkflowResult(result);
        setShowWorkflowDrawer(true);
      }
    } finally {
      setIsRunningWorkflow(false);
      setIsOpen(false);
    }
  };
  
  const handleStatusChange = async (status: ActionStatus) => {
    setIsLoading(true);
    try {
      await onStatusChange(action.contentHash, status);
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };
  
  const handleAssign = async (assignee: string | null) => {
    setIsLoading(true);
    try {
      await onAssign(action.contentHash, assignee);
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };
  
  const handleSnooze = async (days: number) => {
    const until = new Date();
    until.setDate(until.getDate() + days);
    setIsLoading(true);
    try {
      await onSnooze(action.contentHash, until.toISOString());
    } finally {
      setIsLoading(false);
      setShowSnoozeModal(false);
      setIsOpen(false);
    }
  };
  
  const handleUnsnooze = async () => {
    setIsLoading(true);
    try {
      await onSnooze(action.contentHash, null);
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };
  
  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : (
          <MoreVertical className="w-4 h-4 text-gray-400" />
        )}
      </button>
      
      {isOpen && (
        <div className="absolute right-0 top-8 z-50 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
          {/* Contextual Workflow Launch - Phase 5 */}
          {recommendedWorkflow && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleRunWorkflow(recommendedWorkflow.id); }}
                disabled={isRunningWorkflow}
                className="w-full px-3 py-2 text-left text-sm bg-violet-50 hover:bg-violet-100 flex items-center gap-2 text-violet-700 font-medium"
              >
                {isRunningWorkflow ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4" />
                )}
                Run {recommendedWorkflow.label}
              </button>
              <div className="border-t border-gray-100 my-1" />
            </>
          )}
          
          {/* Status Section */}
          <div className="px-3 py-1 text-xs text-gray-500 font-medium">Status</div>
          {(['new', 'in_progress', 'resolved', 'rejected'] as ActionStatus[]).map(status => (
            <button
              key={status}
              onClick={(e) => { e.stopPropagation(); handleStatusChange(status); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                action.status === status ? 'bg-gray-50 font-medium' : ''
              }`}
            >
              <StatusBadge status={status} />
            </button>
          ))}
          
          <div className="border-t border-gray-100 my-1" />
          
          {/* Snooze Section */}
          <div className="px-3 py-1 text-xs text-gray-500 font-medium">Snooze</div>
          {action.status === 'snoozed' ? (
            <button
              onClick={(e) => { e.stopPropagation(); handleUnsnooze(); }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <Play className="w-4 h-4 text-green-500" />
              Unsnooze
            </button>
          ) : (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleSnooze(1); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <Pause className="w-4 h-4 text-purple-500" />
                1 Day
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleSnooze(3); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <Pause className="w-4 h-4 text-purple-500" />
                3 Days
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleSnooze(7); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <Pause className="w-4 h-4 text-purple-500" />
                7 Days
              </button>
            </>
          )}
          
          <div className="border-t border-gray-100 my-1" />
          
          {/* Assign Section */}
          <div className="px-3 py-1 text-xs text-gray-500 font-medium">Assign To</div>
          {ASSIGNEE_ROLES.map(role => (
            <button
              key={role}
              onClick={(e) => { e.stopPropagation(); handleAssign(role); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                action.assignedTo === role ? 'bg-indigo-50 text-indigo-700' : ''
              }`}
            >
              <Users className="w-4 h-4 text-indigo-500" />
              {role}
            </button>
          ))}
          {action.assignedTo && (
            <button
              onClick={(e) => { e.stopPropagation(); handleAssign(null); }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
            >
              <X className="w-4 h-4" />
              Unassign
            </button>
          )}
        </div>
      )}
      
      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
        />
      )}
      
      {/* Phase 5: Workflow Result Drawer */}
      <WorkflowRunDrawer
        result={workflowResult}
        isOpen={showWorkflowDrawer}
        onClose={() => setShowWorkflowDrawer(false)}
      />
    </div>
  );
}

/**
 * Check if action should be hidden (snoozed)
 */
function isActionSnoozed(action: CommandCenterAction): boolean {
  if (action.status === 'snoozed') return true;
  if (action.snoozeUntil) {
    return new Date(action.snoozeUntil) > new Date();
  }
  return false;
}

/**
 * Partition unified actions into active and snoozed
 */
function partitionActions(actions: CommandCenterAction[]): {
  active: CommandCenterAction[];
  snoozed: CommandCenterAction[];
} {
  const active: CommandCenterAction[] = [];
  const snoozed: CommandCenterAction[] = [];
  
  for (const action of actions) {
    if (isActionSnoozed(action)) {
      snoozed.push(action);
    } else {
      active.push(action);
    }
  }
  
  return { active, snoozed };
}

// ============================================================================
// ACTION INSIGHTS TABS - Unified tabbed interface for Actions/Warnings/Discoveries
// ============================================================================

type ActionTab = 'actions' | 'warnings' | 'discoveries' | 'google_ads';

interface ActionInsightsTabsProps {
  // Actions props
  actions: AgentIntelligence['actions'];
  unifiedActionsForActions?: CommandCenterAction[];
  // Warnings props  
  warnings: AgentIntelligence['warnings'];
  unifiedActionsForWarnings?: CommandCenterAction[];
  // Discoveries props
  insights: AgentIntelligence['insights'];
  unifiedActionsForDiscoveries?: CommandCenterAction[];
  // Shared props
  acknowledgedHashes: Set<string>;
  onAcknowledge: (contentHash: string, category: string, title: string) => Promise<void>;
  onUnacknowledge: (contentHash: string) => Promise<void>;
  showAcknowledged: boolean;
  beliefSummary?: AgentIntelligence['beliefSummary'];
  // Actions-specific handlers
  onStatusChange?: (actionId: string, status: ActionStatus) => Promise<void>;
  onAssign?: (actionId: string, assignee: string | null) => Promise<void>;
  onSnooze?: (actionId: string, until: string | null) => Promise<void>;
  createdActionIds?: string[];
  // Counts for badges
  actionCount: number;
  warningCount: number;
  discoveryCount: number;
  googleAdsCount: number;
}

function ActionInsightsTabs({
  actions,
  unifiedActionsForActions,
  warnings,
  unifiedActionsForWarnings,
  insights,
  unifiedActionsForDiscoveries,
  acknowledgedHashes,
  onAcknowledge,
  onUnacknowledge,
  showAcknowledged,
  beliefSummary,
  onStatusChange,
  onAssign,
  onSnooze,
  createdActionIds = [],
  actionCount,
  warningCount,
  discoveryCount,
  googleAdsCount,
}: ActionInsightsTabsProps) {
  const [activeTab, setActiveTab] = useState<ActionTab>('actions');

  const getTabStyles = (tabId: ActionTab) => {
    const isActive = activeTab === tabId;
    const styles = {
      actions: {
        active: 'bg-emerald-50 border-emerald-500 text-emerald-700',
        inactive: 'border-emerald-200 text-emerald-600 hover:bg-emerald-50/50',
        icon: isActive ? 'text-emerald-600' : 'text-emerald-500',
        badge: isActive ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700',
      },
      warnings: {
        active: 'bg-amber-50 border-amber-500 text-amber-700',
        inactive: 'border-amber-200 text-amber-600 hover:bg-amber-50/50',
        icon: isActive ? 'text-amber-600' : 'text-amber-500',
        badge: isActive ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-700',
      },
      discoveries: {
        active: 'bg-purple-50 border-purple-500 text-purple-700',
        inactive: 'border-purple-200 text-purple-600 hover:bg-purple-50/50',
        icon: isActive ? 'text-purple-600' : 'text-purple-500',
        badge: isActive ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-700',
      },
      google_ads: {
        active: 'bg-blue-50 border-blue-500 text-blue-700',
        inactive: 'border-blue-200 text-blue-600 hover:bg-blue-50/50',
        icon: isActive ? 'text-blue-600' : 'text-blue-500',
        badge: isActive ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-700',
      },
    };
    return styles[tabId];
  };

  const tabs: { id: ActionTab; label: string; icon: React.ReactNode; count: number }[] = [
    { id: 'actions', label: 'Do These Today', icon: <Zap className="w-4 h-4" />, count: actionCount },
    { id: 'warnings', label: 'Early Warnings', icon: <AlertTriangle className="w-4 h-4" />, count: warningCount },
    { id: 'discoveries', label: 'Pattern Discoveries', icon: <Sparkles className="w-4 h-4" />, count: discoveryCount },
    { id: 'google_ads', label: 'Google Ads Advisory', icon: <Target className="w-4 h-4" />, count: googleAdsCount },
  ];

  return (
    <div id="action-insights" className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      {/* Tab Headers - Color-coded with visible borders */}
      <div className="flex gap-2 p-2 bg-gray-100 border-b border-gray-200">
        {tabs.map((tab) => {
          const styles = getTabStyles(tab.id);
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border-2 transition-all ${
                isActive ? styles.active : styles.inactive
              }`}
            >
              <span className={styles.icon}>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${styles.badge}`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="p-4 bg-gray-50">
        {activeTab === 'actions' && (
          <PriorityActions
            actions={actions}
            unifiedActions={unifiedActionsForActions}
            acknowledgedHashes={acknowledgedHashes}
            onAcknowledge={onAcknowledge}
            onUnacknowledge={onUnacknowledge}
            showAcknowledged={showAcknowledged}
            beliefSummary={beliefSummary}
            onStatusChange={onStatusChange}
            onAssign={onAssign}
            onSnooze={onSnooze}
            createdActionIds={createdActionIds}
          />
        )}
        {activeTab === 'warnings' && (
          <EarlyWarnings
            warnings={warnings}
            unifiedActions={unifiedActionsForWarnings}
            acknowledgedHashes={acknowledgedHashes}
            onAcknowledge={onAcknowledge}
            onUnacknowledge={onUnacknowledge}
            showAcknowledged={showAcknowledged}
          />
        )}
        {activeTab === 'discoveries' && (
          <div className="space-y-4">
            <PatternDiscoveries
              insights={insights}
              unifiedActions={unifiedActionsForDiscoveries}
              acknowledgedHashes={acknowledgedHashes}
              onAcknowledge={onAcknowledge}
              onUnacknowledge={onUnacknowledge}
              showAcknowledged={showAcknowledged}
            />
            {/* Cross-Channel Patterns integrated into discoveries */}
            <CrossChannelPatternsPanel />
          </div>
        )}
        {activeTab === 'google_ads' && (
          <div className="p-4">
            <GoogleAdvisoryPanel isFullTab />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// GA-R4A: BUSINESS STATE BANNER
// ============================================================================

interface BusinessStateProps {
  state: {
    primaryState: string;
    stateLabel: string;
    confidence: number;
    supportingSignals: string[];
    explanation: string;
    urgency: 'low' | 'medium' | 'high';
    implications: string[];
  };
}

function BusinessStateBanner({ state }: BusinessStateProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // State-specific styling
  const stateStyles: Record<string, { bg: string; border: string; text: string; icon: string }> = {
    post_promo_normalization: {
      bg: 'bg-sky-50',
      border: 'border-sky-200',
      text: 'text-sky-800',
      icon: '📅',
    },
    forecast_miss: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-800',
      icon: '📊',
    },
    execution_constraint: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: '⚠️',
    },
    demand_softening: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: '📉',
    },
    growth_opportunity: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-800',
      icon: '🚀',
    },
  };
  
  const style = stateStyles[state.primaryState] || stateStyles.post_promo_normalization;
  const confidenceLabel = state.confidence >= 0.8 ? 'High Confidence' : 
                          state.confidence >= 0.5 ? 'Medium Confidence' : 'Low Confidence';
  
  return (
    <div className={`mb-6 p-4 rounded-lg border-2 ${style.bg} ${style.border}`}>
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{style.icon}</span>
          <div>
            <div className="flex items-center gap-2">
              <Brain className={`w-4 h-4 ${style.text}`} />
              <span className={`font-semibold ${style.text}`}>
                Current Business State: {state.stateLabel}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${style.bg} ${style.text} border ${style.border}`}>
                {confidenceLabel}
              </span>
            </div>
            <p className={`text-sm mt-0.5 ${style.text} opacity-80`}>
              {state.implications?.[0] || state.explanation}
            </p>
          </div>
        </div>
        
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`flex items-center gap-1 text-sm ${style.text} hover:opacity-70 transition-opacity`}
        >
          {isExpanded ? (
            <>
              <span>Less</span>
              <ChevronUp className="w-4 h-4" />
            </>
          ) : (
            <>
              <span>Why this state?</span>
              <ChevronDown className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-current/10">
          {/* Supporting Signals */}
          {state.supportingSignals && state.supportingSignals.length > 0 && (
            <div className="mb-3">
              <p className={`text-xs font-semibold uppercase tracking-wide ${style.text} opacity-60 mb-2`}>
                Supporting Signals
              </p>
              <ul className="space-y-1">
                {state.supportingSignals.map((signal, i) => (
                  <li key={i} className={`text-sm ${style.text} flex items-start gap-2`}>
                    <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{signal}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Implications */}
          {state.implications && state.implications.length > 1 && (
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide ${style.text} opacity-60 mb-2`}>
                What This Means
              </p>
              <ul className="space-y-1">
                {state.implications.slice(1).map((impl, i) => (
                  <li key={i} className={`text-sm ${style.text} flex items-start gap-2`}>
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>{impl}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// V3-10A: DOMINANT BELIEF BANNER
// ============================================================================

interface BeliefBannerProps {
  belief: {
    primary: string | null;
    confidence: number | null;
    contributors: any[];
    exceptions: any[];
  };
}

function BeliefBanner({ belief }: BeliefBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const primaryLabel = belief.primary ? belief.primary.replace(/_/g, ' ') : 'Unknown';
  const confidenceLabel = belief.confidence != null
    ? belief.confidence >= 0.8
      ? 'High Confidence'
      : belief.confidence >= 0.5
        ? 'Medium Confidence'
        : 'Low Confidence'
    : 'Unknown Confidence';

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-700" />
            <span className="font-semibold text-indigo-800">Current Belief: {primaryLabel}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">
              {confidenceLabel}
            </span>
          </div>
          <p className="text-sm text-indigo-700 mt-1">
            Exceptions surfaced: {belief.exceptions?.length || 0}
          </p>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-indigo-700 text-xs flex items-center gap-1"
        >
          {isExpanded ? 'Hide details' : 'Show contributors'}
          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-indigo-200">
          {Array.isArray(belief.contributors) && belief.contributors.length > 0 ? (
            <div>
              <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">
                Contributing Beliefs
              </div>
              <ul className="space-y-1 text-sm text-indigo-700">
                {belief.contributors.slice(0, 2).map((c: any, idx: number) => (
                  <li key={idx} className="flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>{String(c.beliefId || c.belief || '').replace(/_/g, ' ')}</span>
                    {typeof c.score === 'number' && (
                      <span className="text-xs text-indigo-500">({Math.round(c.score * 100)}%)</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-sm text-indigo-600">No contributors for this belief.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// V2.7: RETAIL CALENDAR NARRATIVE BANNER
// ============================================================================

interface RetailCalendarNarrativeBannerProps {
  narrative: {
    calendarWeekKey: string;
    calendarWeekNumber: number;
    overall: {
      expectedRange: { low: number; median: number; high: number } | null;
      actual: number | null;
      withinExpected: boolean | null;
      confidence: number;
      note: string;
    };
    byChannel: Array<{
      channel: string;
      expectedRange: { low: number; median: number; high: number } | null;
      actual: number | null;
      wowPct: number | null;
      yoyPct: number | null;
      withinExpected: boolean | null;
      confidence: number | null;
      narrative: string;
    }>;
    context: {
      promoEndedRecently: boolean;
      promoActive: boolean;
      forecastErrorStatus: string | null;
    };
  };
}

function formatCurrencyShort(num: number | null): string {
  if (num === null) return '—';
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

function RetailCalendarNarrativeBanner({ narrative }: RetailCalendarNarrativeBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const { overall, byChannel, context } = narrative;
  const confidenceLabel = overall.confidence >= 0.8 ? 'High' 
    : overall.confidence >= 0.6 ? 'Medium' : 'Low';
  
  // Determine styling based on whether within expected
  const withinStyle = overall.withinExpected === true
    ? { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: '✓' }
    : overall.withinExpected === false
      ? { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: '!' }
      : { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', icon: '?' };
  
  return (
    <div className={`mb-4 p-4 rounded-lg border ${withinStyle.bg} ${withinStyle.border}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className={`w-4 h-4 ${withinStyle.text}`} />
            <span className={`font-semibold ${withinStyle.text}`}>
              Seasonality Check (Week {narrative.calendarWeekNumber})
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${withinStyle.bg} ${withinStyle.border} ${withinStyle.text}`}>
              {confidenceLabel} Confidence
            </span>
          </div>
          <p className={`text-sm ${withinStyle.text}`}>
            {overall.note}
          </p>
          
          {/* Context badges */}
          <div className="flex items-center gap-2 mt-2">
            {context.promoActive && (
              <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 border border-violet-200 rounded-full">
                Promo Active
              </span>
            )}
            {context.promoEndedRecently && (
              <span className="text-xs px-2 py-0.5 bg-sky-100 text-sky-700 border border-sky-200 rounded-full">
                Post-Promo
              </span>
            )}
            {overall.actual !== null && overall.expectedRange && (
              <span className={`text-xs px-2 py-0.5 rounded-full border ${
                overall.withinExpected === true 
                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                  : overall.withinExpected === false
                    ? 'bg-amber-100 text-amber-700 border-amber-200'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
              }`}>
                {formatCurrencyShort(overall.actual)} vs {formatCurrencyShort(overall.expectedRange.low)}-{formatCurrencyShort(overall.expectedRange.high)}
              </span>
            )}
          </div>
        </div>
        
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`text-xs flex items-center gap-1 ${withinStyle.text}`}
        >
          {isExpanded ? 'Hide channels' : 'Show channels'}
          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>
      
      {isExpanded && (
        <div className={`mt-3 pt-3 border-t ${withinStyle.border}`}>
          <div className="space-y-2">
            {byChannel.filter(c => c.actual !== null).map((channel, idx) => {
              const channelWithin = channel.withinExpected;
              return (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 flex items-center justify-center text-xs rounded-full ${
                      channelWithin === true 
                        ? 'bg-emerald-100 text-emerald-600'
                        : channelWithin === false
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-slate-100 text-slate-500'
                    }`}>
                      {channelWithin === true ? '✓' : channelWithin === false ? '!' : '?'}
                    </span>
                    <span className={`font-medium ${withinStyle.text}`}>{channel.channel}</span>
                  </div>
                  <div className={`text-xs ${withinStyle.text} opacity-80`}>
                    {formatCurrencyShort(channel.actual)}
                    {channel.expectedRange && (
                      <span className="ml-2 text-xs opacity-60">
                        (expected: {formatCurrencyShort(channel.expectedRange.low)}-{formatCurrencyShort(channel.expectedRange.high)})
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// QUICK NAV - Sticky section navigation
// ============================================================================

interface QuickNavProps {
  snoozedCount: number;
}

function QuickNav({ snoozedCount }: QuickNavProps) {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections: { id: string; label: string; icon: React.ReactNode; color: string }[] = [
    { id: 'action-insights', label: 'Actions', icon: <Zap className="w-3.5 h-3.5" />, color: 'violet' },
    { id: 'playbooks', label: 'Playbooks', icon: <BookOpen className="w-3.5 h-3.5" />, color: 'blue' },
    { id: 'performance', label: 'Metrics', icon: <BarChart3 className="w-3.5 h-3.5" />, color: 'teal' },
    { id: 'admin', label: 'Admin', icon: <Settings className="w-3.5 h-3.5" />, color: 'slate' },
  ];

  // Add snoozed if there are snoozed items
  if (snoozedCount > 0) {
    sections.splice(1, 0, { id: 'snoozed', label: `Snoozed (${snoozedCount})`, icon: <Pause className="w-3.5 h-3.5" />, color: 'purple' });
  }

  const colorStyles: Record<string, string> = {
    violet: 'border-violet-300 text-violet-700 hover:bg-violet-50 hover:border-violet-400',
    purple: 'border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400',
    blue: 'border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400',
    teal: 'border-teal-300 text-teal-700 hover:bg-teal-50 hover:border-teal-400',
    slate: 'border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400',
  };

  return (
    <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-200 mb-4 -mx-4 sm:-mx-6 px-4 sm:px-6">
      <div className="flex items-center gap-2 py-2.5 overflow-x-auto scrollbar-hide">
        <span className="text-xs font-medium text-gray-500 mr-1 hidden sm:inline">Jump to:</span>
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border bg-white transition-all whitespace-nowrap ${colorStyles[section.color]}`}
          >
            {section.icon}
            {section.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// PRIORITY ACTIONS SECTION
// ============================================================================

interface PriorityActionsProps {
  actions: AgentIntelligence['actions'];
  unifiedActions?: CommandCenterAction[];  // Phase 1 - Unified model
  acknowledgedHashes: Set<string>;
  onAcknowledge: (contentHash: string, category: string, title: string) => Promise<void>;
  onUnacknowledge: (contentHash: string) => Promise<void>;
  showAcknowledged: boolean;
  beliefSummary?: AgentIntelligence['beliefSummary'];
  // Phase 2 - Workflow handlers
  onStatusChange?: (actionId: string, status: ActionStatus) => Promise<void>;
  onAssign?: (actionId: string, assignee: string | null) => Promise<void>;
  onSnooze?: (actionId: string, until: string | null) => Promise<void>;
  // Phase 9E - Created actions highlight
  createdActionIds?: string[];
}

function PriorityActions({ actions, unifiedActions, acknowledgedHashes, onAcknowledge, onUnacknowledge, showAcknowledged, beliefSummary, onStatusChange, onAssign, onSnooze, createdActionIds = [] }: PriorityActionsProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);
  
  // Phase 3: Explain drawer state
  const [explainAction, setExplainAction] = useState<CommandCenterAction | null>(null);
  
  // Create lookup for unified actions by contentHash (for workflow controls)
  const unifiedActionMap = new Map<string, CommandCenterAction>();
  if (unifiedActions) {
    for (const ua of unifiedActions) {
      unifiedActionMap.set(ua.contentHash, ua);
    }
  }
  
  // Use unified actions if provided (Phase 1), otherwise fall back to legacy
  const allActions: DailyAction[] = unifiedActions
    ? unifiedActions.map(unifiedToLegacyAction)
    : [
        ...(actions?.dailyActions || []),
        ...(actions?.weeklyActions || [])
      ]
        .map(a => ({ ...a, isAcknowledged: acknowledgedHashes.has(a.contentHash) }))
        .filter(a => showAcknowledged || !a.isAcknowledged)
        .sort((a, b) => b.impactDollars - a.impactDollars)
        .slice(0, 5);

  const handleAcknowledge = async (action: DailyAction, e: React.MouseEvent) => {
    e.stopPropagation();
    setAcknowledgingId(action.id);
    try {
      await onAcknowledge(action.contentHash, action.category, action.action);
    } finally {
      setAcknowledgingId(null);
    }
  };

  const handleUnacknowledge = async (action: DailyAction, e: React.MouseEvent) => {
    e.stopPropagation();
    setAcknowledgingId(action.id);
    try {
      await onUnacknowledge(action.contentHash);
    } finally {
      setAcknowledgingId(null);
    }
  };

  if (allActions.length === 0) {
  return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Target className="w-5 h-5 text-emerald-600" />
            </div>
          <h3 className="font-bold text-gray-900">Priority Actions</h3>
            </div>
        <div className="text-center py-8 text-gray-500">
          <Activity className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>AI agents are analyzing your data...</p>
          <p className="text-sm mt-1">Actions will appear as opportunities are identified</p>
            </div>
            </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Target className="w-5 h-5 text-emerald-600" />
              </div>
            <div>
              <h3 className="font-bold text-gray-900">Do These Today</h3>
              <p className="text-xs text-gray-500">AI-prioritized by impact</p>
              </div>
                        </div>
          {actions?.totalPotentialImpact && actions.totalPotentialImpact > 0 && (
                        <div className="text-right">
              <p className="text-sm font-bold text-emerald-600">{formatCurrencyCompact(actions.totalPotentialImpact)}</p>
              <p className="text-xs text-gray-500">total potential</p>
                        </div>
                      )}
                    </div>
                  </div>

      <div className="divide-y divide-gray-100">
        {allActions.map((action, index) => {
          const effort = getEffortBadge(action.effort);
          const isExpanded = expandedId === action.id;
          const isAcknowledged = action.isAcknowledged;
          
          // Phase 9E/V3-3A: Check if this action was just created from a workflow or brain finding
          const ua = unifiedActionMap.get(action.contentHash);
          const isNewlyCreated = ua?.id ? createdActionIds.includes(ua.id) : false;
          
          // V3-3A: Also check notes for brain finding source
          let notesObjCheck: any = null;
          try {
            notesObjCheck = ua?.notes ? (typeof ua.notes === 'string' ? JSON.parse(ua.notes) : ua.notes) : null;
          } catch {}
          const isFromBrain = notesObjCheck?.source === 'brain_finding';
          const shouldHighlight = isNewlyCreated || isFromBrain;
          
          return (
            <div 
              key={action.id} 
              className={`p-4 cursor-pointer transition-all ${
                shouldHighlight
                  ? 'bg-violet-50 border-l-4 border-violet-500'
                  : isAcknowledged 
                    ? 'opacity-50 bg-gray-50' 
                    : isExpanded 
                      ? 'bg-emerald-50' 
                      : 'hover:bg-gray-50'
              }`}
              onClick={() => setExpandedId(isExpanded ? null : action.id)}
            >
              <div className="flex items-start gap-3">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold ${
                  isAcknowledged 
                    ? 'bg-gray-300 text-gray-500' 
                    : index === 0 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-gray-100 text-gray-600'
                }`}>
                  {isAcknowledged ? <Check className="w-4 h-4" /> : index + 1}
              </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {/* Phase 2: Show status badge if not 'new' */}
                    {(() => {
                      const ua = unifiedActionMap.get(action.contentHash);
                      return ua && ua.status !== 'new' ? <StatusBadge status={ua.status} /> : null;
                    })()}
                    {/* Phase 2: Show assignee pill */}
                    {(() => {
                      const ua = unifiedActionMap.get(action.contentHash);
                      return ua?.assignedTo ? <AssigneePill assignedTo={ua.assignedTo} /> : null;
                    })()}
                    {/* Phase 3: Show confidence badge */}
                    {(() => {
                      const ua = unifiedActionMap.get(action.contentHash);
                      return ua?.confidence != null ? <ConfidenceBadge confidence={ua.confidence} /> : null;
                    })()}
                    {/* V3-3B: Proven Play badge */}
                    {(() => {
                      const ua = unifiedActionMap.get(action.contentHash);
                      let notesObj: any = null;
                      try {
                        notesObj = ua?.notes ? (typeof ua.notes === 'string' ? JSON.parse(ua.notes) : ua.notes) : null;
                      } catch {}
                      return notesObj?.isProvenPlay ? <ProvenPlayBadge isProvenPlay={true} /> : null;
                    })()}
                    {/* V3-3B: Outcome Status badge */}
                    {(() => {
                      const ua = unifiedActionMap.get(action.contentHash);
                      let notesObj: any = null;
                      try {
                        notesObj = ua?.notes ? (typeof ua.notes === 'string' ? JSON.parse(ua.notes) : ua.notes) : null;
                      } catch {}
                      return notesObj?.outcomeStatus ? <OutcomeStatusBadge outcome={notesObj.outcomeStatus} /> : null;
                    })()}
                    {isAcknowledged && (
                      <button
                        onClick={(e) => handleUnacknowledge(action, e)}
                        disabled={acknowledgingId === action.id}
                        className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors"
                        title="Click to reopen"
                      >
                        {acknowledgingId === action.id ? 'Reopening...' : 'Done ↩'}
                      </button>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${effort.color}`}>
                      {effort.text}
                    </span>
                    {action.retailerName && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                        {action.retailerName}
                      </span>
                    )}
                    {action.cadence === 'daily' && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
                        DTC
                      </span>
                    )}
                    {/* GA-R4B: State-Aware Status Badge */}
                    {(() => {
                      const ua = unifiedActionMap.get(action.contentHash);
                      const stateBadge = getStateStatusBadge(ua?.stateStatus);
                      if (!stateBadge) return null;
                      return (
                        <span 
                          className={`px-2 py-0.5 rounded-full text-xs font-medium border ${stateBadge.color}`}
                          title={stateBadge.tooltip}
                        >
                          {stateBadge.text}
                        </span>
                      );
                    })()}
                    {/* Phase 9E/V3-3A: Show "Created from" badge */}
                    {(() => {
                      const ua = unifiedActionMap.get(action.contentHash);
                      // Parse notes to check source
                      let notesObj: any = null;
                      try {
                        notesObj = ua?.notes ? (typeof ua.notes === 'string' ? JSON.parse(ua.notes) : ua.notes) : null;
                      } catch {}
                      const isFromBrain = notesObj?.source === 'brain_finding';
                      const isFromWorkflow = notesObj?.createdFromWorkflow === true;
                      
                      if (isNewlyCreated || isFromBrain) {
                        return (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-violet-100 text-violet-700 font-medium">
                            ✓ Created from Brain
                          </span>
                        );
                      }
                      if (isFromWorkflow) {
                        return (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700 font-medium">
                            ✓ Created from workflow
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <p className="font-medium text-gray-900 mb-1">{action.action}</p>
                  <p className="text-sm text-gray-500">{action.rationale}</p>
                  
                  {/* Context Events Display */}
                  {(() => {
                    const ua = unifiedActionMap.get(action.contentHash);
                    if (!ua?.contextEvents || ua.contextEvents.length === 0) return null;
                    return (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {ua.contextEvents.map((event: any, i: number) => (
                          <span 
                            key={event.id || i}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                              event.impact === 'up' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              event.impact === 'down' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                              'bg-amber-50 text-amber-700 border-amber-200'
                            }`}
                            title={event.impactRange ? `Expected: ${event.impactRange[0]}% to ${event.impactRange[1]}%` : undefined}
                          >
                            <Calendar className="w-3 h-3" />
                            {event.name}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                  
                  {action.impactDollars > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <DollarSign className="w-3 h-3 text-emerald-500" />
                      <span className="text-sm font-medium text-emerald-600">
                        {formatCurrency(action.impactDollars)} potential impact
                      </span>
            </div>
          )}

                  {/* Expanded Details - AI Evidence Panel */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-emerald-200 space-y-4">
                      {/* Expected Impact */}
                      <div className="bg-white rounded-lg p-3 border border-emerald-100">
                        <p className="text-xs font-semibold text-gray-700 mb-1">📊 Expected Impact</p>
                        <p className="text-sm text-gray-600">{action.expectedImpact}</p>
                      </div>
                      
                      {/* AI Evidence Section */}
                      {action.aiEvidence && (
                        <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-lg p-4 border border-violet-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Brain className="w-4 h-4 text-violet-600" />
                            <span className="text-sm font-bold text-violet-800">Why AI Recommends This</span>
                            {action.aiEvidence.confidenceScore != null && (
                              <span className="ml-auto px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full font-medium">
                                {Math.round(action.aiEvidence.confidenceScore * 100)}% confidence
                              </span>
                            )}
                            {action.aiEvidence.inferenceType && (
                              <span className="ml-auto px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full font-medium">
                                {action.aiEvidence.inferenceType === 'hybrid' ? 'Brain-Powered' : action.aiEvidence.inferenceType}
                              </span>
                            )}
                          </div>
                          
                          {/* Methodology (legacy) or Description */}
                          {action.aiEvidence.methodology && (
                            <p className="text-sm text-gray-700 mb-3">{action.aiEvidence.methodology}</p>
                          )}

                          {/* V3-10A: Dominant Belief Context */}
                          {beliefSummary?.primary && (
                            <div className="bg-white rounded-lg p-3 border border-violet-100 mb-3">
                              <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-indigo-500" />
                                Dominant Belief Context
                              </p>
                              <div className="text-sm text-gray-700">
                                <div>
                                  <span className="font-medium">Primary:</span>{' '}
                                  {beliefSummary.primary.replace(/_/g, ' ')}
                                  {beliefSummary.confidence != null && (
                                    <span className="text-xs text-gray-500 ml-2">
                                      ({Math.round(beliefSummary.confidence * 100)}% confidence)
                                    </span>
                                  )}
                                </div>
                                {Array.isArray(beliefSummary.contributors) && beliefSummary.contributors.length > 0 && (
                                  <div className="mt-1 text-xs text-gray-600">
                                    Contributors:{' '}
                                    {beliefSummary.contributors
                                      .slice(0, 2)
                                      .map((c: any) => String(c.beliefId || c.belief || '').replace(/_/g, ' '))
                                      .filter(Boolean)
                                      .join(', ') || '—'}
                                  </div>
                                )}
                                {(() => {
                                  const ua = unifiedActionMap.get(action.contentHash);
                                  if (ua?.beliefException) {
                                    return (
                                      <div className="mt-2 text-xs text-amber-700">
                                        Exception surfaced: contradicts the primary belief and is material.
                                      </div>
                                    );
                                  }
                                  if (ua?.suppressCase) {
                                    return (
                                      <div className="mt-2 text-xs text-gray-500">
                                        Suppressed in cases: consistent with the primary belief.
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </div>
                          )}
                          
                          {/* Supporting Metrics Grid (legacy) */}
                          {action.aiEvidence.supportingMetrics && action.aiEvidence.supportingMetrics.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                              {action.aiEvidence.supportingMetrics.map((metric, idx) => (
                                <div key={idx} className="bg-white rounded-lg p-2 border border-violet-100">
                                  <p className="text-xs text-gray-500">{metric.label}</p>
                                  <div className="flex items-center gap-1">
                                    <p className="text-sm font-bold text-gray-900">{metric.value}</p>
                                    {metric.trend === 'up' && <TrendingUp className="w-3 h-3 text-emerald-500" />}
                                    {metric.trend === 'down' && <TrendingDown className="w-3 h-3 text-red-500" />}
                                  </div>
                                  {metric.trendPct !== undefined && metric.trendPct !== 0 && (
                                    <p className={`text-xs ${metric.trendPct > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {metric.trendPct > 0 ? '+' : ''}{metric.trendPct.toFixed(0)}% vs prior
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Key Metrics (Brain findings) */}
                          {action.aiEvidence.keyMetrics && action.aiEvidence.keyMetrics.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                              {action.aiEvidence.keyMetrics.slice(0, 6).map((metric: any, idx: number) => (
                                <div key={idx} className="bg-white rounded-lg p-2 border border-violet-100">
                                  <p className="text-xs text-gray-500">{metric.label || metric.name}</p>
                                  <p className="text-sm font-bold text-gray-900">{metric.value}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Historical Context */}
                          {action.aiEvidence.historicalContext && (
                            <div className="flex items-start gap-2 p-2 bg-white rounded border border-violet-100 mb-2">
                              <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-gray-700">{action.aiEvidence.historicalContext}</p>
                            </div>
                          )}
                          
                          {/* GA-R3B: Forecast Expectation Context */}
                          {action.forecastExpectation && (
                            <div className="bg-white rounded-lg p-3 border border-violet-100 mb-2">
                              <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                <TrendingUp className="w-3 h-3 text-blue-500" />
                                Forecast vs Actual
                              </p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                <div>
                                  <p className="text-gray-500">Forecast</p>
                                  <p className="font-bold text-gray-900">
                                    {action.forecastExpectation.forecastValue > 0 
                                      ? `$${(action.forecastExpectation.forecastValue / 1000).toFixed(0)}K`
                                      : 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Error</p>
                                  <p className={`font-bold ${action.forecastExpectation.errorPct > 15 ? 'text-red-600' : 'text-gray-900'}`}>
                                    {action.forecastExpectation.errorPct.toFixed(1)}%
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Expected Band</p>
                                  <p className="font-bold text-gray-900">
                                    0-{action.forecastExpectation.errorBand[1]?.toFixed(0) || '12'}%
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Status</p>
                                  <p className={`font-bold ${action.forecastExpectation.status === 'within_expected_error_band' ? 'text-emerald-600' : 'text-amber-600'}`}>
                                    {action.forecastExpectation.status === 'within_expected_error_band' 
                                      ? '✓ Normal Variance' 
                                      : action.forecastExpectation.status === 'outside_error_band'
                                      ? '⚠ Forecast Miss'
                                      : 'No Baseline'}
                                  </p>
                                </div>
                              </div>
                              {action.forecastExpectation.explanation && (
                                <p className="text-xs text-gray-600 mt-2 italic">
                                  {action.forecastExpectation.explanation}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Data Source & Verification */}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 pt-2 border-t border-violet-100">
                            {action.aiEvidence.dataSource && (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-emerald-500" />
                                Verified from: {action.aiEvidence.dataSource}
                              </span>
                            )}
                            {action.aiEvidence.dataSources && action.aiEvidence.dataSources.length > 0 && (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-emerald-500" />
                                Sources: {action.aiEvidence.dataSources.map((d: any) => d.name).join(', ')}
                              </span>
                            )}
                            {action.aiEvidence.fiscalWeek && (
                              <span>Week: {action.aiEvidence.fiscalWeek}</span>
                            )}
                            {action.aiEvidence.queryType && (
                              <span className="text-violet-600 font-medium">{action.aiEvidence.queryType.replace(/_/g, ' ')}</span>
                            )}
                            {action.aiEvidence.traceId && (
                              <span className="text-violet-600 font-medium">Run: {action.aiEvidence.traceId.slice(0, 8)}...</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Basic Info Footer */}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {action.cadence === 'daily' ? 'Daily Priority' : 'Weekly Priority'}
                        </span>
                        <span>Category: {action.category}</span>
                        {action.deadline && <span>Deadline: {action.deadline}</span>}
                      </div>
                      
                      {/* Acknowledge Button */}
                      {!isAcknowledged && (
                        <button
                          onClick={(e) => handleAcknowledge(action, e)}
                          disabled={acknowledgingId === action.id}
                          className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 bg-emerald-100 text-emerald-700 rounded-lg font-medium text-sm hover:bg-emerald-200 transition-colors disabled:opacity-50"
                        >
                          {acknowledgingId === action.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Mark as Done
                        </button>
                      )}
                    </div>
                  )}
                          </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  {/* Phase 3: "Why this?" button */}
                  {(() => {
                    const ua = unifiedActionMap.get(action.contentHash);
                    return ua ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setExplainAction(ua); }}
                        className="px-2 py-1 text-xs text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                        title="Why this action?"
                      >
                        Why?
                      </button>
                    ) : null;
                  })()}
                  {/* Phase 2: Action Workflow Controls */}
                  {onStatusChange && onAssign && onSnooze && (() => {
                    const ua = unifiedActionMap.get(action.contentHash);
                    return ua ? (
                      <ActionWorkflowControls
                        action={ua}
                        onStatusChange={onStatusChange}
                        onAssign={onAssign}
                        onSnooze={onSnooze}
                      />
                    ) : null;
                  })()}
                  {!isAcknowledged && !isExpanded && (
                    <button
                      onClick={(e) => handleAcknowledge(action, e)}
                      disabled={acknowledgingId === action.id}
                      className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                      title="Mark as done"
                    >
                      {acknowledgingId === action.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
              </div>
          );
        })}
            </div>
            
      {/* Phase 3: Explain Drawer */}
      <ActionExplainDrawer 
        action={explainAction}
        isOpen={explainAction !== null}
        onClose={() => setExplainAction(null)}
      />
            </div>
  );
}

// ============================================================================
// EARLY WARNINGS SECTION
// ============================================================================

interface EarlyWarningsProps {
  warnings: AgentIntelligence['warnings'];
  unifiedActions?: CommandCenterAction[];  // Phase 1 - Unified model
  acknowledgedHashes: Set<string>;
  onAcknowledge: (contentHash: string, category: string, title: string) => Promise<void>;
  onUnacknowledge: (contentHash: string) => Promise<void>;
  showAcknowledged: boolean;
}

function EarlyWarnings({ warnings, unifiedActions, acknowledgedHashes, onAcknowledge, onUnacknowledge, showAcknowledged }: EarlyWarningsProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);

  // Use unified actions if provided (Phase 1), otherwise fall back to legacy
  const allWarnings: EarlyWarning[] = unifiedActions
    ? unifiedActions.map(unifiedToLegacyWarning)
    : [
        ...(warnings?.dailyWarnings || []),
        ...(warnings?.weeklyWarnings || [])
      ]
        .map(w => ({ ...w, isAcknowledged: acknowledgedHashes.has(w.contentHash) }))
        .filter(w => showAcknowledged || !w.isAcknowledged)
        .sort((a, b) => {
          const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          return (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0);
        })
        .slice(0, 4);

  const handleAcknowledge = async (warning: EarlyWarning, e: React.MouseEvent) => {
    e.stopPropagation();
    setAcknowledgingId(warning.id || warning.contentHash);
    try {
      await onAcknowledge(warning.contentHash, warning.riskType, warning.title);
    } finally {
      setAcknowledgingId(null);
    }
  };

  const handleUnacknowledge = async (warning: EarlyWarning, e: React.MouseEvent) => {
    e.stopPropagation();
    setAcknowledgingId(warning.id || warning.contentHash);
    try {
      await onUnacknowledge(warning.contentHash);
    } finally {
      setAcknowledgingId(null);
    }
  };

  if (allWarnings.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <h3 className="font-bold text-gray-900">Early Warnings</h3>
        </div>
        <div className="text-center py-8">
          <ThumbsUp className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
          <p className="text-gray-600 font-medium">All Clear</p>
          <p className="text-sm text-gray-400 mt-1">No critical risks detected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
            <div>
              <h3 className="font-bold text-gray-900">Early Warnings</h3>
              <p className="text-xs text-gray-500">Risks detected before impact</p>
              </div>
                      </div>
          {warnings?.totalImpactDollars && warnings.totalImpactDollars > 0 && (
            <div className="text-right">
              <p className="text-sm font-bold text-red-600">{formatCurrencyCompact(warnings.totalImpactDollars)}</p>
              <p className="text-xs text-gray-500">at risk</p>
                        </div>
          )}
                        </div>
                      </div>

      <div className="divide-y divide-gray-100">
        {allWarnings.map((warning, index) => {
          const warnId = warning.id || `warning-${index}`;
          const isExpanded = expandedId === warnId;
          const isAcknowledged = warning.isAcknowledged;
          return (
            <div 
              key={warnId} 
              className={`p-4 cursor-pointer transition-all ${
                isAcknowledged 
                  ? 'opacity-50 bg-gray-50' 
                  : isExpanded 
                    ? 'bg-amber-50' 
                    : 'hover:bg-gray-50'
              }`}
              onClick={() => setExpandedId(isExpanded ? null : warnId)}
            >
              <div className="flex items-start gap-3">
                {isAcknowledged ? (
                  <button
                    onClick={(e) => handleUnacknowledge(warning, e)}
                    disabled={acknowledgingId === warnId}
                    className="px-2 py-1 rounded-md text-xs font-bold uppercase bg-gray-200 text-gray-500 hover:bg-gray-300 transition-colors"
                    title="Click to reopen"
                  >
                    {acknowledgingId === warnId ? '...' : 'Done ↩'}
                  </button>
                ) : (
                  <div className={`px-2 py-1 rounded-md text-xs font-bold uppercase ${getSeverityColor(warning.severity)}`}>
                    {warning.severity}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`font-medium mb-1 ${isAcknowledged ? 'text-gray-500' : 'text-gray-900'}`}>{warning.title}</p>
                  <p className="text-sm text-gray-500 mb-2">{warning.description}</p>
                  
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    {warning.retailerName && <span>📍 {warning.retailerName}</span>}
                    {warning.estimatedImpactDollars > 0 && (
                      <span className={isAcknowledged ? 'text-gray-400' : 'text-red-500 font-medium'}>
                        💰 {formatCurrency(warning.estimatedImpactDollars)} at risk
                      </span>
                    )}
                    <span>🎯 {Math.round(warning.confidence * 100)}% confidence</span>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-amber-200 space-y-3">
                      {warning.recommendedAction && (
                        <div className="flex items-start gap-2 p-3 bg-white rounded-lg border border-amber-100">
                          <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Recommended Action</p>
                            <p className="text-sm text-gray-600">{warning.recommendedAction}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* AI Evidence Section */}
                      {warning.aiEvidence && (
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Brain className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-bold text-amber-800">Why AI Flagged This</span>
                            {warning.aiEvidence.confidenceScore != null && (
                              <span className="ml-auto px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                                {Math.round(warning.aiEvidence.confidenceScore * 100)}% confidence
                              </span>
                            )}
                          </div>
                          
                          {warning.aiEvidence.methodology && (
                            <p className="text-sm text-gray-700 mb-3">{warning.aiEvidence.methodology}</p>
                          )}
                          
                          {warning.aiEvidence.supportingMetrics && warning.aiEvidence.supportingMetrics.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                              {warning.aiEvidence.supportingMetrics.map((metric, idx) => (
                                <div key={idx} className="bg-white rounded-lg p-2 border border-amber-100">
                                  <p className="text-xs text-gray-500">{metric.label}</p>
                                  <div className="flex items-center gap-1">
                                    <p className="text-sm font-bold text-gray-900">{metric.value}</p>
                                    {metric.trend === 'up' && <TrendingUp className="w-3 h-3 text-emerald-500" />}
                                    {metric.trend === 'down' && <TrendingDown className="w-3 h-3 text-red-500" />}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {warning.aiEvidence.historicalContext && (
                            <div className="flex items-start gap-2 p-2 bg-white rounded border border-amber-100">
                              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-gray-700">{warning.aiEvidence.historicalContext}</p>
                            </div>
                          )}
                          
                          {(warning.aiEvidence?.dataSource || warning.aiEvidence?.queryType) && (
                            <div className="flex items-center gap-3 text-xs text-gray-500 pt-2 border-t border-amber-100 mt-2">
                              {warning.aiEvidence?.dataSource && (
                                <span className="flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3 text-amber-500" />
                                  Verified from: {warning.aiEvidence.dataSource}
                                </span>
                              )}
                              {warning.aiEvidence?.queryType && (
                                <span className="text-amber-600 font-medium">{warning.aiEvidence.queryType.replace(/_/g, ' ')}</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {warning.affectedSkus && warning.affectedSkus.length > 0 && (
                        <div className="bg-white rounded-lg p-3 border border-amber-100">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Affected SKUs</p>
                          <div className="flex flex-wrap gap-1">
                            {warning.affectedSkus.slice(0, 5).map((sku, i) => (
                              <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                {sku}
                              </span>
                            ))}
                            {warning.affectedSkus.length > 5 && (
                              <span className="text-xs text-gray-400">+{warning.affectedSkus.length - 5} more</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {warning.cadence === 'daily' ? 'Detected Today' : 'Weekly Alert'}
                        </span>
                        <span>Risk Type: {warning.riskType}</span>
                      </div>
                      
                      {/* Acknowledge Button */}
                      {!isAcknowledged && (
                        <button
                          onClick={(e) => handleAcknowledge(warning, e)}
                          disabled={acknowledgingId === warnId}
                          className="mt-2 w-full flex items-center justify-center gap-2 py-2 px-4 bg-amber-100 text-amber-700 rounded-lg font-medium text-sm hover:bg-amber-200 transition-colors disabled:opacity-50"
                        >
                          {acknowledgingId === warnId ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Mark as Addressed
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  {!isAcknowledged && !isExpanded && (
                    <button
                      onClick={(e) => handleAcknowledge(warning, e)}
                      disabled={acknowledgingId === warnId}
                      className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                      title="Mark as addressed"
                    >
                      {acknowledgingId === warnId ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-amber-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          );
        })}
            </div>
        </div>
  );
}

// ============================================================================
// PATTERN DISCOVERIES SECTION
// ============================================================================

interface PatternDiscoveriesProps {
  insights: AgentIntelligence['insights'];
  unifiedActions?: CommandCenterAction[];  // Phase 1 - Unified model
  acknowledgedHashes: Set<string>;
  onAcknowledge: (contentHash: string, category: string, title: string) => Promise<void>;
  onUnacknowledge: (contentHash: string) => Promise<void>;
  showAcknowledged: boolean;
}

function PatternDiscoveries({ insights, unifiedActions, acknowledgedHashes, onAcknowledge, onUnacknowledge, showAcknowledged }: PatternDiscoveriesProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);
  
  // Use unified actions if provided (Phase 1), otherwise fall back to legacy
  const allInsights: Insight[] = unifiedActions
    ? unifiedActions.map(unifiedToLegacyInsight)
    : [
        ...(insights?.topInsights || []),
        ...(insights?.dailyInsights || []),
        ...(insights?.weeklyInsights || [])
      ]
        .filter((insight, index, self) => 
          index === self.findIndex(i => i.id === insight.id)
        )
        .map(i => ({ ...i, isAcknowledged: i.contentHash ? acknowledgedHashes.has(i.contentHash) : false }))
        .filter(i => showAcknowledged || !i.isAcknowledged)
        .sort((a, b) => b.noveltyScore - a.noveltyScore)
        .slice(0, 4);

  const handleAcknowledge = async (insight: Insight, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!insight.contentHash) return;
    setAcknowledgingId(insight.id);
    try {
      await onAcknowledge(insight.contentHash, insight.category, insight.title);
    } finally {
      setAcknowledgingId(null);
    }
  };

  const handleUnacknowledge = async (insight: Insight, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!insight.contentHash) return;
    setAcknowledgingId(insight.id);
    try {
      await onUnacknowledge(insight.contentHash);
    } finally {
      setAcknowledgingId(null);
    }
  };

  if (allInsights.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Sparkles className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="font-bold text-gray-900">Pattern Discoveries</h3>
        </div>
        <div className="text-center py-8 text-gray-500">
          <Brain className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>Analyzing patterns across your data...</p>
          <p className="text-sm mt-1">Discoveries will appear as AI finds notable trends</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-purple-50 to-violet-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Pattern Discoveries</h3>
              <p className="text-xs text-gray-500">AI-detected insights you'd miss</p>
            </div>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {allInsights.map((insight) => {
          const novelty = getNoveltyLabel(insight.noveltyScore);
          const NoveltyIcon = novelty.icon;
          const isExpanded = expandedId === insight.id;
          
          return (
            <div 
              key={insight.id} 
              className={`p-4 cursor-pointer transition-all ${isExpanded ? 'bg-purple-50' : 'hover:bg-gray-50'} ${insight.isAcknowledged ? 'opacity-60' : ''}`}
              onClick={() => setExpandedId(isExpanded ? null : insight.id)}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg bg-purple-100`}>
                  <NoveltyIcon className={`w-4 h-4 ${novelty.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs font-medium ${novelty.color}`}>
                      {novelty.text}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                      {insight.category}
                    </span>
                    {insight.retailerName && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                        {insight.retailerName}
                      </span>
                    )}
                    {insight.isAcknowledged && (
                      <button
                        onClick={(e) => handleUnacknowledge(insight, e)}
                        disabled={acknowledgingId === insight.id}
                        className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-500 hover:bg-gray-300 transition-colors"
                        title="Click to reopen"
                      >
                        {acknowledgingId === insight.id ? '...' : '✓ Done ↩'}
                      </button>
                    )}
                  </div>
                  <p className="font-medium text-gray-900 mb-1">{insight.title}</p>
                  <p className="text-sm text-gray-500">{insight.description}</p>
                  {insight.impactEstimate > 0 && (
                    <p className="text-xs text-emerald-600 font-medium mt-2">
                      💰 {formatCurrency(insight.impactEstimate)} estimated impact
                    </p>
                  )}
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-purple-200 space-y-3">
                      {insight.recommendation && (
                        <div className="flex items-start gap-2 p-3 bg-white rounded-lg border border-purple-100">
                          <Rocket className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Recommendation</p>
                            <p className="text-sm text-gray-600">{insight.recommendation}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* AI Evidence Section */}
                      {insight.aiEvidence && (
                        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Brain className="w-4 h-4 text-purple-600" />
                            <p className="text-sm font-semibold text-purple-900">AI Evidence & Reasoning</p>
                          </div>
                          
                          <div className="space-y-3">
                            {/* Methodology */}
                            {insight.aiEvidence.methodology && (
                              <div className="bg-white/70 rounded-lg p-3">
                                <p className="text-xs font-medium text-purple-700 mb-1">How AI Found This</p>
                                <p className="text-sm text-gray-600">{insight.aiEvidence.methodology}</p>
                              </div>
                            )}
                            
                            {/* Verification Query */}
                            {insight.aiEvidence.verificationQuery && (
                              <div className="bg-white/70 rounded-lg p-3">
                                <p className="text-xs font-medium text-purple-700 mb-1">What Was Analyzed</p>
                                <p className="text-sm text-gray-600">{insight.aiEvidence.verificationQuery}</p>
                              </div>
                            )}
                            
                            {/* Supporting Metrics */}
                            {insight.aiEvidence.supportingMetrics?.length > 0 && (
                              <div className="bg-white/70 rounded-lg p-3">
                                <p className="text-xs font-medium text-purple-700 mb-2">Supporting Data</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {insight.aiEvidence.supportingMetrics.map((metric, idx) => (
                                    <div key={idx} className="flex items-center justify-between bg-white/50 rounded p-2">
                                      <span className="text-xs text-gray-600">{metric.label}</span>
                                      <span className={`text-xs font-medium ${
                                        metric.trend === 'up' ? 'text-emerald-600' :
                                        metric.trend === 'down' ? 'text-red-600' :
                                        'text-gray-700'
                                      }`}>
                                        {metric.trend === 'up' && '↑ '}
                                        {metric.trend === 'down' && '↓ '}
                                        {metric.value}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Historical Context */}
                            {insight.aiEvidence.historicalContext && (
                              <div className="bg-white/70 rounded-lg p-3">
                                <p className="text-xs font-medium text-purple-700 mb-1">Historical Context</p>
                                <p className="text-sm text-gray-600">{insight.aiEvidence.historicalContext}</p>
                              </div>
                            )}
                            
                            {/* Data Source & Confidence */}
                            {(insight.aiEvidence.dataSource || insight.aiEvidence.confidenceScore != null) && (
                              <div className="flex items-center justify-between text-xs text-purple-600">
                                {insight.aiEvidence.dataSource && (
                                  <span className="flex items-center gap-1">
                                    <Database className="w-3 h-3" />
                                    Source: {insight.aiEvidence.dataSource}
                                  </span>
                                )}
                                {insight.aiEvidence.confidenceScore != null && (
                                  <span>
                                    Confidence: {Math.round(insight.aiEvidence.confidenceScore * 100)}%
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white rounded-lg p-3 border border-purple-100">
                          <p className="text-xs font-semibold text-gray-700 mb-1">Novelty Score</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full">
                              <div 
                                className="h-full bg-purple-500 rounded-full"
                                style={{ width: `${insight.noveltyScore * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{Math.round(insight.noveltyScore * 100)}%</span>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-purple-100">
                          <p className="text-xs font-semibold text-gray-700 mb-1">Confidence</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full">
                              <div 
                                className="h-full bg-emerald-500 rounded-full"
                                style={{ width: `${insight.confidence * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{Math.round(insight.confidence * 100)}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {insight.cadence === 'daily' ? 'Discovered Today' : 'Weekly Pattern'}
                        </span>
                        <span>Type: {insight.insightType}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!insight.isAcknowledged && insight.contentHash && (
                    <button
                      onClick={(e) => handleAcknowledge(insight, e)}
                      disabled={acknowledgingId === insight.id}
                      className="p-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                      title="Mark as acknowledged"
                    >
                      {acknowledgingId === insight.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-purple-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// V3-4A: Cross-Channel Patterns Panel
// ============================================================================

// ============================================================================
// GA-R1: GOOGLE ADVISORY PANEL
// ============================================================================

interface GoogleAdvisoryRec {
  id: string;
  recType: string;
  recTypeLabel: string;
  summary: string;
  impactPct: number | null;
  budgetChange: string | null;
  keywordText: string | null;
  // Rich data from Google Ads API
  projectedMetrics?: {
    clicks: number | null;
    clicksDelta: number | null;
    conversions: number | null;
    conversionsDelta: number | null;
    impressions: number | null;
    impressionsDelta: number | null;
    conversionValue: number | null;
    convValueDelta: number | null;
    additionalCost: number | null;
  };
  currentMetrics?: {
    clicks: number | null;
    conversions: number | null;
    impressions: number | null;
    conversionValue: number | null;
  };
  budget?: {
    current: number | null;
    recommended: number | null;
    delta: number | null;
  } | null;
  targetRoas?: {
    recommended: string | null;
    currentBudget: number | null;
    recommendedBudget: number | null;
    budgetDelta: number | null;
  } | null;
  keyword?: {
    text: string;
    matchType: string | null;
    totalWeeklySearches: number;
    relatedSearches: Array<{ text: string; weeklySearches: number }>;
  } | null;
}

// Hook to fetch Google Ads recommendations (used for tab count)
function useGoogleAdsRecs() {
  const [recs, setRecs] = useState<GoogleAdvisoryRec[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUnmatchedRecs = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/api/dtc/google-ads/advisory?mode=unmatched');
        const data = await response.json();
        if (data.ok && data.recs) {
          setRecs(data.recs);
        }
      } catch (err: any) {
        console.error('[useGoogleAdsRecs] Error:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUnmatchedRecs();
  }, []);

  return { recs, isLoading, error, count: recs.length };
}

interface GoogleAdvisoryPanelProps {
  isFullTab?: boolean;
}

function GoogleAdvisoryPanel({ isFullTab = false }: GoogleAdvisoryPanelProps) {
  const { recs, isLoading, error } = useGoogleAdsRecs();
  const [expandedRecId, setExpandedRecId] = useState<string | null>(null);

  const handleOpenInGoogleAds = () => {
    window.open('https://ads.google.com/aw/recommendations', '_blank');
  };

  // When used as full tab, always show content (no collapse)
  if (isFullTab) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Target className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Google Ads Recommendations</h3>
              <p className="text-sm text-gray-500">
                {recs.length} recommendation{recs.length !== 1 ? 's' : ''} from Google not covered by PIQ actions
              </p>
            </div>
          </div>
          <button
            onClick={handleOpenInGoogleAds}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Open Google Ads
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
            <p className="text-gray-500">Loading Google Ads recommendations...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center bg-red-50 rounded-lg border border-red-200">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-600">{error}</p>
          </div>
        ) : recs.length === 0 ? (
          <div className="p-12 text-center bg-gray-50 rounded-lg border border-gray-200">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <h4 className="font-semibold text-gray-900 mb-1">All caught up!</h4>
            <p className="text-gray-500">No pending Google Ads recommendations at this time.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {recs.map((rec) => (
              <div key={rec.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 shrink-0">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                        {rec.recTypeLabel}
                      </span>
                      {rec.impactPct != null && rec.impactPct > 0 && (
                        <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          +{rec.impactPct.toFixed(1)}% projected
                        </span>
                      )}
                    </div>
                    <p className="text-gray-800 font-medium">{rec.summary}</p>
                    {rec.budgetChange && (
                      <p className="text-sm text-gray-500 mt-1">
                        💰 Budget change: {rec.budgetChange}
                      </p>
                    )}
                    
                    {/* Expanded details - Rich data from Google Ads */}
                    {expandedRecId === rec.id && (
                      <div className="mt-4 space-y-4">
                        {/* Projected Impact Section */}
                        {rec.projectedMetrics && (rec.projectedMetrics.conversionsDelta || rec.projectedMetrics.clicksDelta) && (
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                            <h5 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                              <TrendingUp className="w-4 h-4" />
                              Projected Weekly Impact
                            </h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {rec.projectedMetrics.conversionsDelta != null && (
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-green-600">
                                    +{rec.projectedMetrics.conversionsDelta.toFixed(1)}
                                  </div>
                                  <div className="text-xs text-gray-600">Conversions</div>
                                </div>
                              )}
                              {rec.projectedMetrics.convValueDelta != null && (
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-green-600">
                                    +${(rec.projectedMetrics.convValueDelta / 1000).toFixed(1)}K
                                  </div>
                                  <div className="text-xs text-gray-600">Conv. Value</div>
                                </div>
                              )}
                              {rec.projectedMetrics.clicksDelta != null && (
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-green-600">
                                    +{rec.projectedMetrics.clicksDelta.toLocaleString()}
                                  </div>
                                  <div className="text-xs text-gray-600">Clicks</div>
                                </div>
                              )}
                              {rec.projectedMetrics.additionalCost != null && (
                                <div className="text-center">
                                  <div className="text-xl font-bold text-amber-600">
                                    +${(rec.projectedMetrics.additionalCost / 1000).toFixed(1)}K
                                  </div>
                                  <div className="text-xs text-gray-600">Add'l Cost</div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Budget Recommendation Details */}
                        {rec.budget && (
                          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <h5 className="text-sm font-semibold text-blue-800 mb-3">💰 Budget Recommendation</h5>
                            <div className="flex items-center gap-4">
                              <div className="text-center">
                                <div className="text-lg font-medium text-gray-600">${rec.budget.current?.toLocaleString()}</div>
                                <div className="text-xs text-gray-500">Current Daily</div>
                              </div>
                              <ArrowRight className="w-5 h-5 text-blue-500" />
                              <div className="text-center">
                                <div className="text-lg font-bold text-blue-600">${rec.budget.recommended?.toLocaleString()}</div>
                                <div className="text-xs text-gray-500">Recommended</div>
                              </div>
                              {rec.budget.delta && (
                                <div className="ml-auto px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                                  +${rec.budget.delta.toLocaleString()}/day
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Target ROAS Details */}
                        {rec.targetRoas && (
                          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                            <h5 className="text-sm font-semibold text-purple-800 mb-3">📈 Target ROAS Recommendation</h5>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-gray-600">Recommended Target ROAS:</span>
                                <span className="text-xl font-bold text-purple-600">{rec.targetRoas.recommended}%</span>
                              </div>
                              {rec.targetRoas.budgetDelta && (
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Budget increase needed:</span>
                                  <span className="font-medium text-purple-600">
                                    ${rec.targetRoas.currentBudget?.toLocaleString()} → ${rec.targetRoas.recommendedBudget?.toLocaleString()}
                                  </span>
                                </div>
                              )}
                            </div>
                            <p className="mt-3 text-xs text-purple-700 bg-purple-100 rounded p-2">
                              💡 Google's simulations predict traffic will increase. Setting a target ROAS helps capture this value.
                            </p>
                          </div>
                        )}

                        {/* Keyword Details */}
                        {rec.keyword && (
                          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                            <h5 className="text-sm font-semibold text-amber-800 mb-3">🔍 Keyword Recommendation</h5>
                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <span className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg font-medium">
                                  "{rec.keyword.text}"
                                </span>
                                {rec.keyword.matchType && (
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                    {rec.keyword.matchType}
                                  </span>
                                )}
                              </div>
                              {rec.keyword.totalWeeklySearches > 0 && (
                                <div className="text-sm text-gray-600">
                                  <strong>{rec.keyword.totalWeeklySearches.toLocaleString()}</strong> estimated weekly searches
                                </div>
                              )}
                              {rec.keyword.relatedSearches.length > 0 && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-2">Related search terms:</div>
                                  <div className="flex flex-wrap gap-2">
                                    {rec.keyword.relatedSearches.map((term, i) => (
                                      <span key={i} className="px-2 py-1 bg-white border border-amber-200 rounded text-xs text-gray-700">
                                        {term.text} <span className="text-gray-400">({term.weeklySearches}/wk)</span>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Action Button */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                          <span className="text-xs text-gray-500">
                            Source: Google Ads API • Type: {rec.recType}
                          </span>
                          <button
                            onClick={handleOpenInGoogleAds}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Apply in Google Ads
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors shrink-0 ${
                      expandedRecId === rec.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'
                    }`}
                    onClick={() => setExpandedRecId(expandedRecId === rec.id ? null : rec.id)}
                  >
                    {expandedRecId === rec.id ? 'Close' : 'Review'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Original collapsible version (not used anymore, but kept for compatibility)
  return null;
}

// ============================================================================
// CROSS-CHANNEL PATTERNS
// ============================================================================

interface CrossChannelPattern {
  id: string;
  patternKey: string;
  name: string;
  description: string;
  isProven: boolean;
  improvedRate: number;
  nInstances: number;
  nEvaluated: number;
  typicalActionTypes: string[];
  avgConfidenceAdjustment: number;
}

function CrossChannelPatternsPanel() {
  const [isLoading, setIsLoading] = useState(false);
  const [patterns, setPatterns] = useState<CrossChannelPattern[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchPatterns = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/brain/patterns/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('patterniq_auth_session') ? JSON.parse(localStorage.getItem('patterniq_auth_session')!).token : ''}`,
        },
      });
      if (!resp.ok) {
        if (resp.status === 404) {
          // Table doesn't exist yet - not an error
          setPatterns([]);
          return;
        }
        throw new Error(`HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setPatterns(data.patterns || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load patterns');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPatterns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only show if we have patterns
  if (!isLoading && patterns.length === 0 && !error) {
    return null;
  }

  const provenPatterns = patterns.filter(p => p.isProven);
  const activePatterns = patterns.filter(p => p.nInstances > 0);

  if (activePatterns.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-gray-900">Cross-Channel Patterns Detected</h3>
            <p className="text-xs text-gray-500">
              {activePatterns.length} active pattern{activePatterns.length !== 1 ? 's' : ''}
              {provenPatterns.length > 0 && (
                <span className="ml-1 text-amber-600">• {provenPatterns.length} proven</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 p-5 space-y-3">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}
          
          {activePatterns.map((pattern) => (
            <div
              key={pattern.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-900">{pattern.name}</h4>
                    {pattern.isProven && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 flex items-center gap-1">
                        ⭐ Proven Play
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{pattern.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>{pattern.nInstances} instance{pattern.nInstances !== 1 ? 's' : ''}</span>
                    {pattern.nEvaluated > 0 && (
                      <span className={pattern.improvedRate >= 0.6 ? 'text-emerald-600' : ''}>
                        {Math.round(pattern.improvedRate * 100)}% success rate
                      </span>
                    )}
                    {pattern.typicalActionTypes?.length > 0 && (
                      <span>Actions: {pattern.typicalActionTypes.slice(0, 2).join(', ')}</span>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Confidence</div>
                    <div className="text-lg font-bold text-indigo-600">
                      {Math.round(((pattern.nEvaluated > 0 ? pattern.improvedRate : 0.5) + 0.3) * 100)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// V3-6: TODAY'S CASES PANEL
// ============================================================================

// V3-4B: Best Play action template
interface BestPlayAction {
  actionType: string;
  titleTemplate: string;
  ownerRole: string;
  timeHorizon: string;
  rationaleTemplate: string;
  successRate: number;
  nEvaluated: number;
}

// V3-4B: Best Play data from pattern performance
interface BestPlayData {
  isProven: boolean;
  improvedRate: number;
  bestPlayActions: BestPlayAction[] | null;
  bestPlaySuccessRate: number | null;
  avgDownsideRange: { min: number; max: number } | null;
  avgUpsideRange: { min: number; max: number } | null;
}

// ============================================================================
// GA-R2A: CASE FEEDBACK CONTROLS
// ============================================================================

type FeedbackType = 'helpful' | 'noise' | 'needs_work';

interface CaseFeedbackControlsProps {
  caseId: string;
  caseTitle: string;
}

function CaseFeedbackControls({ caseId }: CaseFeedbackControlsProps) {
  const [feedbackState, setFeedbackState] = useState<{
    submitted: boolean;
    selectedFeedback: FeedbackType | null;
    showComment: boolean;
    comment: string;
    isSubmitting: boolean;
    counts: { helpful: number; noise: number; needs_work: number; total: number } | null;
    error: string | null;
  }>({
    submitted: false,
    selectedFeedback: null,
    showComment: false,
    comment: '',
    isSubmitting: false,
    counts: null,
    error: null,
  });

  // Fetch existing feedback summary on mount
  useEffect(() => {
    fetch(`/api/brain/cases/feedback?caseId=${encodeURIComponent(caseId)}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('patterniq_auth_session') ? JSON.parse(localStorage.getItem('patterniq_auth_session')!).token : ''}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        if (data.ok) {
          setFeedbackState(prev => ({
            ...prev,
            counts: data.counts,
            selectedFeedback: data.userFeedback?.feedback || null,
            submitted: !!data.userFeedback,
          }));
        }
      })
      .catch(err => console.error('[CaseFeedback] Error fetching:', err));
  }, [caseId]);

  const submitFeedback = async () => {
    if (!feedbackState.selectedFeedback) return;

    setFeedbackState(prev => ({ ...prev, isSubmitting: true, error: null }));

    try {
      const res = await fetch('/api/brain/cases/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('patterniq_auth_session') ? JSON.parse(localStorage.getItem('patterniq_auth_session')!).token : ''}`,
        },
        body: JSON.stringify({
          caseId,
          feedback: feedbackState.selectedFeedback,
          comment: feedbackState.comment || undefined,
        }),
      });

      const data = await res.json();
      
      if (data.ok) {
        setFeedbackState(prev => ({
          ...prev,
          submitted: true,
          isSubmitting: false,
          showComment: false,
          // Update counts locally
          counts: prev.counts 
            ? {
                ...prev.counts,
                [prev.selectedFeedback!]: (prev.counts[prev.selectedFeedback!] || 0) + 1,
                total: prev.counts.total + 1,
              }
            : { helpful: 0, noise: 0, needs_work: 0, total: 1, [prev.selectedFeedback!]: 1 },
        }));
      } else {
        setFeedbackState(prev => ({ ...prev, isSubmitting: false, error: data.error || 'Failed to submit' }));
      }
    } catch (err: any) {
      setFeedbackState(prev => ({ ...prev, isSubmitting: false, error: err.message }));
    }
  };

  const handleFeedbackSelect = (type: FeedbackType) => {
    setFeedbackState(prev => ({
      ...prev,
      selectedFeedback: type,
      showComment: true,
    }));
  };

  return (
    <div className="mt-4 pt-3 border-t border-gray-200">
      <div className="text-xs font-semibold text-gray-600 mb-2">Was this case helpful?</div>
      
      {feedbackState.submitted ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-green-600 font-medium">✓ Thanks — recorded</span>
          {feedbackState.counts && feedbackState.counts.total > 0 && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span title="Helpful">👍 {feedbackState.counts.helpful}</span>
              <span title="Noise">👎 {feedbackState.counts.noise}</span>
              <span title="Needs work">🛠 {feedbackState.counts.needs_work}</span>
            </div>
          )}
          {feedbackState.selectedFeedback && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              feedbackState.selectedFeedback === 'helpful' ? 'bg-green-100 text-green-700' :
              feedbackState.selectedFeedback === 'noise' ? 'bg-red-100 text-red-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              Your vote: {feedbackState.selectedFeedback === 'needs_work' ? 'Needs work' : feedbackState.selectedFeedback}
            </span>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleFeedbackSelect('helpful')}
              disabled={feedbackState.isSubmitting}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                feedbackState.selectedFeedback === 'helpful'
                  ? 'bg-green-100 text-green-700 border-2 border-green-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-700'
              }`}
            >
              👍 Helpful
            </button>
            <button
              onClick={() => handleFeedbackSelect('noise')}
              disabled={feedbackState.isSubmitting}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                feedbackState.selectedFeedback === 'noise'
                  ? 'bg-red-100 text-red-700 border-2 border-red-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700'
              }`}
            >
              👎 Noise
            </button>
            <button
              onClick={() => handleFeedbackSelect('needs_work')}
              disabled={feedbackState.isSubmitting}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                feedbackState.selectedFeedback === 'needs_work'
                  ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-yellow-50 hover:text-yellow-700'
              }`}
            >
              🛠 Needs work
            </button>
          </div>

          {feedbackState.showComment && (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Optional comment..."
                value={feedbackState.comment}
                onChange={(e) => setFeedbackState(prev => ({ ...prev, comment: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={submitFeedback}
                  disabled={feedbackState.isSubmitting || !feedbackState.selectedFeedback}
                  className="px-4 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1"
                >
                  {feedbackState.isSubmitting ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                  Submit
                </button>
                <button
                  onClick={() => setFeedbackState(prev => ({ ...prev, showComment: false, selectedFeedback: null }))}
                  className="px-3 py-1.5 text-gray-600 text-xs hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
              {feedbackState.error && (
                <p className="text-xs text-red-600">{feedbackState.error}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// BRAIN CASES PANEL
// ============================================================================

interface BrainCase {
  id: string;
  companyId: string;
  calendarWeekKey: string;
  title: string;
  summary: string;
  priorityScore: number;
  priorityRank: number;
  downsideMin: number | null;
  downsideMax: number | null;
  downsideMidpoint: number | null;
  confidence: number;
  timeToImpactDays: number | null;
  isProvenPattern: boolean;
  patternId: string | null;
  patternType: string | null;
  patternConfidence: number | null;
  retailerId: string | null;
  retailerName: string | null;
  constraintType: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'dismissed';
  brainRunId: string | null;
  createdAt: string;
  updatedAt: string;
  findings?: Array<{
    findingId: string;
    findingType: string;
    severity: string;
    title: string;
    summary: string;
  }>;
  // V3-4B: Best Play
  bestPlay?: BestPlayData | null;
}

function TodaysCasesPanel() {
  const [isLoading, setIsLoading] = useState(false);
  const [cases, setCases] = useState<BrainCase[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedCaseId, setExpandedCaseId] = useState<string | null>(null);
  const [loadingActions, setLoadingActions] = useState<string | null>(null);
  // Phase 4: Google Ads filter state
  const [showGoogleAdsOnly, setShowGoogleAdsOnly] = useState(false);

  const fetchCases = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/brain/cases?status=open,in_progress&limit=7&includeFindings=true', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('patterniq_auth_session') ? JSON.parse(localStorage.getItem('patterniq_auth_session')!).token : ''}`,
        },
      });
      if (!resp.ok) {
        if (resp.status === 404) {
          setCases([]);
          return;
        }
        throw new Error(`HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setCases(data.cases || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load cases');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  const [loadingBestPlay, setLoadingBestPlay] = useState<string | null>(null);

  const createActionsFromCase = async (caseId: string) => {
    setLoadingActions(caseId);
    try {
      const resp = await fetch('/api/brain/cases/create-actions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('patterniq_auth_session') ? JSON.parse(localStorage.getItem('patterniq_auth_session')!).token : ''}`,
        },
        body: JSON.stringify({ caseId }),
      });
      
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      
      // Refresh cases
      await fetchCases();
    } catch (e: any) {
      setError(e?.message || 'Failed to create actions');
    } finally {
      setLoadingActions(null);
    }
  };

  // V3-4B: Create Best Play Actions
  const createBestPlayActions = async (caseId: string) => {
    setLoadingBestPlay(caseId);
    try {
      const resp = await fetch('/api/brain/cases/create-best-play-actions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('patterniq_auth_session') ? JSON.parse(localStorage.getItem('patterniq_auth_session')!).token : ''}`,
        },
        body: JSON.stringify({ caseId }),
      });
      
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      
      // Refresh cases
      await fetchCases();
    } catch (e: any) {
      setError(e?.message || 'Failed to create best play actions');
    } finally {
      setLoadingBestPlay(null);
    }
  };

  if (!isLoading && cases.length === 0 && !error) {
    return null;
  }

  const getPriorityColor = (score: number) => {
    if (score >= 75) return 'red';
    if (score >= 50) return 'orange';
    if (score >= 25) return 'yellow';
    return 'gray';
  };

  const getConstraintIcon = (type: string | null) => {
    switch (type) {
      case 'inventory': return Package;
      case 'demand': return TrendingDown;
      case 'execution': return Store;
      case 'marketing': return Target;
      case 'promo': return Flame;
      case 'forecast': return Brain;
      default: return AlertTriangle;
    }
  };
  
  // Phase 4: Check if a case contains Google Ads recommendations
  const isGoogleAdsCase = (c: BrainCase) => {
    return c.findings?.some(f => f.findingType === 'google_ads_recommendation') || 
           c.title?.includes('Google Ads');
  };

  return (
    <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl border border-violet-200 shadow-sm overflow-hidden mb-6">
      <div className="px-5 py-4 border-b border-violet-200 bg-white/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg shadow-sm">
              <ListTodo className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                Today's Cases
                <span className="text-xs font-normal text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">
                  V3-6
                </span>
              </h3>
              <p className="text-xs text-gray-500">
                {cases.length} prioritized decision{cases.length !== 1 ? 's' : ''} requiring attention
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Phase 4: Google Ads Filter Toggle */}
            {cases.some(c => c.findings?.some(f => f.findingType === 'google_ads_recommendation') || c.title?.includes('Google Ads')) && (
              <button
                onClick={() => setShowGoogleAdsOnly(!showGoogleAdsOnly)}
                className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                  showGoogleAdsOnly 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
              >
                <Target className="w-3 h-3" />
                Google Ads
              </button>
            )}
            <button
              onClick={fetchCases}
              disabled={isLoading}
              className="p-2 hover:bg-white/80 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {isLoading && cases.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Phase 4: Filter cases based on Google Ads toggle */}
        {cases
          .filter(c => !showGoogleAdsOnly || isGoogleAdsCase(c))
          .map((c, index) => {
          const ConstraintIcon = getConstraintIcon(c.constraintType);
          const priorityColor = getPriorityColor(c.priorityScore);
          const isExpanded = expandedCaseId === c.id;
          const isGoogleAds = isGoogleAdsCase(c);

          return (
            <div
              key={c.id}
              className={`bg-white rounded-xl border ${
                isGoogleAds ? 'border-blue-300 bg-blue-50/30' :
                c.isProvenPattern ? 'border-amber-300' : 'border-gray-200'
              } overflow-hidden transition-all hover:shadow-md`}
            >
              {/* Case Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedCaseId(isExpanded ? null : c.id)}
              >
                <div className="flex items-start gap-3">
                  {/* Rank Badge */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white ${
                    index === 0 ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                    index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-500' :
                    index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
                    'bg-gray-300'
                  }`}>
                    {c.priorityRank}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900 line-clamp-1">{c.title}</h4>
                      {/* Phase 4: Google Ads Badge */}
                      {isGoogleAds && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          Google Ads
                        </span>
                      )}
                      {c.isProvenPattern && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          Proven
                        </span>
                      )}
                      {c.status === 'in_progress' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          In Progress
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{c.summary}</p>

                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {c.retailerName && (
                        <span className="flex items-center gap-1">
                          <Store className="w-3 h-3" />
                          {c.retailerName}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <ConstraintIcon className="w-3 h-3" />
                        {c.constraintType || 'mixed'}
                      </span>
                      {c.timeToImpactDays && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {c.timeToImpactDays}d
                        </span>
                      )}
                      {c.findings && c.findings.length > 0 && (
                        <span>{c.findings.length} finding{c.findings.length !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                  </div>

                  {/* Priority & Impact */}
                  <div className="flex-shrink-0 text-right">
                    {c.downsideMidpoint && c.downsideMidpoint > 0 && (
                      <div className="text-lg font-bold text-red-600">
                        ${Math.round(c.downsideMidpoint).toLocaleString()}
                      </div>
                    )}
                    <div className="flex items-center gap-1 justify-end text-xs text-gray-500">
                      <span className={`w-2 h-2 rounded-full ${
                        priorityColor === 'red' ? 'bg-red-500' :
                        priorityColor === 'orange' ? 'bg-orange-500' :
                        priorityColor === 'yellow' ? 'bg-yellow-500' :
                        'bg-gray-400'
                      }`} />
                      {Math.round(c.priorityScore)} priority
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {Math.round(c.confidence * 100)}% conf
                    </div>
                  </div>

                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50/50">
                  {/* V3-4B: Best Play Section */}
                  {c.bestPlay && c.bestPlay.bestPlayActions && c.bestPlay.bestPlayActions.length > 0 && (
                    <div className="mt-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-semibold text-amber-800">
                            Best Play ({Math.round((c.bestPlay.bestPlaySuccessRate || 0) * 100)}% success)
                          </span>
                          {c.bestPlay.isProven && (
                            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-200 text-amber-900">
                              Proven Pattern
                            </span>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            createBestPlayActions(c.id);
                          }}
                          disabled={loadingBestPlay === c.id || c.status === 'in_progress'}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                            c.status === 'in_progress'
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-amber-600 text-white hover:bg-amber-700'
                          }`}
                        >
                          {loadingBestPlay === c.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Rocket className="w-3 h-3" />
                          )}
                          Create Best Play Actions
                        </button>
                      </div>
                      <ul className="text-sm text-amber-900 space-y-1 ml-6">
                        {c.bestPlay.bestPlayActions.map((action, i) => (
                          <li key={i} className="list-disc">
                            <span className="font-medium">{action.titleTemplate}</span>
                            <span className="text-amber-700 text-xs ml-1">({action.ownerRole})</span>
                          </li>
                        ))}
                      </ul>
                      {(c.bestPlay.avgDownsideRange || c.bestPlay.avgUpsideRange) && (
                        <div className="mt-2 text-xs text-amber-700">
                          <span className="font-medium">Typical Impact: </span>
                          {c.bestPlay.avgDownsideRange && (
                            <span className="text-red-600">
                              Risk ${Math.round(c.bestPlay.avgDownsideRange.min).toLocaleString()}-${Math.round(c.bestPlay.avgDownsideRange.max).toLocaleString()}
                            </span>
                          )}
                          {c.bestPlay.avgDownsideRange && c.bestPlay.avgUpsideRange && ' • '}
                          {c.bestPlay.avgUpsideRange && (
                            <span className="text-green-600">
                              Upside ${Math.round(c.bestPlay.avgUpsideRange.min).toLocaleString()}-${Math.round(c.bestPlay.avgUpsideRange.max).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Phase 4: Google Ads Recommendation Details */}
                  {isGoogleAds && (
                    <div className="mt-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-800">Google Ads Recommendations</span>
                      </div>
                      <div className="space-y-2">
                        {c.findings?.filter(f => f.findingType === 'google_ads_recommendation').map((f, i) => (
                          <div key={i} className="bg-white/70 rounded-lg p-2 border border-blue-100">
                            <div className="flex items-center gap-2">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                f.severity === 'high' ? 'bg-blue-100 text-blue-800' :
                                f.severity === 'medium' ? 'bg-blue-50 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {f.severity}
                              </span>
                              <span className="font-medium text-sm text-gray-900">{f.title}</span>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{f.summary}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-blue-700">
                        <Lightbulb className="w-3 h-3 inline mr-1" />
                        Apply these recommendations in Google Ads to improve campaign performance
                      </div>
                    </div>
                  )}

                  {/* Emerging Pattern (non-proven but has pattern) */}
                  {c.patternId && (!c.bestPlay || !c.bestPlay.bestPlayActions) && !isGoogleAds && (
                    <div className="mt-3 bg-gray-50 rounded-lg border border-gray-200 p-3">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Emerging Pattern</span>
                        <span className="text-xs text-gray-500">(building history)</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        This case matches a detected pattern but doesn't have enough outcome data for Best Play recommendations yet.
                      </p>
                    </div>
                  )}

                  {/* Findings */}
                  {c.findings && c.findings.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Bundled Findings</div>
                      <div className="space-y-2">
                        {c.findings.map((f) => (
                          <div key={f.findingId} className="bg-white rounded-lg border border-gray-200 p-3">
                            <div className="flex items-center gap-2">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                f.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                f.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                                f.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {f.severity}
                              </span>
                              <span className="font-medium text-sm text-gray-900">{f.title}</span>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{f.summary}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        createActionsFromCase(c.id);
                      }}
                      disabled={loadingActions === c.id || c.status === 'in_progress'}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                        c.status === 'in_progress'
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          : 'bg-violet-600 text-white hover:bg-violet-700'
                      }`}
                    >
                      {loadingActions === c.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Rocket className="w-4 h-4" />
                      )}
                      {c.status === 'in_progress' ? 'Actions Created' : 'Create Actions'}
                    </button>
                    
                    <Link href={`/brain/case/${c.id}`}>
                      <a className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2">
                        <ExternalLink className="w-4 h-4" />
                        View Details
                      </a>
                    </Link>
                  </div>

                  {/* GA-R2A: Case Feedback */}
                  <CaseFeedbackControls caseId={c.id} caseTitle={c.title} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// PHASE 2: SNOOZED ITEMS SECTION
// ============================================================================

interface SnoozedItemsProps {
  snoozedActions: CommandCenterAction[];
  onUnsnooze: (actionId: string) => Promise<void>;
}

// ============================================================================
// PHASE 3: USAGE ANALYTICS PANEL
// ============================================================================

interface AnalyticsSummary {
  windowStart: string;
  windowEnd: string;
  countsByStatus: Record<string, number>;
  countsByInteractionType: Record<string, number>;
  explainedCount: number;
  acknowledgedCount: number;
  totalInteractions: number;
  // Phase 6A: Recommendation outcomes
  outcomes?: {
    totalTracked: number;
    improved: number;
    worsened: number;
    noChange: number;
    pending: number;
  };
}

function UsageAnalyticsPanel() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isExpanded && !analytics) {
      setIsLoading(true);
      fetch('/api/command-center/analytics/summary')
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.success) {
            setAnalytics(data);
          }
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  }, [isExpanded]);

  const statusColors: Record<string, string> = {
    new: 'bg-blue-500',
    acknowledged: 'bg-gray-500',
    in_progress: 'bg-yellow-500',
    snoozed: 'bg-purple-500',
    resolved: 'bg-green-500',
    rejected: 'bg-red-500'
  };

  const statusLabels: Record<string, string> = {
    new: 'New',
    acknowledged: 'Acknowledged',
    in_progress: 'In Progress',
    snoozed: 'Snoozed',
    resolved: 'Resolved',
    rejected: 'Rejected'
  };

  const totalStatusCount = analytics 
    ? Object.values(analytics.countsByStatus).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-3 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <BarChart3 className="w-5 h-5 text-gray-600" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-gray-900">Command Center Usage</h3>
            <p className="text-xs text-gray-500">Last 30 days activity</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Status Distribution */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Actions by Status</h4>
                {totalStatusCount > 0 ? (
                  <>
                    <div className="flex h-4 rounded-full overflow-hidden mb-2">
                      {Object.entries(analytics.countsByStatus).map(([status, count]) => (
                        <div 
                          key={status}
                          className={`${statusColors[status] || 'bg-gray-400'} transition-all`}
                          style={{ width: `${(count / totalStatusCount) * 100}%` }}
                          title={`${statusLabels[status] || status}: ${count}`}
                        />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs">
                      {Object.entries(analytics.countsByStatus).map(([status, count]) => (
                        <div key={status} className="flex items-center gap-1.5">
                          <span className={`w-2.5 h-2.5 rounded-full ${statusColors[status] || 'bg-gray-400'}`} />
                          <span className="text-gray-600">{statusLabels[status] || status}: {count}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No actions tracked yet</p>
                )}
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{analytics.totalInteractions}</p>
                  <p className="text-xs text-gray-500">Total Interactions</p>
                </div>
                <div className="bg-violet-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-violet-700">{analytics.explainedCount}</p>
                  <p className="text-xs text-gray-500">Explanations Viewed</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{analytics.acknowledgedCount}</p>
                  <p className="text-xs text-gray-500">Acknowledged</p>
                </div>
              </div>

              {/* Phase 6A: Recommendation Outcomes */}
              {analytics.outcomes && analytics.outcomes.totalTracked > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Recommendation Outcomes (30 days)</h4>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-green-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-green-700">{analytics.outcomes.improved}</p>
                      <p className="text-xs text-gray-500">Improved</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-red-700">{analytics.outcomes.worsened}</p>
                      <p className="text-xs text-gray-500">Declined</p>
                    </div>
                    <div className="bg-gray-100 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-gray-700">{analytics.outcomes.noChange}</p>
                      <p className="text-xs text-gray-500">No Change</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold text-blue-700">{analytics.outcomes.pending}</p>
                      <p className="text-xs text-gray-500">Pending</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Time Window */}
              <p className="text-xs text-gray-400 text-center">
                Data from {new Date(analytics.windowStart).toLocaleDateString()} to {new Date(analytics.windowEnd).toLocaleDateString()}
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Unable to load analytics</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMMAND CENTER ADMIN SECTION (Collapsible)
// ============================================================================

interface CommandCenterAdminSectionProps {
  scorecardData: any;
  scorecardLoading: boolean;
  scorecardWindowDays: 7 | 30;
  setScorecardWindowDays: (days: 7 | 30) => void;
}

function CommandCenterAdminSection({ 
  scorecardData, 
  scorecardLoading, 
  scorecardWindowDays, 
  setScorecardWindowDays 
}: CommandCenterAdminSectionProps) {
  const [isAdminExpanded, setIsAdminExpanded] = useState(false);
  const [isScorecardExpanded, setIsScorecardExpanded] = useState(false);
  const [isArtifactsExpanded, setIsArtifactsExpanded] = useState(false);
  const [isUsageExpanded, setIsUsageExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Admin Section Header */}
      <button
        onClick={() => setIsAdminExpanded(!isAdminExpanded)}
        className="w-full px-5 py-4 bg-gradient-to-r from-slate-100 to-gray-100 border-b border-gray-200 flex items-center justify-between hover:from-slate-150 hover:to-gray-150 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <Settings className="w-5 h-5 text-slate-600" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-gray-900">Command Center Admin</h3>
            <p className="text-xs text-gray-500">Brain metrics, workflow artifacts & usage analytics</p>
          </div>
        </div>
        {isAdminExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Admin Section Content */}
      {isAdminExpanded && (
        <div className="p-4 space-y-4 bg-gray-50/50">
          {/* Brain Scorecard Sub-section */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setIsScorecardExpanded(!isScorecardExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Gauge className="w-5 h-5 text-violet-600" />
                <span className="font-semibold text-gray-900">Brain Scorecard</span>
                {scorecardLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
              </div>
              <div className="flex items-center gap-3">
                {/* Window toggle */}
                <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-0.5" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setScorecardWindowDays(7)}
                    className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                      scorecardWindowDays === 7 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    7 days
                  </button>
                  <button
                    onClick={() => setScorecardWindowDays(30)}
                    className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                      scorecardWindowDays === 30 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    30 days
                  </button>
                </div>
                {isScorecardExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </button>
            
            {/* Scorecard Content */}
            {isScorecardExpanded && scorecardData && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-t border-gray-200">
                {/* Adoption */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Users className="w-4 h-4 text-blue-500" />
                    Adoption
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Case Open Rate</span>
                      <span className="font-medium">{Math.round(scorecardData.adoption.caseOpenRate * 100)}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Actions/Case</span>
                      <span className="font-medium">{scorecardData.adoption.actionsPerCase}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Best Play Actions</span>
                      <span className="font-medium">{scorecardData.adoption.bestPlayActionsCreated}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Unread Inbox</span>
                      <span className="font-medium">{scorecardData.adoption.inboxUnread}</span>
                    </div>
                  </div>
                </div>
                
                {/* Effectiveness */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                    Effectiveness
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Improvement Rate</span>
                      <span className="font-medium text-emerald-600">{Math.round(scorecardData.effectiveness.improvementRate * 100)}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Outcomes</span>
                      <span className="font-medium">{scorecardData.effectiveness.outcomesProcessed}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Proven Patterns</span>
                      <span className="font-medium">{scorecardData.effectiveness.provenPatterns}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Improved</span>
                      <span className="font-medium text-emerald-600">{scorecardData.effectiveness.improved}</span>
                    </div>
                  </div>
                </div>
                
                {/* Signal Quality */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Signal className="w-4 h-4 text-amber-500" />
                    Signal Quality
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Degraded Rate</span>
                      <span className={`font-medium ${scorecardData.signalQuality.degradedRate > 0.2 ? 'text-red-600' : ''}`}>
                        {Math.round(scorecardData.signalQuality.degradedRate * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Conflict Rate</span>
                      <span className={`font-medium ${scorecardData.signalQuality.conflictRate > 0.3 ? 'text-amber-600' : ''}`}>
                        {Math.round(scorecardData.signalQuality.conflictRate * 100)}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Findings/Case</span>
                      <span className="font-medium">{scorecardData.signalQuality.avgFindingsPerCase}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Brain Runs</span>
                      <span className="font-medium">{scorecardData.signalQuality.brainRuns}</span>
                    </div>
                  </div>
                </div>
                
                {/* Timeliness */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Timer className="w-4 h-4 text-purple-500" />
                    Timeliness
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Avg Queue</span>
                      <span className="font-medium">{scorecardData.timeliness.avgMinutesInQueue}m</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">P95 Queue</span>
                      <span className="font-medium">{scorecardData.timeliness.p95MinutesInQueue}m</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Cases Created</span>
                      <span className="font-medium">{scorecardData.signalQuality.casesCreated}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Findings</span>
                      <span className="font-medium">{scorecardData.signalQuality.findingsCreated}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Loading state */}
            {isScorecardExpanded && scorecardLoading && !scorecardData && (
              <div className="flex items-center justify-center py-8 text-gray-400 border-t border-gray-200">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Loading scorecard...
              </div>
            )}
          </div>

          {/* Workflow Artifacts Sub-section */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setIsArtifactsExpanded(!isArtifactsExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-slate-700" />
                <span className="font-semibold text-gray-900">Workflow Artifacts</span>
              </div>
              {isArtifactsExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {isArtifactsExpanded && (
              <div className="border-t border-gray-200">
                <WorkflowArtifactsPanelContent />
              </div>
            )}
          </div>

          {/* Command Center Usage Sub-section */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setIsUsageExpanded(!isUsageExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-gray-600" />
                <span className="font-semibold text-gray-900">Command Center Usage</span>
              </div>
              {isUsageExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {isUsageExpanded && (
              <div className="border-t border-gray-200">
                <UsageAnalyticsPanelContent />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Extracted content components for the admin section
function WorkflowArtifactsPanelContent() {
  const { companyId } = useTenant();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery<{ artifacts: WorkflowArtifactListItem[] }>({
    queryKey: ['workflow-artifacts', companyId],
    queryFn: async () => {
      const resp = await fetch('/api/command-center/workflow-artifacts/list?limit=10');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return resp.json();
    },
    enabled: !!companyId,
    staleTime: 30 * 1000
  });

  const artifacts = data?.artifacts || [];

  const openArtifact = (id: string) => {
    setSelectedId(id);
    setOpen(true);
  };

  return (
    <>
      <div className="p-4 flex justify-end">
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
        >
          Refresh
        </button>
      </div>
      <div className="px-4 pb-4">
        {isLoading ? (
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
            Loading artifacts…
          </div>
        ) : artifacts.length === 0 ? (
          <div className="text-sm text-gray-600">No workflow artifacts yet. Run a playbook and click "Save to Command Center".</div>
        ) : (
          <div className="space-y-2">
            {artifacts.slice(0, 10).map((a) => (
              <div key={a.id} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">{a.workflowId}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(a.createdAt).toLocaleString()}
                    <span className="px-2 py-0.5 bg-gray-100 rounded-full">{a.qualityStatus || '—'}</span>
                    {typeof a.qualityScore === 'number' ? <span>score {a.qualityScore}</span> : null}
                    {typeof a.coveragePctKnown === 'number' ? <span>coverage {Math.round(a.coveragePctKnown * 100)}%</span> : null}
                  </div>
                </div>
                <button
                  onClick={() => openArtifact(a.id)}
                  className="px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 text-sm"
                >
                  Open
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <WorkflowArtifactDrawer
        artifactId={selectedId}
        isOpen={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function UsageAnalyticsPanelContent() {
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetch('/api/command-center/analytics/summary')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.success) {
          setAnalytics(data);
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  const statusColors: Record<string, string> = {
    new: 'bg-blue-500',
    acknowledged: 'bg-gray-500',
    in_progress: 'bg-yellow-500',
    snoozed: 'bg-purple-500',
    resolved: 'bg-green-500',
    rejected: 'bg-red-500'
  };

  const statusLabels: Record<string, string> = {
    new: 'New',
    acknowledged: 'Acknowledged',
    in_progress: 'In Progress',
    snoozed: 'Snoozed',
    resolved: 'Resolved',
    rejected: 'Rejected'
  };

  const totalStatusCount = analytics 
    ? Object.values(analytics.countsByStatus).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="p-5">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : analytics ? (
        <div className="space-y-6">
          {/* Status Distribution */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Actions by Status</h4>
            {totalStatusCount > 0 ? (
              <>
                <div className="flex h-4 rounded-full overflow-hidden mb-2">
                  {Object.entries(analytics.countsByStatus).map(([status, count]) => (
                    <div 
                      key={status}
                      className={`${statusColors[status] || 'bg-gray-400'}`}
                      style={{ width: `${(count / totalStatusCount) * 100}%` }}
                      title={`${statusLabels[status] || status}: ${count}`}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 text-xs">
                  {Object.entries(analytics.countsByStatus).map(([status, count]) => (
                    <div key={status} className="flex items-center gap-1.5">
                      <div className={`w-2.5 h-2.5 rounded-full ${statusColors[status] || 'bg-gray-400'}`} />
                      <span className="text-gray-600">{statusLabels[status] || status}: {count}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500">No action statuses recorded yet</p>
            )}
          </div>

          {/* Outcomes Summary */}
          {analytics.outcomes && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Outcomes Tracked</h4>
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-green-700">{analytics.outcomes.improved}</p>
                  <p className="text-xs text-gray-500">Improved</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-red-700">{analytics.outcomes.worsened}</p>
                  <p className="text-xs text-gray-500">Worsened</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-gray-700">{analytics.outcomes.noChange}</p>
                  <p className="text-xs text-gray-500">No Change</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-blue-700">{analytics.outcomes.pending}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </div>
          )}

          {/* Time Window */}
          <p className="text-xs text-gray-400 text-center">
            Data from {new Date(analytics.windowStart).toLocaleDateString()} to {new Date(analytics.windowEnd).toLocaleDateString()}
          </p>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">Unable to load analytics</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PHASE 5: PLAYBOOK PANEL
// ============================================================================

interface WorkflowDefinition {
  id: string;
  workflowId: string;
  name: string;
  description?: string;
  category?: string;
  steps: Array<{ agentId: string; mode: string; optional?: boolean }>;
  enabled: boolean;
  isCustom?: boolean;
}

interface WorkflowRunResult {
  success: boolean;
  workflow: {
    id: string;
    name: string;
    description?: string;
    category?: string;
    stepsCount: number;
  };
  steps: Array<{
    agentId: string;
    result: string;
    success: boolean;
    latencyMs: number;
  }>;
  finalAnswer: string;
  totalLatencyMs: number;
  // Phase 8A: Supervised workflow output (optional)
  runId?: string;
  final?: {
    executiveSummary: string;
    decisionPlan: {
      topActions: Array<{
        title: string;
        rationale: string;
        linkedAssertions: string[];
        recommendedOwnerRole?: 'Founder' | 'Retail' | 'Marketing' | 'Ops';
        timeHorizon: 'today' | 'this_week' | 'this_month';
      }>;
    };
    evidence: {
      assertions: Array<{
        id: string;
        label: string;
        metricKey: string;
        value: number | string | null;
        unit?: string;
        dataStatus: 'known' | 'unavailable';
        lineageRefIds: string[];
        notes?: string[];
      }>;
      lineage: Array<{
        toolName: string;
        toolArgsHash: string;
        tables: string[];
        timeWindow?: string;
        tenantScoped: boolean;
        createdAt: string;
      }>;
      warnings?: string[];
    };
    conflicts?: Array<{
      metricKey: string;
      unit?: string;
      values: Array<number | string>;
      assertionIds: string[];
    }>;
    needsReview: boolean;
    quality?: {
      strictness: 'strict' | 'soft';
      status: 'PASS' | 'DEGRADED' | 'FAIL';
      score: number;
      requiredTotal: number;
      presentKnown: number;
      presentUnavailable: number;
      knownWithoutLineageCount: number;
      coveragePctKnown: number;
      missing: string[];
      conflicts: Array<{ metricKey: string; values: Array<number | string | null> }>;
      tenantScopeViolations: string[];
      warnings: string[];
    };
    followups?: { attempted: boolean; createdCount?: number };
  };
}

/**
 * Fetch available workflows (playbooks)
 */
async function fetchWorkflows(): Promise<WorkflowDefinition[]> {
  try {
    const response = await fetch('/api/workflows/list');
    if (!response.ok) return [];
    const data = await response.json();
    return data.workflows || [];
  } catch (error) {
    console.error('[PlaybookPanel] Error fetching workflows:', error);
    return [];
  }
}

/**
 * Run a workflow and get results
 */
async function runWorkflow(
  workflowId: string, 
  options?: { retailerId?: string; actionId?: string }
): Promise<WorkflowRunResult | null> {
  try {
    const response = await fetch('/api/workflows/run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workflowId,
        retailerId: options?.retailerId,
        actionId: options?.actionId
      })
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('[PlaybookPanel] Error running workflow:', error);
    return null;
  }
}

/**
 * WorkflowRunDrawer: Phase 9D - Operator View
 * 
 * Presents clean, executive-ready output:
 * - Quality Banner
 * - Executive Summary
 * - What Happened
 * - Key Risks
 * - What To Do (Decision Plan)
 * - What's Missing
 * - Evidence Panel (drilldown)
 * - Debug toggle for agent transcript
 */
function WorkflowRunDrawer({
  result,
  isOpen,
  onClose,
  onActionsCreated
}: {
  result: WorkflowRunResult | null;
  isOpen: boolean;
  onClose: () => void;
  onActionsCreated?: (actionIds: string[]) => void;
}) {
  if (!isOpen || !result) return null;
  
  const isSupervised = Boolean(result.final?.decisionPlan?.topActions);
  const [selectedEvidenceMetricKey, setSelectedEvidenceMetricKey] = useState<string | null>(null);
  const [showDebugTranscript, setShowDebugTranscript] = useState(false);
  const queryClient = useQueryClient();
  const [flash, setFlash] = useState<string | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);
  const [isCreatingActions, setIsCreatingActions] = useState(false);
  const [localCreatedActionIds, setLocalCreatedActionIds] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const runId = (result as any).runId || 'legacy';
  
  // Build operator view for supervised workflows
  const operatorView: OperatorWorkflowView | null = isSupervised 
    ? buildOperatorWorkflowView(result as any) 
    : null;

  const attachToCommandCenter = async () => {
    if (!isSupervised || !result.final) return;
    setIsAttaching(true);
    setFlash(null);
    try {
      const resp = await fetch('/api/command-center/workflow-artifacts/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runId,
          workflowId: result.workflow.id,
          executiveSummary: result.final.executiveSummary,
          decisionPlan: result.final.decisionPlan,
          quality: result.final.quality,
          conflicts: result.final.conflicts || null,
          evidenceBundleId: null
        })
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setFlash('Attached to Command Center');
      queryClient.invalidateQueries({ queryKey: ['workflow-artifacts'] });
    } catch (e: any) {
      console.error('[WorkflowRunDrawer] Attach failed:', e);
      setFlash('Attach failed');
    } finally {
      setIsAttaching(false);
    }
  };

  const createActionsFromDecisionPlan = async () => {
    if (!isSupervised || !result.final) return;
    setIsCreatingActions(true);
    setFlash(null);
    try {
      const resp = await fetch('/api/command-center/actions/createFromDecisionPlan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runId,
          workflowId: result.workflow.id,
          items: result.final.decisionPlan.topActions || []
        })
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setFlash(`Created ${data.createdCount ?? 0} actions`);
      
      // Phase 9E: Store created action IDs and notify parent
      const ids = data.createdActionIds || [];
      setLocalCreatedActionIds(ids);
      if (onActionsCreated && ids.length > 0) {
        onActionsCreated(ids);
      }
      
      queryClient.invalidateQueries({ queryKey: ['command-center-intelligence'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-artifacts'] });
    } catch (e: any) {
      console.error('[WorkflowRunDrawer] Create actions failed:', e);
      setFlash('Create actions failed');
    } finally {
      setIsCreatingActions(false);
    }
  };

  // Export to PDF function
  const exportToPdf = async () => {
    setIsExporting(true);
    try {
      // Use browser print with PDF destination
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to export PDF');
        return;
      }
      
      const content = document.getElementById('workflow-report-content');
      const title = result.workflow.name;
      const date = new Date().toLocaleDateString();
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title} - ${date}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { font-size: 24px; margin-bottom: 8px; }
            h2 { font-size: 18px; color: #374151; margin-top: 24px; }
            h3 { font-size: 14px; color: #6b7280; margin-bottom: 16px; }
            .section { margin-bottom: 24px; padding: 16px; background: #f9fafb; border-radius: 8px; }
            .metric { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
            .metric:last-child { border-bottom: none; }
            .value { font-weight: 600; }
            .summary { background: linear-gradient(135deg, #f3e8ff 0%, #fae8ff 100%); padding: 16px; border-radius: 8px; }
            .action { background: white; border: 1px solid #e5e7eb; padding: 12px; border-radius: 8px; margin-bottom: 8px; }
            .badge { display: inline-block; padding: 2px 8px; background: #e5e7eb; border-radius: 12px; font-size: 12px; margin-right: 8px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <h3>Generated: ${date} • Run: ${runId.slice(0, 12)}...</h3>
          ${content?.innerHTML || '<p>No content available</p>'}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    } catch (e) {
      console.error('[WorkflowRunDrawer] PDF export failed:', e);
      setFlash('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-50" 
        onClick={onClose}
      />
      
      {/* Drawer - expandable width */}
      <div className={`fixed right-0 top-0 h-full bg-white shadow-xl z-50 overflow-y-auto transition-all duration-300 ${
        isExpanded ? 'w-full max-w-4xl' : 'w-full max-w-lg'
      }`}>
        {/* Header with action buttons */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-gray-900 truncate">{result.workflow.name}</h2>
              <p className="text-sm text-gray-500 truncate">{result.workflow.description}</p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              {/* Expand/Collapse */}
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <Minimize2 className="w-5 h-5" />
                ) : (
                  <Maximize2 className="w-5 h-5" />
                )}
              </button>
              {/* Close */}
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
          
          {/* Action buttons row */}
          {isSupervised && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={attachToCommandCenter}
                disabled={isAttaching}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 text-sm font-medium"
              >
                {isAttaching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save to Command Center
              </button>
              
              <button
                onClick={createActionsFromDecisionPlan}
                disabled={isCreatingActions}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
              >
                {isCreatingActions ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ListTodo className="w-4 h-4" />
                )}
                Create Action List
              </button>
              
              <button
                onClick={exportToPdf}
                disabled={isExporting}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4" />
                )}
                Export PDF
              </button>
              
              {/* Flash message */}
              {flash && (
                <span className={`text-sm font-medium ${
                  flash.includes('failed') ? 'text-red-600' : 'text-emerald-600'
                }`}>
                  {flash}
                </span>
              )}
            </div>
          )}
          
          {/* View created actions button */}
          {localCreatedActionIds.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => {
                  onActionsCreated?.(localCreatedActionIds);
                  onClose();
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 text-sm"
              >
                <CheckCircle className="w-4 h-4" />
                View {localCreatedActionIds.length} created actions
              </button>
            </div>
          )}
        </div>

        <div id="workflow-report-content" className="p-6 space-y-6">
          {/* ================================================================
              PHASE 9D: OPERATOR VIEW - Clean executive presentation
              ================================================================ */}
          
          {isSupervised && operatorView ? (
            <>
              {/* 1. QUALITY BANNER */}
              <div
                className={`p-4 rounded-lg border ${
                  operatorView.quality.status === 'PASS'
                    ? 'bg-green-50 border-green-200'
                    : operatorView.quality.status === 'DEGRADED'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {operatorView.quality.status === 'PASS' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : operatorView.quality.status === 'DEGRADED' ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <div>
                      <div className="font-semibold text-gray-900">
                        {operatorView.quality.status === 'PASS'
                          ? 'Verified Analysis'
                          : operatorView.quality.status === 'DEGRADED'
                            ? 'Analysis Complete — Some Data Missing'
                            : 'Analysis Incomplete'}
                      </div>
                      <div className="text-xs text-gray-600">
                        Coverage: {Math.round((operatorView.quality.coveragePctKnown || 0) * 100)}% • 
                        Score: {operatorView.quality.score}/100 • 
                        Mode: {operatorView.quality.strictness}
                      </div>
                      {operatorView.quality.why && (
                        <div className="text-xs text-gray-500 mt-1">{operatorView.quality.why}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 text-right">
                    <div className="font-mono">run: {runId.slice(0, 12)}...</div>
                    <div className="text-gray-500">
                      {result.steps.length} steps • {(result.totalLatencyMs / 1000).toFixed(1)}s
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. EXECUTIVE SUMMARY */}
              {operatorView.executiveSummary && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-violet-500" />
                    Executive Summary
                  </h3>
                  <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4">
                    <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                      {operatorView.executiveSummary}
                    </div>
                  </div>
                </div>
              )}

              {/* 3. WHAT HAPPENED */}
              {operatorView.whatHappened.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-600" />
                    What Happened
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {operatorView.whatHappened.map((item, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                        <div className="font-semibold text-gray-900">{item.value || 'N/A'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 4. KEY RISKS */}
              {operatorView.keyRisks.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Key Risks
                  </h3>
                  <div className="space-y-2">
                    {operatorView.keyRisks.map((risk, idx) => (
                      <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="font-medium text-amber-900">{risk.title}</div>
                        {risk.detail && (
                          <div className="text-sm text-amber-800 mt-1">{risk.detail}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 5. WHAT TO DO (Decision Plan) */}
              {operatorView.whatToDo.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Target className="w-4 h-4 text-emerald-600" />
                    What To Do
                  </h3>
                  <div className="space-y-3">
                    {operatorView.whatToDo.map((action, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900">{action.title}</div>
                            {action.rationale && (
                              <div className="text-sm text-gray-700 mt-1">{action.rationale}</div>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 flex flex-col items-end gap-1 shrink-0">
                            {action.owner && (
                              <span className="px-2 py-0.5 bg-gray-100 rounded-full">{action.owner}</span>
                            )}
                            {action.timeHorizon && (
                              <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full">
                                {action.timeHorizon}
                              </span>
                            )}
                          </div>
                        </div>
                        {action.linkedAssertionIds.length > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            <Database className="w-3 h-3 inline mr-1" />
                            {action.linkedAssertionIds.length} evidence link(s)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 6. WHAT'S MISSING */}
              {operatorView.whatsMissing.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-gray-500" />
                    What's Missing
                  </h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="space-y-2">
                      {operatorView.whatsMissing.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-gray-400">•</span>
                          <div>
                            <span className="font-medium text-gray-700">{item.label}</span>
                            {item.reason && (
                              <span className="text-gray-500"> — {item.reason}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* EVIDENCE PANEL (Drilldown) */}
              <EvidencePanel
                runId={runId}
                evidence={result.final.evidence as any}
                conflicts={result.final.conflicts as any}
                selectedMetricKey={selectedEvidenceMetricKey}
                onSelectMetricKey={(mk) => setSelectedEvidenceMetricKey(mk)}
              />

              {/* 9. DEBUG TOGGLE - Agent Execution Details */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={() => setShowDebugTranscript(!showDebugTranscript)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  {showDebugTranscript ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  <Activity className="w-4 h-4" />
                  Agent Execution Details (Debug)
                </button>
                
                {showDebugTranscript && (
                  <div className="mt-4 space-y-4">
                    {result.steps.map((step, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className={`px-4 py-2 flex items-center justify-between ${step.success ? 'bg-gray-50' : 'bg-red-50'}`}>
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                              {idx + 1}
                            </span>
                            <span className="font-medium text-gray-900">{step.agentId}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{step.latencyMs}ms</span>
                            {step.success ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                        </div>
                        <div className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto font-mono text-xs">
                          {step.result.substring(0, 500)}{step.result.length > 500 ? '...' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Legacy (non-supervised) workflow - Show step results + final answer */
            <>
              {/* Execution Summary */}
              <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {result.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-semibold ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                    {result.success ? 'Workflow completed' : 'Workflow encountered errors'}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {result.steps.length} steps • {(result.totalLatencyMs / 1000).toFixed(1)}s
                </p>
              </div>

              {/* Final Answer */}
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                  Analysis
                </h3>
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4">
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                    {result.finalAnswer}
                  </div>
                </div>
              </div>

              {/* Debug Transcript (collapsed by default) */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={() => setShowDebugTranscript(!showDebugTranscript)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  {showDebugTranscript ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  <Activity className="w-4 h-4" />
                  Step Details (Debug)
                </button>
                
                {showDebugTranscript && (
                  <div className="mt-4 space-y-4">
                    {result.steps.map((step, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className={`px-4 py-2 flex items-center justify-between ${step.success ? 'bg-gray-50' : 'bg-red-50'}`}>
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                              {idx + 1}
                            </span>
                            <span className="font-medium text-gray-900">{step.agentId}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>{step.latencyMs}ms</span>
                            {step.success ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                        </div>
                        <div className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto font-mono text-xs">
                          {step.result.substring(0, 500)}{step.result.length > 500 ? '...' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

type WorkflowArtifactListItem = {
  id: string;
  runId: string;
  workflowId: string;
  createdAt: string;
  status: string;
  qualityStatus: string | null;
  qualityScore: number | null;
  coveragePctKnown: number | null;
};

type WorkflowArtifactFull = {
  id: string;
  runId: string;
  workflowId: string;
  createdAt: string;
  status: string;
  quality: any;
  decisionPlan: any;
  executiveSummary: string | null;
  evidenceBundleId: string | null;
  conflicts: any;
};

/**
 * WorkflowArtifactDrawer: Phase 9F - Full Operator View for Saved Artifacts
 * 
 * Shows the same operator layout as WorkflowRunDrawer:
 * - Quality Banner
 * - Executive Summary  
 * - Decision Plan (What To Do)
 * - Evidence Panel (interactive drilldown)
 */
function WorkflowArtifactDrawer({
  artifactId,
  isOpen,
  onClose
}: {
  artifactId: string | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [selectedEvidenceMetricKey, setSelectedEvidenceMetricKey] = useState<string | null>(null);
  
  // HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS (React rules of hooks)
  const { data, isLoading } = useQuery<{ success: boolean; artifact: WorkflowArtifactFull }>({
    queryKey: ['workflow-artifact', artifactId],
    queryFn: async () => {
      if (!artifactId) throw new Error('artifactId is null');
      const resp = await fetch(`/api/command-center/workflow-artifacts/get?id=${encodeURIComponent(artifactId)}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return resp.json();
    },
    enabled: Boolean(isOpen && artifactId),  // Only fetch when drawer is open
    staleTime: 30 * 1000
  });

  const artifact = data?.artifact;
  const runId = artifact?.runId;
  const quality = artifact?.quality;

  const { data: evidenceRun } = useQuery<any>({
    queryKey: ['evidence-run', runId],
    queryFn: async () => {
      if (!runId) throw new Error('runId is undefined');
      const resp = await fetch(`/api/agent-core/evidence/run?runId=${encodeURIComponent(runId)}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return resp.json();
    },
    enabled: Boolean(isOpen && runId),  // Only fetch when drawer is open AND runId exists
    staleTime: 30 * 1000
  });

  const evidence = evidenceRun?.finalBundle || null;

  // Early return AFTER hooks
  if (!isOpen || !artifactId) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-50" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Workflow Artifact</h2>
            <p className="text-xs text-gray-500 font-mono">{artifact?.workflowId || '—'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {isLoading || !artifact ? (
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
              Loading artifact…
            </div>
          ) : (
            <>
              {/* 1. QUALITY BANNER */}
              {quality && (
                <div
                  className={`p-4 rounded-lg border ${
                    quality.status === 'PASS'
                      ? 'bg-green-50 border-green-200'
                      : quality.status === 'DEGRADED'
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      {quality.status === 'PASS' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : quality.status === 'DEGRADED' ? (
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <div className="font-semibold text-gray-900">
                          {quality.status === 'PASS'
                            ? 'Verified Analysis'
                            : quality.status === 'DEGRADED'
                              ? 'Analysis Complete — Some Data Missing'
                              : 'Analysis Incomplete'}
                        </div>
                        <div className="text-xs text-gray-600">
                          Coverage: {Math.round((quality.coveragePctKnown || 0) * 100)}% • 
                          Score: {quality.score}/100 • 
                          Mode: {quality.strictness}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 text-right">
                      <div className="font-mono">run: {runId?.slice(0, 12)}...</div>
                      <div className="text-gray-500">
                        {new Date(artifact.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata bar (if no quality) */}
              {!quality && (
                <div className="text-xs text-gray-600 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(artifact.createdAt).toLocaleString()}
                  </div>
                  <div className="px-2 py-0.5 bg-gray-100 rounded-full">{artifact.status}</div>
                </div>
              )}

              {/* 2. EXECUTIVE SUMMARY */}
              {artifact.executiveSummary ? (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-violet-500" />
                    Executive Summary
                  </h3>
                  <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4">
                    <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                      {typeof artifact.executiveSummary === 'string' 
                        ? artifact.executiveSummary 
                        : JSON.stringify(artifact.executiveSummary, null, 2)}
                    </div>
                  </div>
                </div>
              ) : null}

              {/* BRAIN RUN: What We Know (normalized metrics) */}
              {artifact.workflowId === 'brain_run' && artifact.decisionPlan?.brainOutput?.normalizedMetrics?.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-600" />
                    What We Know
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {artifact.decisionPlan.brainOutput.normalizedMetrics.slice(0, 8).map((m: any, idx: number) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">{m.label}</div>
                        <div className="font-semibold text-gray-900">{m.displayValue}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* BRAIN RUN: Impact Summary */}
              {artifact.workflowId === 'brain_run' && artifact.decisionPlan?.brainOutput?.impactSummary?.downsideRange && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <h3 className="font-semibold text-red-900 flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4" />
                    If Ignored
                  </h3>
                  <div className="text-red-900">
                    <span className="font-semibold">
                      ${artifact.decisionPlan.brainOutput.impactSummary.downsideRange[0]?.toLocaleString()} - 
                      ${artifact.decisionPlan.brainOutput.impactSummary.downsideRange[1]?.toLocaleString()}
                    </span>
                    {' '}at risk if recommendations are not followed.
                  </div>
                  {artifact.decisionPlan.brainOutput.impactSummary.upsideRange && (
                    <div className="text-sm text-red-800 mt-2">
                      Potential upside: ${artifact.decisionPlan.brainOutput.impactSummary.upsideRange[0]?.toLocaleString()} - 
                      ${artifact.decisionPlan.brainOutput.impactSummary.upsideRange[1]?.toLocaleString()}
                    </div>
                  )}
                </div>
              )}

              {/* 3. DECISION PLAN (What To Do) */}
              {artifact.decisionPlan?.topActions?.length ? (
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Target className="w-4 h-4 text-emerald-600" />
                    What To Do
                  </h3>
                  <div className="space-y-3">
                    {artifact.decisionPlan.topActions.slice(0, 6).map((a: any, idx: number) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900">{a.title}</div>
                            {a.rationale && (
                              <div className="text-sm text-gray-700 mt-1">{a.rationale}</div>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 flex flex-col items-end gap-1 shrink-0">
                            {a.recommendedOwnerRole && (
                              <span className="px-2 py-0.5 bg-gray-100 rounded-full">{a.recommendedOwnerRole}</span>
                            )}
                            {a.timeHorizon && (
                              <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full">
                                {String(a.timeHorizon).replace('_', ' ')}
                              </span>
                            )}
                          </div>
                        </div>
                        {a.linkedAssertions?.length > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            <Database className="w-3 h-3 inline mr-1" />
                            {a.linkedAssertions.length} evidence link(s)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* 4. CONFLICTS (if any) */}
              {artifact.conflicts?.length > 0 && (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-semibold text-amber-900">Evidence Conflicts</span>
                  </div>
                  <div className="space-y-1">
                    {artifact.conflicts.slice(0, 5).map((c: any, idx: number) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedEvidenceMetricKey(c.metricKey)}
                        className="w-full text-left text-xs text-amber-900 hover:underline font-mono"
                      >
                        {c.metricKey}: {Array.isArray(c.values) 
                          ? c.values.map((v: any) => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ') 
                          : '?'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 5. EVIDENCE PANEL (Interactive Drilldown) */}
              {evidence && runId ? (
                <EvidencePanel
                  runId={runId}
                  evidence={evidence}
                  conflicts={artifact.conflicts || undefined}
                  selectedMetricKey={selectedEvidenceMetricKey}
                  onSelectMetricKey={(mk) => setSelectedEvidenceMetricKey(mk)}
                />
              ) : (
                <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="text-sm text-gray-600 flex items-center gap-2">
                    <Database className="w-4 h-4 text-gray-400" />
                    Evidence not available for this artifact.
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Evidence bundle may have expired or was not persisted.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function WorkflowArtifactsPanel() {
  const { companyId } = useTenant();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery<{ artifacts: WorkflowArtifactListItem[] }>({
    queryKey: ['workflow-artifacts', companyId],
    queryFn: async () => {
      const resp = await fetch('/api/command-center/workflow-artifacts/list?limit=10');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return resp.json();
    },
    enabled: !!companyId,
    staleTime: 30 * 1000
  });

  const artifacts = data?.artifacts || [];

  const openArtifact = (id: string) => {
    setSelectedId(id);
    setOpen(true);
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Database className="w-5 h-5 text-slate-700" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Workflow Artifacts</h3>
              <p className="text-xs text-gray-500">Attached supervised workflow runs (operating artifacts)</p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
          >
            Refresh
          </button>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
              Loading artifacts…
            </div>
          ) : artifacts.length === 0 ? (
            <div className="text-sm text-gray-600">No workflow artifacts yet. Run a playbook and click "Save to Command Center".</div>
          ) : (
            <div className="space-y-2">
              {artifacts.slice(0, 10).map((a) => (
                <div key={a.id} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900">{a.workflowId}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(a.createdAt).toLocaleString()}
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full">{a.qualityStatus || '—'}</span>
                      {typeof a.qualityScore === 'number' ? <span>score {a.qualityScore}</span> : null}
                      {typeof a.coveragePctKnown === 'number' ? <span>coverage {Math.round(a.coveragePctKnown * 100)}%</span> : null}
                    </div>
                  </div>
                  <button
                    onClick={() => openArtifact(a.id)}
                    className="px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 text-sm"
                  >
                    Open
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <WorkflowArtifactDrawer
        artifactId={selectedId}
        isOpen={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

/**
 * Playbook Panel - Collapsible section showing available workflows
 */
function PlaybookPanel() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [runningWorkflowId, setRunningWorkflowId] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<WorkflowRunResult | null>(null);
  const [showDrawer, setShowDrawer] = useState(false);

  useEffect(() => {
    if (isExpanded && workflows.length === 0) {
      setIsLoading(true);
      fetchWorkflows().then(wfs => {
        setWorkflows(wfs.filter(w => w.enabled));
        setIsLoading(false);
      });
    }
  }, [isExpanded]);

  const handleRunWorkflow = async (workflowId: string) => {
    setRunningWorkflowId(workflowId);
    const result = await runWorkflow(workflowId);
    setRunningWorkflowId(null);
    if (result) {
      setRunResult(result);
      setShowDrawer(true);
    }
  };

  // Group workflows by category
  const groupedWorkflows = workflows.reduce((acc, wf) => {
    const category = wf.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(wf);
    return acc;
  }, {} as Record<string, WorkflowDefinition[]>);

  const categoryLabels: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    executive: { label: 'Executive & Strategy', icon: <Star className="w-4 h-4" />, color: 'text-amber-600' },
    retail: { label: 'Retail Operations', icon: <Store className="w-4 h-4" />, color: 'text-blue-600' },
    campaign: { label: 'Marketing & Campaigns', icon: <Rocket className="w-4 h-4" />, color: 'text-purple-600' },
    inventory: { label: 'Inventory & Supply Chain', icon: <Package className="w-4 h-4" />, color: 'text-green-600' },
    other: { label: 'Other Playbooks', icon: <Zap className="w-4 h-4" />, color: 'text-gray-600' }
  };

  const categoryOrder = ['executive', 'retail', 'campaign', 'inventory', 'other'];

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Collapsed Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-5 py-3 bg-gradient-to-r from-violet-50 to-purple-50 border-b border-gray-200 flex items-center justify-between hover:bg-violet-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Zap className="w-5 h-5 text-violet-600" />
            </div>
            <div className="text-left">
              <h3 className="font-bold text-gray-900">Playbooks</h3>
              <p className="text-xs text-gray-500">One-click agentic workflows</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full text-xs font-medium">
              {workflows.length} available
            </span>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
              </div>
            ) : workflows.length > 0 ? (
              <div className="space-y-4">
                {categoryOrder.map(category => {
                  const categoryWorkflows = groupedWorkflows[category];
                  if (!categoryWorkflows || categoryWorkflows.length === 0) return null;
                  
                  const catInfo = categoryLabels[category] || categoryLabels.other;
                  
                  return (
                    <div key={category}>
                      <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${catInfo.color}`}>
                        {catInfo.icon}
                        {catInfo.label}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {categoryWorkflows.slice(0, 4).map(wf => (
                          <div 
                            key={wf.workflowId}
                            className="border border-gray-200 rounded-lg p-3 hover:border-violet-300 hover:bg-violet-50/50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h5 className="font-medium text-gray-900 text-sm truncate">{wf.name}</h5>
                                <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                                  {wf.description || `${wf.steps.length} agent steps`}
                                </p>
                              </div>
                              <button
                                onClick={() => handleRunWorkflow(wf.workflowId)}
                                disabled={runningWorkflowId !== null}
                                className="ml-2 px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                              >
                                {runningWorkflowId === wf.workflowId ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Play className="w-3 h-3" />
                                )}
                                Run
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No playbooks available</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Workflow Run Drawer */}
      <WorkflowRunDrawer 
        result={runResult}
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
      />
    </>
  );
}

function SnoozedItems({ snoozedActions, onUnsnooze }: SnoozedItemsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [unsnoozingId, setUnsnoozingId] = useState<string | null>(null);
  
  if (snoozedActions.length === 0) {
    return null;
  }
  
  const handleUnsnooze = async (action: CommandCenterAction, e: React.MouseEvent) => {
    e.stopPropagation();
    setUnsnoozingId(action.contentHash);
    try {
      await onUnsnooze(action.contentHash);
    } finally {
      setUnsnoozingId(null);
    }
  };
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Compact Collapsed Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 bg-purple-50/50 flex items-center justify-between hover:bg-purple-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Pause className="w-4 h-4 text-purple-600" />
          <span className="font-medium text-gray-900 text-sm">Snoozed</span>
          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
            {snoozedActions.length}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="divide-y divide-gray-100">
          {snoozedActions.map((action) => (
            <div 
              key={action.id}
              className="p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <StatusBadge status={action.status} />
                    {action.snoozeUntil && (
                      <span className="text-xs text-purple-600">
                        Until {new Date(action.snoozeUntil).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    )}
                    {action.assignedTo && (
                      <AssigneePill assignedTo={action.assignedTo} />
                    )}
                  </div>
                  <p className="font-medium text-gray-900 mb-1">{action.title}</p>
                  <p className="text-sm text-gray-500 line-clamp-2">{action.description}</p>
                </div>
                <button
                  onClick={(e) => handleUnsnooze(action, e)}
                  disabled={unsnoozingId === action.contentHash}
                  className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition-colors flex items-center gap-1 disabled:opacity-50"
                >
                  {unsnoozingId === action.contentHash ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Play className="w-3 h-3" />
                  )}
                  Unsnooze
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// QUICK PERFORMANCE SNAPSHOT (Secondary - Bottom)
// ============================================================================

function PerformanceSnapshot() {
  const { selectedRetailerId } = useTenant();
  
  // Retail KPIs
  const { data: retailKPIs } = useQuery({
    queryKey: ['command-center-retail-kpis', selectedRetailerId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedRetailerId) params.set('retailer_id', selectedRetailerId);
      const response = await fetch(`/api/dashboard/kpis${params.toString() ? '?' + params.toString() : ''}`);
      if (!response.ok) return null;
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  // DTC KPIs
  const { data: dtcKPIs } = useQuery({
    queryKey: ['command-center-dtc-summary'],
    queryFn: async () => {
      const response = await fetch('/api/dtc/summary');
      if (!response.ok) return null;
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const hasRetail = retailKPIs && retailKPIs.totalRevenue > 0;
  const hasDTC = dtcKPIs && dtcKPIs.totalRevenue > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <BarChart3 className="w-5 h-5 text-gray-600" />
            </div>
            <div>
            <h3 className="font-bold text-gray-900">Performance Snapshot</h3>
            <p className="text-xs text-gray-500">Quick numbers • Full details in dashboards</p>
              </div>
                      </div>
                        </div>

      <div className="p-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Retail */}
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
            <div className="flex items-center gap-2 mb-2">
              <Store className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-700">Retail</span>
                    </div>
            {hasRetail ? (
              <>
                <p className="text-xl font-bold text-gray-900">{formatCurrencyCompact(retailKPIs.totalRevenue)}</p>
                {retailKPIs.revenue_change !== null && typeof retailKPIs.revenue_change === 'number' && !isNaN(retailKPIs.revenue_change) && (
                  <div className={`flex items-center gap-1 text-sm ${retailKPIs.revenue_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {retailKPIs.revenue_change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {(retailKPIs.revenue_change * 100).toFixed(1)}% YoY
                </div>
                )}
              </>
              ) : (
              <p className="text-gray-400 text-sm">No data</p>
              )}
            <Link href="/dashboard">
              <a className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1 mt-2">
                Full Dashboard <ArrowRight className="w-3 h-3" />
              </a>
            </Link>
            </div>

          {/* DTC */}
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingCart className="w-4 h-4 text-orange-600" />
              <span className="text-xs font-medium text-orange-700">DTC (30d)</span>
                    </div>
            {hasDTC ? (
              <>
                <p className="text-xl font-bold text-gray-900">{formatCurrencyCompact(dtcKPIs.totalRevenue)}</p>
                <p className="text-sm text-gray-500">{dtcKPIs.totalOrders.toLocaleString()} orders</p>
              </>
            ) : (
              <p className="text-gray-400 text-sm">No data</p>
            )}
            <Link href="/dtc">
              <a className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1 mt-2">
                Full DTC Hub <ArrowRight className="w-3 h-3" />
              </a>
            </Link>
            </div>
          </div>
            </div>
        </div>
  );
}

// ============================================================================
// AGENT QUICK ACCESS
// ============================================================================

function AgentQuickAccess() {
  const agents = [
    { name: 'Retail Story', path: '/agents/retail-story', icon: '📊', desc: 'Buyer meeting prep', linkedTo: ['NPD', 'Supply'] },
    { name: 'Competitor Intel', path: '/agents/competitor-intel', icon: '🎯', desc: 'Market positioning', linkedTo: ['NPD', 'Creative'] },
    { name: 'Supply Chain', path: '/agents/supply-chain', icon: '📦', desc: 'Inventory risks', linkedTo: ['Merch', 'Clusters'] },
    { name: 'NPD Opportunity', path: '/agents/npd-opportunity', icon: '💡', desc: 'New product ideas', linkedTo: ['Competitor', 'Creative'] },
    { name: 'Merch Optimization', path: '/agents/merch-optimization', icon: '🏪', desc: 'Shelf optimization', linkedTo: ['Supply', 'Clusters'] },
    { name: 'Store Clusters', path: '/ai/store-clusters', icon: '📍', desc: 'Store grouping', linkedTo: ['Merch', 'Supply'] },
];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-violet-50 to-purple-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Brain className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">PIQ Agentic Workforce</h3>
              <p className="text-xs text-gray-500">Integrated agents • No silos</p>
            </div>
          </div>
          <span className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
            6 Active
          </span>
        </div>
      </div>

      <div className="p-4 grid grid-cols-3 gap-2">
        {agents.map(agent => (
          <Link key={agent.name} href={agent.path}>
            <a className="group block p-3 rounded-lg bg-gray-50 hover:bg-violet-50 border border-transparent hover:border-violet-200 transition-all text-center relative">
              <span className="text-2xl block mb-1">{agent.icon}</span>
              <p className="font-medium text-gray-900 text-sm">{agent.name}</p>
              <p className="text-xs text-gray-500 line-clamp-1">{agent.desc}</p>
              {/* Cross-links indicator */}
              <div className="flex justify-center gap-1 mt-1">
                {agent.linkedTo.slice(0, 2).map((link, i) => (
                  <span key={i} className="px-1 py-0.5 text-[10px] bg-violet-100 text-violet-600 rounded">
                    ↔ {link}
                  </span>
                ))}
              </div>
            </a>
          </Link>
        ))}
      </div>
      
      {/* Integration Status */}
      <div className="px-4 pb-4">
        <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
          <Activity className="w-4 h-4 text-emerald-600" />
          <span className="text-xs text-emerald-700">
            All agents cross-reference insights. No data silos.
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DATA-DRIVEN + ML-POWERED INSIGHTS TAB
// ============================================================================
// Includes TWO types of insights:
// 1. Pattern-Based: Statistical threshold detection (e.g., sales drop > $500)
// 2. ML-Powered: Trained models (anomaly detection, clustering, trend analysis)

interface UnifiedInsight {
  id: string;
  source_type: 'retail' | 'dtc';
  insight_type: string;
  category: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  recommendation: string;
  dollar_impact: number | null;
  confidence_score: number | null;
  
  // Retail-specific
  fiscal_week?: string;
  retailer_id?: string;
  retailer_name?: string;
  related_store?: string;
  
  // DTC-specific
  platform_id?: string;
  platform_name?: string;
  date_range_start?: string;
  date_range_end?: string;
  
  // Common
  related_sku?: string;
  source: string;
  generated_by: string;
  generated_at: string;
  is_actioned: boolean;
  is_acknowledged?: boolean;
  is_ml_powered?: boolean; // TRUE ML vs pattern-based
}

interface InsightsSummary {
  total: number;
  retail_count: number;
  dtc_count: number;
  ml_powered_count: number;
  pattern_based_count: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  byCategory: Record<string, number>;
  totalImpact: number;
}

function InsightsTab() {
  const { selectedRetailerId, retailers, dtcPlatforms } = useTenant();
  const [insights, setInsights] = useState<UnifiedInsight[]>([]);
  const [summary, setSummary] = useState<InsightsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSource, setSelectedSource] = useState<string>('all'); // 'all', 'retail', 'dtc'
  const [selectedInsightType, setSelectedInsightType] = useState<string>('all'); // 'all', 'ml', 'pattern'
  const [selectedRetailerFilter, setSelectedRetailerFilter] = useState<string>('all'); // Retailer/Platform filter
  const [showAcknowledged, setShowAcknowledged] = useState(true);  // Default ON to show all insights
  const [expandedInsight, setExpandedInsight] = useState<string | null>(null);
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateResult, setRegenerateResult] = useState<any>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [expandedPriorityGroups, setExpandedPriorityGroups] = useState<Record<string, boolean>>({
    critical: true,
    high: true,
    medium: false, // Collapsed by default to reduce scroll
    low: false
  });

  // Fetch UNIFIED insights from both retail + DTC
  const fetchInsights = async () => {
    try {
      setIsLoading(true);
      
      const params = new URLSearchParams();
      params.append('limit', '500'); // Increased from 100 to 500
      params.append('source_type', selectedSource);

      // Fetch unified insights (retail + DTC combined)
      const insightsResponse = await fetch(`/api/insights/unified?${params.toString()}`);
      const insightsData = await insightsResponse.json();

      // Fetch acknowledgments for retail insights
      let allAcknowledgments: any[] = [];
      if (selectedRetailerId) {
        const ackResponse = await fetch(`/api/insights/acknowledge?retailer_id=${selectedRetailerId}`);
        const ackData = await ackResponse.json();
        allAcknowledgments = ackData.acknowledgments || [];
      } else {
        const retailerIds = retailers?.map(r => r.id) || [];
        const ackPromises = retailerIds.map(id => 
          fetch(`/api/insights/acknowledge?retailer_id=${id}`).then(r => r.json())
        );
        const ackResults = await Promise.all(ackPromises);
        allAcknowledgments = ackResults.flatMap(r => r.acknowledgments || []);
      }

      if (insightsData.success) {
        const acknowledgedIds = new Set(allAcknowledgments.map((a: any) => a.insight_id));
        
        // Merge insights with acknowledgment status
        const mergedInsights = insightsData.insights.map((insight: UnifiedInsight) => ({
          ...insight,
          is_acknowledged: acknowledgedIds.has(insight.id)
        }));

        setInsights(mergedInsights);
        setSummary(insightsData.summary);
      }
    } catch (error) {
      console.error('[InsightsTab] Error fetching unified insights:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [selectedRetailerId, retailers, selectedSource]);

  // Handle acknowledgment
  const handleAcknowledge = async (insightId: string, retailerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setAcknowledgingId(insightId);
    
    try {
      const response = await fetch('/api/insights/acknowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insight_id: insightId,
          retailer_id: retailerId,
          notes: ''
        })
      });

      if (response.ok) {
        // Update local state
        setInsights(prev => prev.map(i => 
          i.id === insightId 
            ? { ...i, is_acknowledged: true, acknowledged_at: new Date().toISOString() }
            : i
        ));
      }
    } catch (error) {
      console.error('[InsightsTab] Error acknowledging insight:', error);
    } finally {
      setAcknowledgingId(null);
    }
  };

  // Refresh insights
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Generate new insights
      await fetch('/api/insights/generate', { method: 'POST' });
      await fetchInsights();
    } finally {
      setIsRefreshing(false);
    }
  };

  // ADMIN: Force regenerate ALL insights for ALL retailers
  const handleForceRegenerate = async () => {
    if (!confirm('This will regenerate ALL insights for ALL retailers. This may take a moment. Continue?')) {
      return;
    }
    
    setIsRegenerating(true);
    setRegenerateResult(null);
    
    try {
      const response = await fetch('/api/insights/regenerate-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clearExisting: true, daysToKeep: 7 })
      });
      
      const result = await response.json();
      setRegenerateResult(result);
      
      if (result.success) {
        // Refresh the insights display
        await fetchInsights();
      }
    } catch (error) {
      console.error('[InsightsTab] Regenerate all error:', error);
      setRegenerateResult({ success: false, error: 'Failed to regenerate insights' });
    } finally {
      setIsRegenerating(false);
    }
  };

  // Filter insights - apply ALL filters including retailer/platform
  const filteredInsights = insights.filter(insight => {
    if (!showAcknowledged && insight.is_acknowledged) return false;
    if (selectedPriority !== 'all' && insight.priority !== selectedPriority) return false;
    if (selectedCategory !== 'all' && insight.category !== selectedCategory) return false;
    if (selectedSource !== 'all' && insight.source_type !== selectedSource) return false;
    if (selectedInsightType === 'ml' && !insight.is_ml_powered) return false;
    if (selectedInsightType === 'pattern' && insight.is_ml_powered) return false;
    // Retailer/Platform filter
    if (selectedRetailerFilter !== 'all') {
      if (insight.source_type === 'retail' && insight.retailer_id !== selectedRetailerFilter) return false;
      if (insight.source_type === 'dtc' && insight.platform_id !== selectedRetailerFilter) return false;
    }
    return true;
  });

  // Compute filtered counts for categories (recalculate based on current filters EXCEPT category)
  const insightsForCategoryCounts = insights.filter(insight => {
    if (!showAcknowledged && insight.is_acknowledged) return false;
    if (selectedSource !== 'all' && insight.source_type !== selectedSource) return false;
    if (selectedInsightType === 'ml' && !insight.is_ml_powered) return false;
    if (selectedInsightType === 'pattern' && insight.is_ml_powered) return false;
    if (selectedRetailerFilter !== 'all') {
      if (insight.source_type === 'retail' && insight.retailer_id !== selectedRetailerFilter) return false;
      if (insight.source_type === 'dtc' && insight.platform_id !== selectedRetailerFilter) return false;
    }
    return true;
  });

  // Compute category counts from filtered insights
  const filteredCategoryCounts: Record<string, number> = {};
  insightsForCategoryCounts.forEach(i => {
    filteredCategoryCounts[i.category] = (filteredCategoryCounts[i.category] || 0) + 1;
  });

  // Group insights by priority for collapsible sections
  const insightsByPriority = {
    critical: filteredInsights.filter(i => i.priority === 'critical'),
    high: filteredInsights.filter(i => i.priority === 'high'),
    medium: filteredInsights.filter(i => i.priority === 'medium'),
    low: filteredInsights.filter(i => i.priority === 'low')
  };

  // Get unique categories from filtered insights
  const categories = [...new Set(insightsForCategoryCounts.map(i => i.category))].sort();

  // Toggle priority group expansion
  const togglePriorityGroup = (priority: string) => {
    setExpandedPriorityGroups(prev => ({ ...prev, [priority]: !prev[priority] }));
  };

  const getPriorityBadge = (priority: string, insightType?: string) => {
    // Achievements get green celebratory treatment
    if (insightType === 'achievement') {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-amber-100 text-amber-800 border-amber-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };
  
  const getIconBgColor = (priority: string, insightType?: string) => {
    // Achievements get green background
    if (insightType === 'achievement') {
      return 'bg-green-100';
    }
    
    switch (priority) {
      case 'critical': return 'bg-red-100';
      case 'high': return 'bg-orange-100';
      case 'medium': return 'bg-amber-100';
      default: return 'bg-blue-100';
    }
  };

  const getCategoryIcon = (category: string) => {
    if (category.includes('revenue')) return <TrendingDown className="w-4 h-4" />;
    if (category.includes('inventory') || category.includes('stockout')) return <Package className="w-4 h-4" />;
    if (category.includes('velocity')) return <TrendingUp className="w-4 h-4" />;
    if (category.includes('margin') || category.includes('pricing')) return <DollarSign className="w-4 h-4" />;
    return <Lightbulb className="w-4 h-4" />;
  };

  if (isLoading) {
  return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
            </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Source Type Tabs - Retail vs DTC */}
      <div className="flex items-center gap-2 bg-white rounded-xl p-2 border border-gray-200">
        <button
          onClick={() => setSelectedSource('all')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            selectedSource === 'all'
              ? 'bg-violet-100 text-violet-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          All Sources
          <span className="px-1.5 py-0.5 rounded-full text-xs bg-violet-200">{summary?.total || 0}</span>
        </button>
        <button
          onClick={() => setSelectedSource('retail')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            selectedSource === 'retail'
              ? 'bg-purple-100 text-purple-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Store className="w-4 h-4" />
          Retail
          <span className="px-1.5 py-0.5 rounded-full text-xs bg-purple-200">{summary?.retail_count || 0}</span>
        </button>
        <button
          onClick={() => setSelectedSource('dtc')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            selectedSource === 'dtc'
              ? 'bg-orange-100 text-orange-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <ShoppingCart className="w-4 h-4" />
          DTC
          <span className="px-1.5 py-0.5 rounded-full text-xs bg-orange-200">{summary?.dtc_count || 0}</span>
        </button>
        
        <div className="mx-2 h-8 w-px bg-gray-300" />
        
        {/* ML vs Pattern Filter */}
        <button
          onClick={() => setSelectedInsightType('ml')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            selectedInsightType === 'ml'
              ? 'bg-gradient-to-r from-violet-100 to-purple-100 text-violet-700 ring-2 ring-violet-300'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          ML-Powered
          <span className="px-1.5 py-0.5 rounded-full text-xs bg-violet-600 text-white">{summary?.ml_powered_count || 0}</span>
        </button>
        <button
          onClick={() => setSelectedInsightType('pattern')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            selectedInsightType === 'pattern'
              ? 'bg-blue-100 text-blue-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Pattern-Based
          <span className="px-1.5 py-0.5 rounded-full text-xs bg-blue-200">{summary?.pattern_based_count || 0}</span>
        </button>
        {selectedInsightType !== 'all' && (
          <button
            onClick={() => setSelectedInsightType('all')}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        )}
        
        {summary?.totalImpact && summary.totalImpact > 0 && (
          <div className="ml-auto flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-lg">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">
              {formatCurrencyCompact(summary.totalImpact)} potential impact
            </span>
          </div>
        )}
      </div>

      {/* Priority Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <button
          onClick={() => setSelectedPriority('all')}
          className={`p-4 rounded-xl border transition-all ${
            selectedPriority === 'all' 
              ? 'bg-violet-50 border-violet-300 ring-2 ring-violet-200' 
              : 'bg-white border-gray-200 hover:border-violet-200'
          }`}
        >
          <p className="text-2xl font-bold text-gray-900">{summary?.total || 0}</p>
          <p className="text-sm text-gray-500">All Insights</p>
            </button>
        <button
          onClick={() => setSelectedPriority(selectedPriority === 'critical' ? 'all' : 'critical')}
          className={`p-4 rounded-xl border transition-all ${
            selectedPriority === 'critical' 
              ? 'bg-red-50 border-red-300 ring-2 ring-red-200' 
              : 'bg-white border-gray-200 hover:border-red-200'
          }`}
        >
          <p className="text-2xl font-bold text-red-600">{summary?.critical || 0}</p>
          <p className="text-sm text-gray-500">Critical</p>
        </button>
        <button
          onClick={() => setSelectedPriority(selectedPriority === 'high' ? 'all' : 'high')}
          className={`p-4 rounded-xl border transition-all ${
            selectedPriority === 'high' 
              ? 'bg-orange-50 border-orange-300 ring-2 ring-orange-200' 
              : 'bg-white border-gray-200 hover:border-orange-200'
          }`}
        >
          <p className="text-2xl font-bold text-orange-600">{summary?.high || 0}</p>
          <p className="text-sm text-gray-500">High</p>
        </button>
        <button
          onClick={() => setSelectedPriority(selectedPriority === 'medium' ? 'all' : 'medium')}
          className={`p-4 rounded-xl border transition-all ${
            selectedPriority === 'medium' 
              ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200' 
              : 'bg-white border-gray-200 hover:border-amber-200'
          }`}
        >
          <p className="text-2xl font-bold text-amber-600">{summary?.medium || 0}</p>
          <p className="text-sm text-gray-500">Medium</p>
        </button>
        <button
          onClick={() => setSelectedPriority(selectedPriority === 'low' ? 'all' : 'low')}
          className={`p-4 rounded-xl border transition-all ${
            selectedPriority === 'low' 
              ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' 
              : 'bg-white border-gray-200 hover:border-blue-200'
          }`}
        >
          <p className="text-2xl font-bold text-blue-600">{summary?.low || 0}</p>
          <p className="text-sm text-gray-500">Low</p>
        </button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl p-4 border border-gray-200">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-500">Filters:</span>
        </div>

        {/* Retailer / Platform Filter Dropdown */}
        <select
          value={selectedRetailerFilter}
          onChange={(e) => setSelectedRetailerFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-700 hover:border-violet-300 focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
        >
          <option value="all">All Retailers & Platforms</option>
          <optgroup label="Retail Partners">
            {retailers?.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </optgroup>
          {dtcPlatforms && dtcPlatforms.length > 0 && (
            <optgroup label="DTC Platforms">
              {dtcPlatforms.map(p => (
                <option key={p.id} value={p.id}>{p.platform_name}</option>
              ))}
            </optgroup>
          )}
        </select>
        
        {/* Category Filter - uses filtered counts */}
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              selectedCategory === 'all'
                ? 'bg-violet-100 text-violet-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Categories
          </button>
          {categories.slice(0, 6).map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(selectedCategory === cat ? 'all' : cat)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                selectedCategory === cat
                  ? 'bg-violet-100 text-violet-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat} ({filteredCategoryCounts[cat] || 0})
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Show Acknowledged Toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showAcknowledged}
            onChange={(e) => setShowAcknowledged(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
          />
          <span className="text-sm text-gray-600">Show Acknowledged</span>
        </label>

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-lg text-sm font-medium hover:bg-violet-200 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
            
        {/* Admin Toggle */}
        <button
          onClick={() => setShowAdminPanel(!showAdminPanel)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            showAdminPanel 
              ? 'bg-red-100 text-red-700 ring-2 ring-red-200' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Database className="w-4 h-4" />
          Admin
        </button>
      </div>
      
      {/* Admin Panel - Force Regenerate Controls */}
      {showAdminPanel && (
        <div className="mt-4 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h4 className="font-bold text-red-800">Insights Admin Panel</h4>
              </div>
              <p className="text-sm text-red-700 mb-3">
                Force regenerate ALL insights across ALL retailers. Use this if insights are stale, 
                missing, or showing inconsistent data between pages.
              </p>
              <div className="text-xs text-red-600 space-y-1">
                <p>• Clears insights older than 7 days</p>
                <p>• Generates fresh insights for each retailer's latest fiscal week</p>
                <p>• Ensures ONE SOURCE OF TRUTH across Dashboard, Brand pages, and Command Center</p>
              </div>
            </div>
            <button
              onClick={handleForceRegenerate}
              disabled={isRegenerating}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                isRegenerating 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700 shadow-lg'
              }`}
            >
              {isRegenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Force Regenerate All
                </>
              )}
            </button>
          </div>
          
          {/* Regenerate Result */}
          {regenerateResult && (
            <div className={`mt-4 p-3 rounded-lg ${
              regenerateResult.success 
                ? 'bg-emerald-100 border border-emerald-300' 
                : 'bg-red-100 border border-red-300'
            }`}>
              {regenerateResult.success ? (
                <div>
                  <p className="font-semibold text-emerald-800 mb-2">
                    ✅ Regeneration Complete!
                  </p>
                  <div className="text-sm text-emerald-700 space-y-1">
                    <p>• Retailers processed: {regenerateResult.summary?.retailers_processed}</p>
                    <p>• Total insights generated: {regenerateResult.summary?.total_insights_generated}</p>
                    <p>• Old insights deleted: {regenerateResult.summary?.old_insights_deleted}</p>
                  </div>
                  {regenerateResult.results && (
                    <div className="mt-2 pt-2 border-t border-emerald-200">
                      <p className="text-xs font-medium text-emerald-800 mb-1">By Retailer:</p>
                      <div className="flex flex-wrap gap-2">
                        {regenerateResult.results.map((r: any, idx: number) => (
                          <span key={idx} className="px-2 py-1 bg-emerald-200 text-emerald-800 rounded text-xs">
                            {r.retailer}: {r.insights_generated} insights (Week {r.fiscal_week})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-red-800">
                  ❌ Error: {regenerateResult.error || regenerateResult.message || 'Unknown error'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Insights List - Grouped by Priority with Collapsible Sections */}
      <div className="space-y-4">
        {filteredInsights.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 font-medium">No insights to display</p>
            <p className="text-sm text-gray-400 mt-1">
              {insights.length > 0 
                ? 'Try adjusting your filters' 
                : 'AI insights will appear as patterns are detected'}
            </p>
          </div>
        ) : (
          <>
            {/* Priority Groups */}
            {(['critical', 'high', 'medium', 'low'] as const).map(priority => {
              const priorityInsights = insightsByPriority[priority];
              if (priorityInsights.length === 0) return null;
              
              const priorityConfig = {
                critical: { color: 'red', label: 'Critical', icon: AlertCircle },
                high: { color: 'orange', label: 'High Priority', icon: Flame },
                medium: { color: 'amber', label: 'Medium Priority', icon: Target },
                low: { color: 'blue', label: 'Low Priority', icon: Info }
              }[priority];
              
              const PriorityIcon = priorityConfig.icon;
              const isExpanded = expandedPriorityGroups[priority];
              
              return (
                <div key={priority} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {/* Priority Group Header - Clickable to expand/collapse */}
                  <button
                    onClick={() => togglePriorityGroup(priority)}
                    className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors border-b ${isExpanded ? 'border-gray-200' : 'border-transparent'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-${priorityConfig.color}-100`}>
                        <PriorityIcon className={`w-4 h-4 text-${priorityConfig.color}-600`} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900">{priorityConfig.label}</h3>
                        <p className="text-xs text-gray-500">{priorityInsights.length} insight{priorityInsights.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold bg-${priorityConfig.color}-100 text-${priorityConfig.color}-700`}>
                        {priorityInsights.length}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>
                  
                  {/* Expanded Insights */}
                  {isExpanded && (
                    <div className="divide-y divide-gray-100">
                      {priorityInsights.map(insight => (
            <div
              key={insight.id}
              className={`rounded-xl border transition-all ${
                insight.is_acknowledged 
                  ? 'bg-white border-gray-200 opacity-60' 
                  : insight.insight_type === 'achievement'
                    ? 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-md'
                    : 'bg-white border-gray-200 hover:border-violet-200 hover:shadow-md'
              }`}
            >
              {/* Header */}
              <div
                className="p-4 cursor-pointer"
                onClick={() => setExpandedInsight(expandedInsight === insight.id ? null : insight.id)}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${getIconBgColor(insight.priority, insight.insight_type)}`}>
                    {insight.insight_type === 'achievement' ? <Star className="w-4 h-4 text-green-600" /> : getCategoryIcon(insight.category)}
              </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {/* Source Type Badge */}
                      <span className={`px-2 py-0.5 rounded-md text-xs font-semibold uppercase ${
                        insight.source_type === 'dtc' 
                          ? 'bg-orange-100 text-orange-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {insight.source_type === 'dtc' ? 'DTC' : 'Retail'}
                      </span>
                      {/* ML vs Pattern Badge - Green with pulse animation */}
                      {insight.is_ml_powered && (
                        <span className="relative px-2 py-0.5 rounded-md text-xs font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white flex items-center gap-1 animate-pulse shadow-lg shadow-emerald-500/50">
                          <Sparkles className="w-3 h-3 animate-pulse" />
                          ML-Powered
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-ping" />
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-md text-xs font-semibold uppercase border flex items-center gap-1 ${getPriorityBadge(insight.priority, insight.insight_type)}`}>
                        {insight.insight_type === 'achievement' && <Star className="w-3 h-3" />}
                        {insight.insight_type === 'achievement' ? 'WIN' : insight.priority}
                      </span>
                      <span className="px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-600">
                        {insight.category}
                      </span>
                      {/* Retailer name for retail insights */}
                      {insight.source_type === 'retail' && insight.retailer_name && (
                        <span className="px-2 py-0.5 rounded-md text-xs bg-violet-100 text-violet-700">
                          {insight.retailer_name}
                        </span>
                      )}
                      {/* Platform name for DTC insights */}
                      {insight.source_type === 'dtc' && insight.platform_name && (
                        <span className="px-2 py-0.5 rounded-md text-xs bg-amber-100 text-amber-700">
                          {insight.platform_name}
                        </span>
                      )}
                      {insight.is_acknowledged && (
                        <span className="px-2 py-0.5 rounded-md text-xs bg-emerald-100 text-emerald-700 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Acknowledged
                        </span>
                      )}
                    </div>
                    <h4 className="font-semibold text-gray-900">{insight.title}</h4>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{insight.description}</p>
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      {/* Date context - fiscal week for retail, date range for DTC */}
                      {insight.source_type === 'retail' && insight.fiscal_week && (
                        <span>Week {insight.fiscal_week}</span>
                      )}
                      {insight.source_type === 'dtc' && insight.date_range_start && (
                        <span>{insight.date_range_start} - {insight.date_range_end}</span>
                      )}
                      {insight.dollar_impact && insight.dollar_impact > 0 && (
                        <span className={`font-medium ${insight.insight_type === 'achievement' ? 'text-green-600' : 'text-emerald-600'}`}>
                          {insight.insight_type === 'achievement' ? 'Revenue: ' : 'Impact: '}{formatCurrency(insight.dollar_impact)}
                        </span>
                      )}
                      {insight.confidence_score && (
                        <span>Confidence: {insight.confidence_score}%</span>
                      )}
                      {insight.related_sku && <span>SKU: {insight.related_sku}</span>}
              </div>
              </div>

                  <div className="flex items-center gap-2">
                    {!insight.is_acknowledged && insight.source_type === 'retail' && insight.retailer_id && (
                      <button
                        onClick={(e) => handleAcknowledge(insight.id, insight.retailer_id!, e)}
                        disabled={acknowledgingId === insight.id}
                        className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                        title="Mark as acknowledged"
                      >
                        {acknowledgingId === insight.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    {expandedInsight === insight.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                </div>
              </div>
            </div>

              {/* Expanded Content */}
              {expandedInsight === insight.id && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-4">
                  {/* Recommendation */}
                  <div className="bg-violet-50 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Rocket className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-violet-900">Recommendation</p>
                        <p className="text-sm text-violet-700 mt-1">{insight.recommendation}</p>
                      </div>
                    </div>
                  </div>

                  {/* AI Evidence & Context Section */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Brain className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-semibold text-blue-900">AI Analysis & Evidence</p>
                    </div>

                    <div className="space-y-3">
                      {/* Why This Matters */}
                      <div className="bg-white/60 rounded-lg p-3">
                        <p className="text-xs font-medium text-blue-700 mb-1">Why This Matters</p>
                        <p className="text-sm text-gray-600">
                          {insight.category === 'customer_retention' && (
                            <>Repeat customers typically generate 3-5x higher lifetime value than new customers. Industry benchmark for DTC brands is 60-70% revenue from returning customers. Low retention signals a need for loyalty programs, post-purchase nurturing, or product quality review.</>
                          )}
                          {insight.category === 'stockout' && (
                            <>Out-of-stock situations directly result in lost sales and can push customers to competitors. Each stockout event may represent 2-3x the immediate loss due to customer churn and negative brand impact.</>
                          )}
                          {insight.category === 'inventory' && (
                            <>Low inventory on high-velocity items creates stockout risk. The impact shown represents the potential revenue at risk if inventory isn't replenished before depletion.</>
                          )}
                          {insight.category === 'revenue' && (
                            <>Revenue changes beyond normal variance (±10%) indicate meaningful shifts in demand. This may signal competitive pressure, promotional effectiveness, or market changes.</>
                          )}
                          {insight.category === 'revenue_growth' && (
                            <>Rapid product growth indicates strong product-market fit. Capitalizing on momentum with adequate inventory and marketing can accelerate returns.</>
                          )}
                          {insight.category === 'profitability' && (
                            <>Low-margin products consume resources without proportional returns. Reviewing pricing or sourcing can improve overall portfolio profitability.</>
                          )}
                          {insight.category === 'platform_performance' && (
                            <>Significant performance gaps between platforms suggest optimization opportunities. Addressing listing quality, pricing, or inventory can close the gap.</>
                          )}
                          {!['customer_retention', 'stockout', 'inventory', 'revenue', 'revenue_growth', 'profitability', 'platform_performance'].includes(insight.category) && (
                            <>This insight was generated based on statistical analysis of your data. The pattern detected exceeds normal variance thresholds.</>
                          )}
                        </p>
                      </div>

                      {/* Data Analyzed */}
                      <div className="bg-white/60 rounded-lg p-3">
                        <p className="text-xs font-medium text-blue-700 mb-1">Data Analyzed</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {insight.source_type === 'retail' && (
                            <>
                              <div className="flex items-center gap-1">
                                <Database className="w-3 h-3 text-gray-400" />
                                <span className="text-gray-600">Source: sku_detail_current</span>
                              </div>
                              {insight.fiscal_week && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-gray-400" />
                                  <span className="text-gray-600">Fiscal Week: {insight.fiscal_week}</span>
                                </div>
                              )}
                              {insight.retailer_name && (
                                <div className="flex items-center gap-1">
                                  <Store className="w-3 h-3 text-gray-400" />
                                  <span className="text-gray-600">Retailer: {insight.retailer_name}</span>
                                </div>
                              )}
                            </>
                          )}
                          {insight.source_type === 'dtc' && (
                            <>
                              <div className="flex items-center gap-1">
                                <Database className="w-3 h-3 text-gray-400" />
                                <span className="text-gray-600">Source: dtc_sales_summary</span>
                              </div>
                              {insight.date_range_start && (
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-gray-400" />
                                  <span className="text-gray-600">Period: {insight.date_range_start} - {insight.date_range_end}</span>
                                </div>
                              )}
                              {insight.platform_name && (
                                <div className="flex items-center gap-1">
                                  <ShoppingCart className="w-3 h-3 text-gray-400" />
                                  <span className="text-gray-600">Platform: {insight.platform_name}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Impact Calculation */}
                      {insight.dollar_impact && insight.dollar_impact > 0 && (
                        <div className="bg-white/60 rounded-lg p-3">
                          <p className="text-xs font-medium text-blue-700 mb-1">Impact Calculation</p>
                          <p className="text-sm text-gray-600">
                            {insight.category === 'customer_retention' && (
                              <>Impact of {formatCurrency(insight.dollar_impact)} represents the revenue gap between current retention rate and the 60% target. This is an <strong>opportunity cost</strong>, not an actual loss—capturing even 25% of this gap would significantly improve profitability.</>
                            )}
                            {insight.category === 'stockout' && (
                              <>Impact of {formatCurrency(insight.dollar_impact)} is the estimated weekly revenue loss based on historical velocity of affected SKUs multiplied by their out-of-stock percentage.</>
                            )}
                            {insight.category === 'inventory' && (
                              <>Impact of {formatCurrency(insight.dollar_impact)} represents the current weekly velocity of this SKU—revenue at risk if inventory depletes before replenishment.</>
                            )}
                            {insight.category === 'revenue' && (
                              <>Impact of {formatCurrency(insight.dollar_impact)} is the actual revenue change vs the prior comparison period.</>
                            )}
                            {insight.category === 'revenue_growth' && (
                              <>Impact of {formatCurrency(insight.dollar_impact)} is the incremental revenue increase vs the prior period. Continued momentum could yield similar gains in future periods.</>
                            )}
                            {insight.category === 'profitability' && (
                              <>Impact of {formatCurrency(insight.dollar_impact)} is the potential margin improvement if pricing/sourcing is optimized to reach 60% margin target.</>
                            )}
                            {insight.category === 'platform_performance' && (
                              <>Impact of {formatCurrency(insight.dollar_impact)} assumes closing 50% of the performance gap between platforms is achievable through optimization.</>
                            )}
                            {!['customer_retention', 'stockout', 'inventory', 'revenue', 'revenue_growth', 'profitability', 'platform_performance'].includes(insight.category) && (
                              <>The {formatCurrency(insight.dollar_impact)} impact is calculated based on the detected pattern's magnitude and historical data trends.</>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Confidence Factors */}
                      {insight.confidence_score && (
                        <div className="bg-white/60 rounded-lg p-3">
                          <p className="text-xs font-medium text-blue-700 mb-1">Confidence: {insight.confidence_score}%</p>
                          <p className="text-sm text-gray-600">
                            Confidence is based on data quality, sample size, and consistency of the pattern. Higher confidence (85%+) indicates strong statistical significance.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Metadata Footer */}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Generated by: {insight.generated_by}</span>
                    <span>Source: {insight.source}</span>
                    {insight.is_ml_powered && (
                      <span className="flex items-center gap-1 text-green-600">
                        <Sparkles className="w-3 h-3" />
                        Machine Learning Model
                      </span>
                    )}
                </div>
              </div>
            )}
          </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// EXECUTIVE BRIEF TAB CONTENT
// ============================================================================

function ExecutiveBriefTab({ 
  intelligence, 
  isLoading, 
  createdActionIds = [],
  showCreatedActionsFilter = false,
  onClearCreatedActionsFilter,
  scorecardData,
  scorecardLoading,
  scorecardWindowDays,
  setScorecardWindowDays,
  calendarWeekKey,
  brainOutput
}: { 
  intelligence: AgentIntelligence | undefined; 
  isLoading: boolean;
  createdActionIds?: string[];
  showCreatedActionsFilter?: boolean;
  onClearCreatedActionsFilter?: () => void;
  scorecardData: any;
  scorecardLoading: boolean;
  scorecardWindowDays: 7 | 30;
  setScorecardWindowDays: (days: 7 | 30) => void;
  calendarWeekKey?: string | null;
  brainOutput?: any;
}) {
  const [acknowledgedHashes, setAcknowledgedHashes] = useState<Set<string>>(new Set());
  const [showAcknowledged, setShowAcknowledged] = useState(true);  // Default ON to show all
  const [showForecastOnly, setShowForecastOnly] = useState(false);  // Phase 4A: Filter for ML/forecast actions
  const queryClient = useQueryClient();  // Phase 2: For invalidating queries after workflow actions
  const [execBrief, setExecBrief] = useState<any | null>(null);
  const [execBriefOpen, setExecBriefOpen] = useState(true);
  const [execBriefLoading, setExecBriefLoading] = useState(false);
  const [execBriefError, setExecBriefError] = useState<string | null>(null);
  const [execBriefView, setExecBriefView] = useState<'latest' | 'history'>('latest');
  const [execBriefHistory, setExecBriefHistory] = useState<any[]>([]);
  const [execBriefHistoryLoading, setExecBriefHistoryLoading] = useState(false);
  const [execBriefHistoryError, setExecBriefHistoryError] = useState<string | null>(null);
  const [execBriefPdf, setExecBriefPdf] = useState<{ id: string; url: string } | null>(null);
  const [execBriefPdfLoading, setExecBriefPdfLoading] = useState(false);
  const [execBriefPdfError, setExecBriefPdfError] = useState<string | null>(null);
  
  // Google Ads recommendations count for tab badge
  const { count: googleAdsCount } = useGoogleAdsRecs();

  // Fetch existing acknowledgments on mount
  useEffect(() => {
    const fetchAcknowledgments = async () => {
      try {
        const response = await fetch('/api/command-center/acknowledge');
        if (response.ok) {
          const data = await response.json();
          if (data.acknowledgedHashes) {
            setAcknowledgedHashes(new Set(data.acknowledgedHashes));
          }
        }
      } catch (error) {
        console.error('[CommandCenter] Error fetching acknowledgments:', error);
      }
    };
    fetchAcknowledgments();
  }, []);

  useEffect(() => {
    setExecBrief(null);
    setExecBriefError(null);
    setExecBriefPdf(null);
    setExecBriefPdfError(null);
  }, [calendarWeekKey]);

  useEffect(() => {
    if (execBriefView !== 'history') return;
    const loadHistory = async () => {
      setExecBriefHistoryLoading(true);
      setExecBriefHistoryError(null);
      try {
        const response = await fetch('/api/brain/exec-brief/history?limit=10');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        setExecBriefHistory(Array.isArray(data.items) ? data.items : []);
      } catch (error: any) {
        setExecBriefHistoryError(error.message || 'Failed to load history');
      } finally {
        setExecBriefHistoryLoading(false);
      }
    };
    loadHistory();
  }, [execBriefView]);

  const buildExecBriefEmailText = (brief: any) => {
    const lines: string[] = [];
    if (brief?.headline) {
      lines.push(brief.headline);
    }
    lines.push(`State: ${brief?.businessState?.state || 'unknown'} (${brief?.businessState?.confidence ?? 'n/a'})`);
    lines.push(`Belief: ${brief?.dominantBelief?.belief || 'unknown'} (${brief?.dominantBelief?.confidence ?? 'n/a'})`);
    lines.push('');
    lines.push('What changed:');
    const rows = brief?.requiredMetrics?.rows || [];
    if (rows.length === 0) {
      lines.push('- No channel metrics available.');
    } else {
      rows.forEach((row: any) => {
        const revenue = row?.revenue?.value ?? '—';
        const wow = row?.wow?.value ?? '—';
        const yoy = row?.yoy?.value ?? '—';
        lines.push(`- ${row.channel}: ${revenue} (WoW: ${wow}, YoY: ${yoy})`);
      });
    }
    lines.push('');
    lines.push('What matters now:');
    (brief?.whatMattersNow || []).forEach((item: string) => lines.push(`- ${item}`));
    lines.push('');
    lines.push('Do these now:');
    if ((brief?.doTheseNow || []).length === 0) {
      lines.push('- No urgent actions required');
    } else {
      (brief?.doTheseNow || []).forEach((item: any) => {
        lines.push(`- ${item.title} (${item.owner}) — ${item.expectedImpact}`);
      });
    }
    lines.push('');
    lines.push('Do not do:');
    (brief?.doNotDo || []).forEach((item: string) => lines.push(`- ${item}`));
    lines.push('');
    lines.push('Risks and watchouts:');
    (brief?.risksAndWatchouts || []).forEach((item: string) => lines.push(`- ${item}`));
    return lines.join('\n');
  };

  const handleGenerateExecBrief = async () => {
    setExecBriefLoading(true);
    setExecBriefError(null);
    setExecBriefPdf(null);
    setExecBriefPdfError(null);
    try {
      const response = await fetch('/api/brain/exec-brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          calendarWeekKey: calendarWeekKey || undefined,
          mode: 'polished',
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setExecBrief(data);
      setExecBriefView('latest');
    } catch (error: any) {
      setExecBriefError(error.message || 'Failed to generate executive brief');
    } finally {
      setExecBriefLoading(false);
    }
  };

  const handleCopyExecBrief = async () => {
    if (!execBrief) return;
    try {
      const body = execBrief?.email?.body || buildExecBriefEmailText(execBrief);
      await navigator.clipboard.writeText(body);
    } catch (error) {
      console.error('[CommandCenter] Failed to copy exec brief:', error);
    }
  };

  const handleCopyExecBriefJson = async () => {
    if (!execBrief) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(execBrief, null, 2));
    } catch (error) {
      console.error('[CommandCenter] Failed to copy exec brief JSON:', error);
    }
  };

  const handleExportExecBriefPdf = async () => {
    const execBriefId = execBrief?.persistedExecBriefId;
    if (!execBriefId) return;
    setExecBriefPdfLoading(true);
    setExecBriefPdfError(null);
    try {
      const response = await fetch('/api/brain/exec-brief/pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ execBriefId }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setExecBriefPdf({ id: data.pdfId, url: data.downloadUrl });
    } catch (error: any) {
      setExecBriefPdfError(error.message || 'Failed to export PDF');
    } finally {
      setExecBriefPdfLoading(false);
    }
  };

  const renderMetricCell = (cell?: { value?: string | null; reason?: string }) => {
    const value = cell?.value ?? '—';
    const isMissing = !cell?.value;
    return (
      <span
        title={isMissing ? (cell?.reason || 'Unavailable') : undefined}
        className={isMissing ? 'text-gray-400' : 'text-gray-900'}
      >
        {value}
      </span>
    );
  };

  const handleLoadExecBrief = async (id: string) => {
    setExecBriefLoading(true);
    setExecBriefError(null);
    setExecBriefPdf(null);
    setExecBriefPdfError(null);
    try {
      const response = await fetch(`/api/brain/exec-brief/get?id=${encodeURIComponent(id)}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const briefPayload = data.brief
        ? { ...data.brief, persistedExecBriefId: data.id, pdfId: data.pdfId }
        : null;
      setExecBrief(briefPayload);
      setExecBriefView('history');
      if (data.pdfId) {
        setExecBriefPdf({
          id: data.pdfId,
          url: `/api/brain/exec-brief/pdf/download?id=${encodeURIComponent(data.pdfId)}`,
        });
      }
    } catch (error: any) {
      setExecBriefError(error.message || 'Failed to load executive brief');
    } finally {
      setExecBriefLoading(false);
    }
  };

  // Handle acknowledging an item
  const handleAcknowledge = async (contentHash: string, category: string, title: string) => {
    try {
      const response = await fetch('/api/command-center/acknowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_type: category.includes('high_cpa') || category.includes('declining') ? 'warning' : 'action',
          category,
          title
        })
      });

      if (response.ok) {
        // Optimistically update local state
        setAcknowledgedHashes(prev => new Set([...prev, contentHash]));
      }
    } catch (error) {
      console.error('[CommandCenter] Error acknowledging item:', error);
    }
  };

  // Handle unacknowledging an item (undo "Mark as Done")
  const handleUnacknowledge = async (contentHash: string) => {
    try {
      const response = await fetch('/api/command-center/acknowledge', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_hash: contentHash })
      });

      if (response.ok) {
        // Optimistically update local state
        setAcknowledgedHashes(prev => {
          const newSet = new Set(prev);
          newSet.delete(contentHash);
          return newSet;
        });
      }
    } catch (error) {
      console.error('[CommandCenter] Error unacknowledging item:', error);
    }
  };

  // Phase 2: Handle status change
  // Phase 4B: Pass action for outcome tracking when transitioning to in_progress
  const handleStatusChange = async (actionId: string, status: ActionStatus) => {
    // Find the action from unifiedActions to pass for outcome tracking
    const action = intelligence?.unifiedActions?.find(a => a.contentHash === actionId);
    const success = await updateActionStatus(actionId, status, action);
    if (success) {
      console.log(`[CommandCenter] Status updated to ${status} for ${actionId}`);
      // Invalidate and refetch to show updated state
      queryClient.invalidateQueries({ queryKey: ['command-center-intelligence'] });
    }
  };

  // Phase 2: Handle assignment
  const handleAssign = async (actionId: string, assignee: string | null) => {
    const success = await assignAction(actionId, assignee);
    if (success) {
      console.log(`[CommandCenter] Assigned ${assignee || 'none'} to ${actionId}`);
      // Invalidate and refetch to show updated state
      queryClient.invalidateQueries({ queryKey: ['command-center-intelligence'] });
    }
  };

  // Phase 2: Handle snooze/unsnooze
  const handleSnooze = async (actionId: string, until: string | null) => {
    const success = await snoozeAction(actionId, until);
    if (success) {
      console.log(`[CommandCenter] Snoozed/unsnoozed ${actionId}`);
      // Invalidate and refetch to show updated state
      queryClient.invalidateQueries({ queryKey: ['command-center-intelligence'] });
    }
  };

  // Use unified actions if available, otherwise fall back to legacy
  const hasUnifiedActions = intelligence?.unifiedActions && intelligence.unifiedActions.length > 0;
  
  // Phase 4A: Filter for ML/forecast-driven actions if toggle is on
  const filteredUnifiedActions = hasUnifiedActions
    ? (showForecastOnly 
        ? intelligence.unifiedActions!.filter(a => a.aiEvidence?.inferenceType === 'ml_model')
        : intelligence.unifiedActions!)
    : [];
  
  // Partition unified actions for the 3-lane view (Phase 2: includes snoozedItems)
  const { todaysPlan, earlyWarnings, patternDiscoveries, snoozedItems } = hasUnifiedActions
    ? partitionUnifiedActions(filteredUnifiedActions, acknowledgedHashes, showAcknowledged)
    : { todaysPlan: [], earlyWarnings: [], patternDiscoveries: [], snoozedItems: [] };

  // Count items for toggle display
  const totalActions = hasUnifiedActions 
    ? intelligence.unifiedActions!.filter(a => a.sourceType === 'action').length
    : (intelligence?.actions?.dailyActions?.length || 0) + (intelligence?.actions?.weeklyActions?.length || 0);
  const totalWarnings = hasUnifiedActions
    ? intelligence.unifiedActions!.filter(a => a.sourceType === 'warning').length
    : (intelligence?.warnings?.dailyWarnings?.length || 0) + (intelligence?.warnings?.weeklyWarnings?.length || 0);
  const acknowledgedCount = acknowledgedHashes.size;

  return (
    <>
      {/* Executive Brief Panel */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-semibold text-gray-900">Executive Brief</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setExecBriefView('latest')}
                className={`px-3 py-1.5 text-sm font-medium ${
                  execBriefView === 'latest'
                    ? 'bg-violet-100 text-violet-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Latest
              </button>
              <button
                onClick={() => setExecBriefView('history')}
                className={`px-3 py-1.5 text-sm font-medium ${
                  execBriefView === 'history'
                    ? 'bg-violet-100 text-violet-700'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                History
              </button>
            </div>
            <button
              onClick={handleGenerateExecBrief}
              disabled={execBriefLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 text-sm font-medium"
            >
              {execBriefLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate Brief
            </button>
            <button
              onClick={handleCopyExecBrief}
              disabled={!execBrief}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
            >
              <Mail className="w-4 h-4" />
              Copy Email
            </button>
            <button
              onClick={handleCopyExecBriefJson}
              disabled={!execBrief}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
            >
              <FileDown className="w-4 h-4" />
              Copy JSON
            </button>
            <button
              onClick={handleExportExecBriefPdf}
              disabled={!execBrief || execBriefPdfLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
            >
              {execBriefPdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Export PDF
            </button>
            <button
              onClick={() => setExecBriefOpen(prev => !prev)}
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              {execBriefOpen ? (
                <>
                  Collapse <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Expand <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>

        {execBriefError && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {execBriefError}
          </div>
        )}
        {execBriefPdfError && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {execBriefPdfError}
          </div>
        )}
        {execBriefPdf && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            PDF ready: <a className="underline" href={execBriefPdf.url} target="_blank" rel="noreferrer">Download PDF</a>
          </div>
        )}

        {execBriefOpen && (
          <div className="mt-4 space-y-4">
            {execBriefView === 'history' && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-sm font-medium text-gray-700 mb-2">Recent briefs</div>
                {execBriefHistoryLoading && (
                  <div className="text-sm text-gray-500">Loading history…</div>
                )}
                {execBriefHistoryError && (
                  <div className="text-sm text-red-600">{execBriefHistoryError}</div>
                )}
                {!execBriefHistoryLoading && !execBriefHistoryError && execBriefHistory.length === 0 && (
                  <div className="text-sm text-gray-500">No briefs found yet.</div>
                )}
                <div className="space-y-2">
                  {execBriefHistory.map((item: any) => (
                    <button
                      key={item.id}
                      onClick={() => handleLoadExecBrief(item.id)}
                      className="w-full text-left p-3 rounded-lg bg-white border border-gray-200 hover:border-violet-300 hover:shadow-sm transition"
                    >
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{item.calendar_week_key}</span>
                        <span className="flex items-center gap-2">
                          {item.mode}
                          {item.pdf_id && (
                            <a
                              href={`/api/brain/exec-brief/pdf/download?id=${encodeURIComponent(item.pdf_id)}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => event.stopPropagation()}
                              className="text-violet-600 hover:text-violet-800 underline"
                            >
                              Download
                            </a>
                          )}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-900 mt-1">{item.headline}</div>
                      <div className="text-xs text-gray-500 mt-1">{new Date(item.created_at).toLocaleString()}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {execBriefLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating executive brief...
              </div>
            )}

            {!execBriefLoading && execBrief && (
              <>
                <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-violet-900">{execBrief.headline}</p>
                    {(() => {
                      const belief = String(execBrief?.dominantBelief?.belief || '').toLowerCase();
                      const state = String(execBrief?.businessState?.state || '').toLowerCase();
                      const expected = belief.includes('post_promo_normalization') || state.includes('post_promo_normalization');
                      const unexpected = belief.includes('forecast_miss') || state.includes('forecast_miss');
                      const label = unexpected ? 'Unexpected' : expected ? 'Expected' : 'Monitor';
                      const color = unexpected ? 'bg-amber-100 text-amber-700' : expected ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-700';
                      return (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-violet-700">
                    State: {execBrief.businessState?.state || 'unknown'} ({execBrief.businessState?.confidence ?? 'n/a'}) •
                    Belief: {execBrief.dominantBelief?.belief || 'unknown'} ({execBrief.dominantBelief?.confidence ?? 'n/a'})
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800">What Changed</h3>
                    {(execBrief.requiredMetrics?.rows || []).length === 0 ? (
                      <p className="text-sm text-gray-500">No channel metrics available.</p>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 text-gray-600">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium">Channel</th>
                              <th className="px-3 py-2 text-left font-medium">Revenue</th>
                              <th className="px-3 py-2 text-left font-medium">WoW %</th>
                              <th className="px-3 py-2 text-left font-medium">YoY %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(execBrief.requiredMetrics?.rows || []).map((row: any, idx: number) => (
                              <tr key={idx} className="border-t border-gray-100">
                                <td className="px-3 py-2 text-gray-700">{row.channel}</td>
                                <td className="px-3 py-2">{renderMetricCell(row.revenue)}</td>
                                <td className="px-3 py-2">{renderMetricCell(row.wow)}</td>
                                <td className="px-3 py-2">{renderMetricCell(row.yoy)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800">What changed vs last week</h3>
                    {execBrief.beliefShift?.note && execBrief.beliefShift?.note !== 'no_current_brief' ? (
                      <p className="text-sm text-gray-500">
                        {execBrief.beliefShift?.narrativeBullets?.[0] || 'No prior brief available. Generate last week\'s brief to enable comparisons.'}
                      </p>
                    ) : (
                      <>
                        <div className="text-sm text-gray-700 space-y-1">
                          <div>
                            State: {execBrief.beliefShift?.fromState || '—'}{' -> '}{execBrief.beliefShift?.toState || '—'}
                          </div>
                          <div>
                            Belief: {execBrief.beliefShift?.fromBelief || '—'}{' -> '}{execBrief.beliefShift?.toBelief || '—'}
                          </div>
                        </div>
                        <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                          {(execBrief.beliefShift?.narrativeBullets || []).map((item: string, idx: number) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800">What Matters Now</h3>
                    <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                      {(execBrief.whatMattersNow || []).map((item: string, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800">Do These Now</h3>
                    {(execBrief.doTheseNow || []).length === 0 ? (
                      <p className="text-sm text-gray-500">No urgent actions required.</p>
                    ) : (
                      <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                        {(execBrief.doTheseNow || []).map((item: any, idx: number) => (
                          <li key={idx}>
                            <span className="font-medium">{item.title}</span> — {item.owner} ({item.expectedImpact})
                            <div className="text-xs text-gray-500">{item.rationale}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800">Do Not Do</h3>
                    <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                      {(execBrief.doNotDo || []).map((item: string, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-2 lg:col-span-2">
                    <h3 className="text-sm font-semibold text-gray-800">Risks and Watchouts</h3>
                    <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                      {(execBrief.risksAndWatchouts || []).map((item: string, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </>
            )}

            {!execBriefLoading && !execBrief && !execBriefError && (
              <p className="text-sm text-gray-500">Generate a brief to summarize this week’s Brain output.</p>
            )}
          </div>
        )}
      </div>

      {/* PIQ 8.0: Operator Status Bar (replaces old banners) */}
      {(() => {
        // Build operator view from intelligence or brainOutput (pure mapping)
        const hasData = intelligence?.businessState || brainOutput?.decisionPosture;
        if (!hasData) return null;
        
        // Determine posture from businessState or brainOutput
        const postureFromState = intelligence?.businessState?.primaryState === 'transition_constraint' 
          ? 'action_required' 
          : intelligence?.businessState?.urgency === 'high'
            ? 'action_required'
            : intelligence?.businessState?.urgency === 'medium'
              ? 'monitor_only'
              : 'all_clear';
        
        const brainContract: BrainContractSubset = {
          decisionPosture: brainOutput?.decisionPosture || {
            posture: postureFromState as any,
            rationale: intelligence?.businessState?.implications || [],
          },
          retailerAttribution: brainOutput?.retailerAttribution,
          earlyWarnings: brainOutput?.earlyWarnings,
          propagation: brainOutput?.propagation,
          valueLedger: brainOutput?.valueLedger,
          dataQuality: brainOutput?.dataQuality,
          calendarWeekKey: calendarWeekKey || undefined,
        };
        
        const operatorView = buildOperatorView({ brainContract });
        
        return (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
            <OperatorStatusBarComponent 
              data={operatorView.statusBar}
              onOpenValueLedger={(ledgerId) => {
                console.log('[CommandCenter] Open Value Ledger:', ledgerId);
              }}
            />
          </div>
        );
      })()}
      
      {/* Filter Toggles */}
      <div className="mb-4 flex items-center justify-end gap-6 flex-wrap">
        {/* Phase 9E: Created actions filter chip */}
        {showCreatedActionsFilter && createdActionIds.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 border border-emerald-300 rounded-full text-sm">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-emerald-800 font-medium">
              Showing {createdActionIds.length} created action{createdActionIds.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={onClearCreatedActionsFilter}
              className="ml-1 text-emerald-600 hover:text-emerald-800"
              title="Clear filter"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        
        {/* Phase 4A: Forecast-only filter */}
        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showForecastOnly}
            onChange={(e) => setShowForecastOnly(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
          />
          <span className="flex items-center gap-1">
            <Brain className="w-3.5 h-3.5 text-violet-500" />
            ML/Forecast actions only
          </span>
        </label>
        
        {/* Show Acknowledged Toggle */}
        {acknowledgedCount > 0 && (
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showAcknowledged}
              onChange={(e) => setShowAcknowledged(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
            />
            Show completed ({acknowledgedCount})
          </label>
        )}
      </div>

      {/* Quick Navigation */}
      <QuickNav snoozedCount={snoozedItems.length} />

      {/* V3-6: Today's Cases - Primary decision unit */}
      <TodaysCasesPanel />

      {/* Unified Action/Warning/Discovery Tabs */}
      <ActionInsightsTabs
        actions={hasUnifiedActions ? null : (intelligence?.actions || null)}
        unifiedActionsForActions={hasUnifiedActions ? todaysPlan : undefined}
        warnings={hasUnifiedActions ? null : (intelligence?.warnings || null)}
        unifiedActionsForWarnings={hasUnifiedActions ? earlyWarnings : undefined}
        insights={hasUnifiedActions ? null : (intelligence?.insights || null)}
        unifiedActionsForDiscoveries={hasUnifiedActions ? patternDiscoveries : undefined}
        acknowledgedHashes={acknowledgedHashes}
        onAcknowledge={handleAcknowledge}
        onUnacknowledge={handleUnacknowledge}
        showAcknowledged={showAcknowledged}
        beliefSummary={intelligence?.beliefSummary}
        onStatusChange={handleStatusChange}
        onAssign={handleAssign}
        onSnooze={handleSnooze}
        createdActionIds={createdActionIds}
        actionCount={todaysPlan.length}
        warningCount={earlyWarnings.length}
        discoveryCount={patternDiscoveries.length}
        googleAdsCount={googleAdsCount}
      />

      {/* Phase 2: Snoozed Items - Compact, collapsed by default */}
      {snoozedItems.length > 0 && (
        <div id="snoozed" className="mb-6">
          <SnoozedItems 
            snoozedActions={snoozedItems}
            onUnsnooze={async (actionId) => handleSnooze(actionId, null)}
          />
        </div>
      )}

      {/* Phase 5: Playbook Panel - One-click agentic workflows */}
      <div id="playbooks" className="mb-6">
        <PlaybookPanel />
      </div>

      {/* Performance Snapshot & Agentic Workforce Row */}
      <div id="performance" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Performance Snapshot */}
        <PerformanceSnapshot />
        
        {/* Agent Quick Access */}
        <AgentQuickAccess />
      </div>

      {/* Command Center Admin Section - Collapsed by default */}
      <div id="admin" className="mb-6">
        <CommandCenterAdminSection 
          scorecardData={scorecardData}
          scorecardLoading={scorecardLoading}
          scorecardWindowDays={scorecardWindowDays}
          setScorecardWindowDays={setScorecardWindowDays}
        />
      </div>
    </>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// ============================================================================
// DEBUG PANEL (Dev Only)
// ============================================================================

interface DebugData {
  summary: {
    totalStates: number;
    totalLogs: number;
    totalOutcomes: number;
    totalAcknowledgments: number;
    statusDistribution: Record<string, number>;
    interactionDistribution: Record<string, number>;
  };
  states: Array<{
    action_id: string;
    status: string;
    assigned_to: string | null;
    snooze_until: string | null;
    updated_at: string;
  }>;
  logs: Array<{
    action_id: string;
    interaction_type: string;
    old_value: string | null;
    new_value: string | null;
    created_at: string;
  }>;
}

function DebugPanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { data: debugData, isLoading, error, refetch } = useQuery<DebugData>({
    queryKey: ['command-center-debug'],
    queryFn: async () => {
      const response = await fetch('/api/command-center/debug/raw');
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Debug endpoint disabled');
        }
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json();
    },
    enabled: isOpen,
    staleTime: 10 * 1000
  });

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 z-50 max-h-80 overflow-y-auto border-t-4 border-yellow-500">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-yellow-500" />
            <span className="font-bold text-yellow-400">Command Center Debug (Dev Only)</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => refetch()} className="px-2 py-1 bg-gray-700 rounded text-xs hover:bg-gray-600">
              Refresh
            </button>
            <button onClick={onClose} className="px-2 py-1 bg-gray-700 rounded text-xs hover:bg-gray-600">
              Close
            </button>
          </div>
        </div>
        
        {isLoading && <p className="text-gray-400">Loading debug data...</p>}
        {error && <p className="text-red-400">Error: {(error as Error).message}</p>}
        
        {debugData && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            {/* Summary */}
            <div className="bg-gray-800 p-3 rounded">
              <h4 className="font-semibold text-gray-300 mb-2">Summary</h4>
              <div className="space-y-1">
                <p>States: <span className="text-white">{debugData.summary.totalStates}</span></p>
                <p>Logs: <span className="text-white">{debugData.summary.totalLogs}</span></p>
                <p>Outcomes: <span className="text-white">{debugData.summary.totalOutcomes}</span></p>
                <p>Acks: <span className="text-white">{debugData.summary.totalAcknowledgments}</span></p>
              </div>
            </div>
            
            {/* Status Distribution */}
            <div className="bg-gray-800 p-3 rounded">
              <h4 className="font-semibold text-gray-300 mb-2">Status Distribution</h4>
              {Object.entries(debugData.summary.statusDistribution).map(([status, count]) => (
                <p key={status}>{status}: <span className="text-white">{count}</span></p>
              ))}
              {Object.keys(debugData.summary.statusDistribution).length === 0 && (
                <p className="text-gray-500">No statuses yet</p>
              )}
            </div>
            
            {/* Recent States */}
            <div className="bg-gray-800 p-3 rounded col-span-2">
              <h4 className="font-semibold text-gray-300 mb-2">First 10 States</h4>
              <table className="w-full text-left">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="pb-1">ID</th>
                    <th className="pb-1">Status</th>
                    <th className="pb-1">Assigned</th>
                    <th className="pb-1">Snoozed</th>
                  </tr>
                </thead>
                <tbody>
                  {debugData.states.slice(0, 10).map((state, i) => (
                    <tr key={i} className="border-b border-gray-700/50">
                      <td className="py-1 font-mono">{state.action_id.substring(0, 8)}...</td>
                      <td className={`py-1 ${
                        state.status === 'in_progress' ? 'text-yellow-400' : 
                        state.status === 'resolved' ? 'text-green-400' : 
                        state.status === 'snoozed' ? 'text-blue-400' : 'text-gray-300'
                      }`}>{state.status}</td>
                      <td className="py-1">{state.assigned_to || '-'}</td>
                      <td className="py-1">{state.snooze_until ? 'Yes' : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {debugData.states.length === 0 && <p className="text-gray-500">No states</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CommandCenter() {
  const { companyId, retailers } = useTenant();
  const [location] = useLocation();
  const queryClient = useQueryClient();
  
  // Calendar week selection from shared context (GlobalHeader manages the picker)
  const { selectedDateRange } = useFiscalWeekContext();
  
  // Parse tab from URL query string
  const urlParams = new URLSearchParams(location.split('?')[1] || '');
  const tabFromUrl = urlParams.get('tab');
  const [activeTab, setActiveTab] = useState<'brief' | 'insights'>(
    tabFromUrl === 'insights' ? 'insights' : 'brief'
  );
  
  // Debug panel state (dev only)
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

  // Phase 9C: Manual workflow runner modal state
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [isRunningWorkflow, setIsRunningWorkflow] = useState(false);
  const [workflowRunResult, setWorkflowRunResult] = useState<WorkflowRunResult | null>(null);
  const [showWorkflowDrawer, setShowWorkflowDrawer] = useState(false);
  
  // Phase V3-1: Brain Run state
  const [isRunningBrain, setIsRunningBrain] = useState(false);
  const [brainOutput, setBrainOutput] = useState<any>(null);
  const [brainError, setBrainError] = useState<{
    errorCode: string;
    attemptId: string;
    stage: string;
    message: string;
    details?: string;
  } | null>(null);
  const [showBrainDrawer, setShowBrainDrawer] = useState(false);
  const [showDecisionRationale, setShowDecisionRationale] = useState(false);
  
  // PIQ 8.2.2: Executive Brief generation state (separate from Brain run)
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [execBriefData, setExecBriefData] = useState<any>(null);
  
  // PIQ 8.2.2: Generate Executive Brief handler
  const handleGenerateBrief = async () => {
    setIsGeneratingBrief(true);
    try {
      const calendarWeekKey = selectedDateRange 
        ? `${selectedDateRange.start}_to_${selectedDateRange.end}` 
        : selectedCalendarWeekKey || undefined;
      
      console.log('[CommandCenter] Generating Executive Brief for week:', calendarWeekKey);
      
      const resp = await fetch('/api/brain/exec-brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          calendarWeekKey,
          mode: 'polished', // Get the polished version
        }),
      });
      
      const data = await resp.json();
      
      if (!resp.ok) {
        console.error('[CommandCenter] Brief generation failed:', data);
        // Show error toast or notification
        return;
      }
      
      console.log('[CommandCenter] Brief generated:', data);
      setExecBriefData(data);
      
      // Also update brainOutput if it has execBrief
      if (brainOutput) {
        setBrainOutput({
          ...brainOutput,
          execBrief: data,
        });
      }
      
      // Refresh intelligence data
      queryClient.invalidateQueries({ queryKey: ['command-center-intelligence'] });
    } catch (e: any) {
      console.error('[CommandCenter] Brief generation exception:', e);
    } finally {
      setIsGeneratingBrief(false);
    }
  };
  
  // V3-7A: Inbox state
  const [showInboxDrawer, setShowInboxDrawer] = useState(false);
  const [inboxTab, setInboxTab] = useState<'unread' | 'all'>('unread');
  
  // V3-8: Brain Scorecard state
  const [scorecardWindowDays, setScorecardWindowDays] = useState<7 | 30>(7);
  const [showScorecard, setShowScorecard] = useState(true);
  
  // V3-9A: Brain Health state
  const [showHealthRecommendations, setShowHealthRecommendations] = useState(false);
  
  // Phase 9E: Created actions highlight + filter state
  const [createdActionIds, setCreatedActionIds] = useState<string[]>([]);
  const [showCreatedActionsFilter, setShowCreatedActionsFilter] = useState(false);
  const [createdActionsExpiry, setCreatedActionsExpiry] = useState<number | null>(null);
  
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const selectedCalendarWeekKey = selectedDateRange
    ? `${selectedDateRange.start}_to_${selectedDateRange.end}`
    : null;

  // Fetch AI intelligence from unified endpoint
  const { data: intelligence, isLoading } = useQuery<AgentIntelligence>({
    queryKey: ['command-center-intelligence', companyId],
    queryFn: async () => {
      const response = await fetch('/api/command-center/intelligence');
      if (!response.ok) {
        console.error('[CommandCenter] Failed to fetch intelligence:', response.status);
        return {
          actions: null,
          warnings: null,
          insights: null,
          generatedAt: new Date().toISOString()
        };
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId
  });
  
  // V3-7A: Inbox notifications query
  interface InboxNotification {
    id: string;
    type: 'case_created' | 'pattern_proven' | 'best_play_available';
    severity: 'critical' | 'high' | 'medium' | 'low';
    title: string;
    body: string;
    entityType: string;
    entityId: string;
    metadata: {
      calendarWeekKey?: string;
      patternId?: string;
      patternType?: string;
      successRate?: number;
      constraintType?: string;
      retailerName?: string;
    };
    status: 'unread' | 'read' | 'archived';
    createdAt: string;
  }
  
  const { data: inboxData, refetch: refetchInbox } = useQuery({
    queryKey: ['inbox', companyId, inboxTab],
    queryFn: async () => {
      const status = inboxTab === 'all' ? 'all' : 'unread';
      const response = await fetch(`/api/inbox?status=${status}&limit=25&includeCount=true`);
      if (!response.ok) {
        throw new Error('Failed to fetch inbox');
      }
      return response.json() as Promise<{
        ok: boolean;
        count: number;
        unreadCount?: number;
        notifications: InboxNotification[];
      }>;
    },
    staleTime: 30 * 1000,
    enabled: !!companyId,
  });
  
  const unreadCount = inboxData?.unreadCount ?? 0;
  
  // V3-8: Brain Scorecard query
  interface BrainScorecard {
    windowDays: number;
    adoption: {
      casesCreated: number;
      casesOpened: number;
      caseOpenRate: number;
      actionsCreated: number;
      actionsPerCase: number;
      bestPlayActionsCreated: number;
      inboxUnread: number;
      inboxOpenCaseClicks: number;
    };
    effectiveness: {
      outcomesProcessed: number;
      improved: number;
      neutral: number;
      worse: number;
      improvementRate: number;
      avgConfidenceBiasByType: Record<string, number>;
      provenPatterns: number;
    };
    signalQuality: {
      brainRuns: number;
      findingsCreated: number;
      casesCreated: number;
      avgFindingsPerCase: number;
      conflictsDetected: number;
      conflictRate: number;
      degradedRuns: number;
      degradedRate: number;
      missingDataFindings: number;
    };
    timeliness: {
      avgMinutesInQueue: number;
      p95MinutesInQueue: number;
    };
  }
  
  const { data: scorecardData, isLoading: scorecardLoading } = useQuery({
    queryKey: ['brain-scorecard', companyId, scorecardWindowDays],
    queryFn: async () => {
      const response = await fetch(`/api/brain/scorecard?windowDays=${scorecardWindowDays}`);
      if (!response.ok) {
        throw new Error('Failed to fetch scorecard');
      }
      return response.json() as Promise<BrainScorecard>;
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: !!companyId,
  });
  
  // V3-9A: Brain Health query
  interface HealthReason {
    code: string;
    message: string;
    metric: string;
    value: number;
    target: number;
  }
  
  interface HealthRecommendation {
    title: string;
    type: 'tuning' | 'data_repair' | 'ops';
    details: Record<string, unknown>;
    rationale: string;
  }
  
  interface BrainHealthAssessment {
    status: 'stable' | 'noisy' | 'slow' | 'low_impact' | 'data_incomplete';
    severity: 'low' | 'medium' | 'high';
    reasons: HealthReason[];
    recommendations: HealthRecommendation[];
    scorecard7d?: BrainScorecard;
    scorecard30d?: BrainScorecard;
  }
  
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['brain-health', companyId],
    queryFn: async () => {
      const response = await fetch('/api/brain/health');
      if (!response.ok) {
        throw new Error('Failed to fetch brain health');
      }
      return response.json() as Promise<BrainHealthAssessment>;
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: !!companyId,
  });

  // Update tab from URL on mount and URL change
  useEffect(() => {
    const params = new URLSearchParams(location.split('?')[1] || '');
    const tab = params.get('tab');
    if (tab === 'insights') {
      setActiveTab('insights');
    } else if (tab === 'brief' || !tab) {
      setActiveTab('brief');
    }
  }, [location]);

  // Phase 9C: Handle manual workflow run
  const handleRunWorkflow = async (workflowId: string, retailerId?: string) => {
    setIsRunningWorkflow(true);
    try {
      const result = await runWorkflow(workflowId, { retailerId });
      if (result) {
        setWorkflowRunResult(result);
        setShowWorkflowModal(false);
        setShowWorkflowDrawer(true);
      } else {
        throw new Error('Workflow returned no result');
      }
    } finally {
      setIsRunningWorkflow(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* PIQ 8.3.1: Dev-only renderer/build indicator */}
      {isDev && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-900/90 text-white text-[10px] px-2 py-1 rounded font-mono shadow-lg">
          Renderer: ExecBriefPrimaryCard | Group: computeFeedGroup | {process.env.NODE_ENV || 'dev'}
        </div>
      )}
      
      <GlobalHeader 
        title="PIQ Command Center™"
        subtitle="AI Intelligence Hub"
      />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg">
                <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Command Center</h1>
                <p className="text-gray-500 text-sm">What your AI agents discovered today</p>
                </div>
              </div>
            <div className="flex items-center gap-3">
              {/* Week picker is in GlobalHeader */}
              
              {/* V3-7A: Inbox Button with Badge */}
              <button
                onClick={() => setShowInboxDrawer(true)}
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Inbox"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              
              {/* Phase V3-1: Run Brain Button (Primary) */}
              <button
                onClick={async () => {
                  setIsRunningBrain(true);
                  setShowBrainDrawer(true);
                  setBrainOutput(null);
                  setBrainError(null);
                  try {
                    // V3-4D: Build calendarWeekKey from selected date range
                    const calendarWeekKey = selectedDateRange 
                      ? `${selectedDateRange.start}_to_${selectedDateRange.end}` 
                      : undefined;
                    
                    console.log('[CommandCenter] Running Brain with calendarWeekKey:', calendarWeekKey);
                    
                    const resp = await fetch('/api/brain/run', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ calendarWeekKey }),
                    });
                    const data = await resp.json();
                    
                    // Handle structured error response
                    if (!resp.ok || data.ok === false) {
                      console.error('[CommandCenter] Brain run failed:', data);
                      setBrainError({
                        errorCode: data.errorCode || 'UNKNOWN_ERROR',
                        attemptId: data.attemptId || 'unknown',
                        stage: data.stage || 'unknown',
                        message: data.message || `HTTP ${resp.status}`,
                        details: data.details,
                      });
                      setBrainOutput(null);
                      return;
                    }
                    
                    setBrainOutput(data.brainOutput);
                    setBrainError(null);
                    queryClient.invalidateQueries({ queryKey: ['command-center-intelligence'] });
                  } catch (e: any) {
                    console.error('[CommandCenter] Brain run exception:', e);
                    setBrainError({
                      errorCode: 'NETWORK_ERROR',
                      attemptId: 'unknown',
                      stage: 'fetch',
                      message: e?.message || 'Network request failed',
                    });
                    setBrainOutput(null);
                  } finally {
                    setIsRunningBrain(false);
                  }
                }}
                disabled={isRunningBrain}
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm disabled:opacity-50"
              >
                {isRunningBrain ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Brain className="w-4 h-4" />
                )}
                Run Brain
              </button>
              
              {/* Legacy: Run Workflow Button (Secondary) */}
              <button
                onClick={() => setShowWorkflowModal(true)}
                className="px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                Workflows
              </button>
              <div className="text-right">
                <p className="text-sm text-gray-600">{formattedDate}</p>
                <div className="flex items-center gap-2 justify-end mt-0.5">
                  {isLoading ? (
                    <Loader2 className="w-3 h-3 text-violet-500 animate-spin" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  )}
                  <span className="text-xs text-emerald-600 font-medium">
                    {isLoading ? 'Analyzing...' : 'Live'}
                  </span>
                </div>
              </div>
              {isDev && (
                <button
                  onClick={() => setShowDebugPanel(!showDebugPanel)}
                  className="ml-3 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-mono hover:bg-yellow-200"
                  title="Show Debug Data (Dev)"
                >
                  🐛 Debug
                </button>
              )}
            </div>
          </div>

          {/* PIQ 8.2: Removed tabs - single unified view */}
        </div>
      </div>

      {/* PIQ 8.2: Removed duplicate banners - all status info now in OperatorVerdictBlock */}
      
      {/* V3-9A: Health Recommendations Modal */}
      {showHealthRecommendations && healthData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-600" />
                <h3 className="text-lg font-semibold text-gray-900">Brain Health Recommendations</h3>
              </div>
              <button
                onClick={() => setShowHealthRecommendations(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Modal content */}
            <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                {healthData.recommendations.map((rec, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start gap-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        rec.type === 'tuning' ? 'bg-violet-100 text-violet-700' :
                        rec.type === 'data_repair' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {rec.type.toUpperCase().replace('_', ' ')}
                      </span>
                    </div>
                    <h4 className="font-medium text-gray-900 mt-2">{rec.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{rec.rationale}</p>
                  </div>
                ))}
              </div>
              
              {healthData.recommendations.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  No recommendations at this time.
                </p>
              )}
            </div>
            
            {/* Modal footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                These are recommendations only. No automatic changes will be made.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - PIQ 8.5: Narrative Spine (single cohesive flow) */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
        <NarrativeSpine
          brainContract={{
            decisionPosture: brainOutput?.decisionPosture || {
              posture: intelligence?.businessState?.primaryState === 'action_required' 
                ? 'action_required' 
                : intelligence?.businessState?.primaryState === 'all_clear'
                ? 'all_clear'
                : 'monitor_only',
              rationale: brainOutput?.decisionPosture?.rationale || 
                (intelligence?.businessState?.stateLabel 
                  ? [`Business state: ${intelligence.businessState.stateLabel}`] 
                  : []),
              confidence: brainOutput?.decisionPosture?.confidence || 
                intelligence?.businessState?.confidence,
            },
            retailerAttribution: brainOutput?.retailerAttribution,
            earlyWarnings: brainOutput?.earlyWarnings,
            propagation: brainOutput?.propagation,
            containmentPlaybook: brainOutput?.containmentPlaybook,
            preventivePlaybooks: brainOutput?.preventivePlaybooks,
            microAttribution: brainOutput?.microAttribution,
            retailerCausality: brainOutput?.retailerCausality,
            valueLedger: brainOutput?.valueLedger,
            calendarWeekKey: selectedCalendarWeekKey || undefined,
            dataQuality: brainOutput?.dataQuality || {
              conflictsCount: 0,
              conflictsBlocking: false,
            },
            businessState: intelligence?.businessState ? {
              primaryState: intelligence.businessState.primaryState || 'unknown',
              stateLabel: intelligence.businessState.stateLabel || 'Unknown',
              confidence: intelligence.businessState.confidence || 0,
            } : undefined,
            beliefSummary: intelligence?.beliefSummary ? {
              primary: intelligence.beliefSummary.primary || null,
              confidence: intelligence.beliefSummary.confidence || null,
              weight: intelligence.beliefSummary.weight || null,
            } : undefined,
            computedExpectedValue: brainOutput?.computedExpectedValue,
            retailPhase: brainOutput?.retailPhase,
            regimeStatus: brainOutput?.regimeStatus,
          }}
          execBriefData={(() => {
            // PIQ 8.2.2 / 8.3.1 / 8.3.2: Prefer dedicated execBriefData state, then brainOutput.execBrief
            const brief = execBriefData || brainOutput?.execBrief;
            if (!brief) return null;
            
            // PIQ 8.3.2: Use robust extractors for channel metrics (imported at top)
            const channelMetrics = extractAllChannelMetrics(brief.requiredMetrics?.rows);
            
            return {
              whatMattersNow: brief.whatMattersNow,
              doTheseNow: brief.doTheseNow?.map((d: any) => 
                typeof d === 'string' ? d : `${d.title} (${d.owner}) — ${d.expectedImpact}`
              ),
              doNotDo: brief.doNotDo,
              whatChanged: brief.whatChanged,
              channelMetrics: channelMetrics.length > 0 ? channelMetrics : undefined,
              generatedAt: brief.generatedAt || brief.persistedAt,
              // PIQ 8.3.2: Pass raw rows for debug panel
              _rawRequiredMetricsRows: brief.requiredMetrics?.rows,
            };
          })()}
          isLoading={isLoading}
          isExecBriefLoading={isGeneratingBrief}
          unifiedActions={intelligence?.unifiedActions || []}
          onGenerateBrief={handleGenerateBrief}
        />

        {/* PIQ 8.5: Admin-only panels (only when ?admin=true or dev mode) */}
        {(isDev || typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('admin') === 'true') && (
          <div className="mt-6 space-y-3 border-t border-dashed border-gray-300 pt-6">
            <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Admin / Debug</p>
            <CoverageAuditPanel
              brainContract={{
                decisionPosture: brainOutput?.decisionPosture,
                retailerAttribution: brainOutput?.retailerAttribution,
                earlyWarnings: brainOutput?.earlyWarnings,
                propagation: brainOutput?.propagation,
                containmentPlaybook: brainOutput?.containmentPlaybook,
                preventivePlaybooks: brainOutput?.preventivePlaybooks,
                microAttribution: brainOutput?.microAttribution,
                retailerCausality: brainOutput?.retailerCausality,
                valueLedger: brainOutput?.valueLedger,
                googleAdsAdvisory: brainOutput?.googleAdsAdvisory,
              }}
              actionsCount={intelligence?.unifiedActions?.length || 0}
              patternsCount={0}
            />
            <DebugDataDump
              requiredMetricsRows={(execBriefData || brainOutput?.execBrief)?.requiredMetrics?.rows || []}
              feedItems={[]}
              brainContract={brainOutput}
              execBriefData={execBriefData || brainOutput?.execBrief}
            />
          </div>
        )}
      </div>
      
      {/* Debug Panel (Dev Only) */}
      {isDev && <DebugPanel isOpen={showDebugPanel} onClose={() => setShowDebugPanel(false)} />}

      {/* Phase 9C: Run Workflow Modal */}
      <RunWorkflowModal
        isOpen={showWorkflowModal}
        onClose={() => setShowWorkflowModal(false)}
        onRun={handleRunWorkflow}
        retailers={retailers || []}
        isRunning={isRunningWorkflow}
      />

      {/* Phase 9C: Workflow Run Drawer */}
      <WorkflowRunDrawer
        result={workflowRunResult}
        isOpen={showWorkflowDrawer}
        onClose={() => setShowWorkflowDrawer(false)}
        onActionsCreated={(ids) => {
          // Phase 9E: Store created action IDs for highlighting
          setCreatedActionIds(ids);
          setShowCreatedActionsFilter(true);
          // Auto-expire after 60 seconds
          setCreatedActionsExpiry(Date.now() + 60000);
          setTimeout(() => {
            setCreatedActionIds([]);
            setShowCreatedActionsFilter(false);
            setCreatedActionsExpiry(null);
          }, 60000);
        }}
      />

      {/* Phase V3-1: Brain Run Drawer */}
      <BrainRunDrawer
        brainOutput={brainOutput}
        isOpen={showBrainDrawer}
        onClose={() => {
          setShowBrainDrawer(false);
          setBrainError(null);
        }}
        isLoading={isRunningBrain}
        error={brainError}
      />
      
      {/* V3-7A: Inbox Drawer */}
      {showInboxDrawer && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowInboxDrawer(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-violet-600" />
                <h2 className="text-lg font-semibold text-gray-900">Inbox</h2>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                    {unreadCount} unread
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowInboxDrawer(false)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setInboxTab('unread')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  inboxTab === 'unread' 
                    ? 'text-violet-600 border-b-2 border-violet-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Mail className="w-4 h-4 inline mr-1" />
                Unread
              </button>
              <button
                onClick={() => setInboxTab('all')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  inboxTab === 'all' 
                    ? 'text-violet-600 border-b-2 border-violet-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MailOpen className="w-4 h-4 inline mr-1" />
                All
              </button>
            </div>
            
            {/* Notification List */}
            <div className="flex-1 overflow-y-auto">
              {!inboxData?.notifications?.length ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
                  <Bell className="w-12 h-12 mb-4 opacity-50" />
                  <p className="text-lg font-medium">No notifications</p>
                  <p className="text-sm">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {inboxData.notifications.map((notification) => (
                    <div 
                      key={notification.id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${
                        notification.status === 'unread' ? 'bg-violet-50/50' : ''
                      }`}
                    >
                      {/* Severity + Title */}
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          notification.severity === 'critical' ? 'bg-red-100 text-red-600' :
                          notification.severity === 'high' ? 'bg-orange-100 text-orange-600' :
                          notification.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {notification.type === 'case_created' && <AlertCircle className="w-4 h-4" />}
                          {notification.type === 'pattern_proven' && <Star className="w-4 h-4" />}
                          {notification.type === 'best_play_available' && <Rocket className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              notification.severity === 'critical' ? 'bg-red-100 text-red-700' :
                              notification.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                              notification.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {notification.severity.toUpperCase()}
                            </span>
                            {notification.status === 'unread' && (
                              <span className="w-2 h-2 bg-violet-500 rounded-full" />
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900 mt-1 line-clamp-2">{notification.title}</h4>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notification.body}</p>
                          
                          {/* Meta */}
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                            <span>{new Date(notification.createdAt).toLocaleString()}</span>
                            {notification.metadata?.retailerName && (
                              <span className="text-gray-500">{notification.metadata.retailerName}</span>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 mt-3">
                            <button
                              onClick={async () => {
                                // Scroll to cases and open the matching case
                                setShowInboxDrawer(false);
                                // Find and expand the case in Today's Cases
                                const casesSection = document.querySelector('[data-cases-section]');
                                if (casesSection) {
                                  casesSection.scrollIntoView({ behavior: 'smooth' });
                                }
                                // V3-8: Log "Open Case" interaction for scorecard telemetry
                                fetch('/api/inbox/log-open-case', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ 
                                    notificationId: notification.id,
                                    caseId: notification.entityId,
                                  }),
                                }).catch(() => {}); // Fire and forget
                                // Mark as read
                                await fetch('/api/inbox/mark-read', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ id: notification.id }),
                                });
                                refetchInbox();
                              }}
                              className="px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-lg hover:bg-violet-700 transition-colors"
                            >
                              Open Case
                            </button>
                            {notification.status === 'unread' && (
                              <button
                                onClick={async () => {
                                  await fetch('/api/inbox/mark-read', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ id: notification.id }),
                                  });
                                  refetchInbox();
                                }}
                                className="px-3 py-1.5 text-gray-600 text-xs font-medium hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                Mark Read
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                await fetch('/api/inbox/archive', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ id: notification.id }),
                                });
                                refetchInbox();
                              }}
                              className="px-3 py-1.5 text-gray-600 text-xs font-medium hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1"
                            >
                              <Archive className="w-3 h-3" />
                              Archive
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
